// patch 224: My money plan. A guided walk through everything a financial planner
// would look at, then one page to check in on, with a page for every part.
// Worthly trial (mp_01): copied from Diary (ln_33). The only changes are the
// imports below, the colour remembered for the top card (kept on this phone),
// and refreshing user data through Worthly's own loader.
import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// The top card remembers which colour you swiped to, on this phone.
const HDR_KEY = 'worthly_money_plan_hdr';
function useStore() {
  const [data, setDataState] = useState(() => {
    try { return { planHdr: Number(window.localStorage.getItem(HDR_KEY)) || 0 }; } catch (e) { return { planHdr: 0 }; }
  });
  const setData = (fn) => setDataState((d) => {
    const next = typeof fn === 'function' ? fn(d) : fn;
    try { window.localStorage.setItem(HDR_KEY, String(next.planHdr || 0)); } catch (e) { /* storage unavailable */ }
    return next;
  });
  return { data, setData };
}
import {
  num, fmt, short, withCommas, dateLabel, monthYear, pid, CHAPTERS, STEPS, stepIndex, CHANGES, insOf, paperOf, accOf, leavingOf, remindersOf,
  newPlan, readPlan, worthly, work, todos, snapshot, checkupDue, nextCheckup,
} from './plan.js';

// Saves a little while after you stop, and straight away when you leave.
function useAutosave(value, save, delay = 600) {
  const sig = JSON.stringify(value);
  const latest = useRef({ value, save, pending: false });
  latest.current.value = value;
  latest.current.save = save;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    latest.current.pending = true;
    const t = setTimeout(() => { latest.current.pending = false; latest.current.save(latest.current.value); }, delay);
    return () => clearTimeout(t);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (latest.current.pending) latest.current.save(latest.current.value); }, []);
}

// Worthly trial: pop-up sheets are drawn outside the scrolling page, so their
// dimmed backdrop always covers the whole screen, top to bottom.
function SheetWrap({ children }) {
  const root = (typeof document !== 'undefined' && document.querySelector('.md')) || document.body;
  return createPortal(<div className="sheet-wrap">{children}</div>, root);
}

/* ---------------------------------------------------------------- little pieces */
const SRC = { w: ['🔗 Worthly', 'mp-src-w'], y: ['✏️ You', 'mp-src-y'], c: ['🧮 Worked out', 'mp-src-c'] };
function Src({ k, text }) {
  const [label, cls] = SRC[k];
  return <span className={'mp-src ' + cls}>{text ? label + ' · ' + text : label}</span>;
}

// A money box you can type in. With `auto`, it shows Worthly's number until you change it.
function Amt({ value, onChange, auto = null, prefix = '$', suffix = '', wide = false, small = false, plain = false }) {
  const show = (v) => (v == null || v === '' || num(v) === 0 ? '' : plain ? String(v) : withCommas(String(Math.round(num(v) * 100) / 100)));
  const [txt, setTxt] = useState(show(value == null ? auto : value));
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setTxt(show(value == null ? auto : value)); }, [value, auto, focus]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <label className={'mp-amt' + (wide ? ' wide' : '') + (small ? ' small' : '')}>
      {prefix && <span className="pf">{prefix}</span>}
      <input inputMode="decimal" value={txt} placeholder="0"
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        onChange={(e) => { const t = plain ? e.target.value.replace(/[^0-9.]/g, '') : withCommas(e.target.value); setTxt(t); onChange(t === '' ? (auto != null ? null : 0) : num(t)); }} />
      {suffix && <span className="sf">{suffix}</span>}
    </label>
  );
}
function Grow({ value, onChange, placeholder }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }, [value]);
  return <textarea ref={ref} className="mp-txt area" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={2} />;
}
const Txt = ({ value, onChange, placeholder = '', area = false }) => (area
  ? <Grow value={value} onChange={onChange} placeholder={placeholder} />
  : <input className="mp-txt" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />);

function Chips({ options, value, onChange, multi = false }) {
  const on = (o) => (multi ? (value || []).includes(o) : value === o);
  const tap = (o) => (multi ? onChange(on(o) ? value.filter((x) => x !== o) : [...(value || []), o]) : onChange(o));
  return <div className="mp-chips">{options.map((o) => <button key={o} className={on(o) ? 'on' : ''} onClick={() => tap(o)}>{o}</button>)}</div>;
}

const Card = ({ tint, eb, ink, children, onClick, className = '' }) => (
  <div className={'mp-card ' + className + (onClick ? ' tap' : '')} style={tint ? { background: 'var(--' + tint + ')' } : null} onClick={onClick}>
    {eb && <p className="eb" style={{ color: 'var(--' + (ink || 'muted') + ')' }}>{eb}</p>}
    {children}
  </div>
);
const Q = ({ children, sub }) => <><p className="mp-q">{children}</p>{sub && <p className="mp-qs">{sub}</p>}</>;
function Row({ e, bg, t, s, right, children, onClick }) {
  return (
    <div className={'mp-row' + (onClick ? ' tap' : '')} onClick={onClick}>
      {e && <span className="mp-ic" style={{ background: 'var(--' + (bg || 'field') + ')' }}>{e}</span>}
      <span className="mp-rt"><b>{t}</b>{s && <small>{s}</small>}{children}</span>
      {right != null && <span className="mp-rr">{right}</span>}
    </div>
  );
}
const Calc = ({ rows, children }) => (
  <div className="mp-calc">
    {rows && rows.map((r, i) => (r.go /* plan_38: tap a line to open where it's set */
      ? <button key={i} type="button" className={'ln mp-lngo' + (r.tot ? ' tot' : '')} onClick={r.go}><span>{r[0]}</span><span>{r[1]}<i>›</i></span></button>
      : <div key={i} className={'ln' + (r.tot ? ' tot' : '')}><span>{r[0]}</span>{r.b ? <b>{r[1]}</b> : <span>{r[1]}</span>}</div>))}
    {children}
  </div>
);
const Bar = ({ pct, ink, track }) => <div className="mp-bar" style={track ? { background: track } : null}><div style={{ width: Math.max(0, Math.min(100, pct || 0)) + '%', background: 'var(--' + ink + ')' }} /></div>;
const Tag = ({ c, children }) => <span className="mp-tag" style={{ background: 'var(--' + c + ')', color: 'var(--' + c + 'Ink)' }}>{children}</span>;
function Check({ on, t, s, onClick, onRemove }) {
  return (
    <div className={'mp-check' + (on ? ' on' : '')} onClick={onClick}>
      <span className="bx">{on ? '✓' : ''}</span>
      <span className="tx"><b>{t}</b>{s && <small>{s}</small>}</span>
      {onRemove && <button className="rm" aria-label="Remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>✕</button>}
    </div>
  );
}
function AddLine({ placeholder, onAdd }) {
  const [v, setV] = useState('');
  const add = () => { if (!v.trim()) return; onAdd(v.trim()); setV(''); };
  return (
    <div className="mp-addline">
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
      <button onClick={add}>Add</button>
    </div>
  );
}
const Sample = ({ children }) => <p className="mp-note">{children}</p>;
// up_02: one line of a list you can change: its name, a note, and its choices.
function ListItem({ x, onChange, onRemove, last, children }) {
  return (
    <div className={'mp-ins' + (last ? ' last' : '')}>
      <EditRow e={x.e} name={x.t || ''} onName={(v) => onChange({ t: v })} onRemove={onRemove} inline />
      <div style={{ margin: '6px 0 10px' }}><Txt value={x.s || ''} onChange={(v) => onChange({ s: v })} placeholder="A note, optional" /></div>
      {children}
    </div>
  );
}
const listOps = (list, put) => ({
  up: (key, p) => put(list.map((x) => ((x.k || x.id) === key ? { ...x, ...p } : x))),
  rm: (key, name) => { if (window.confirm('Remove ' + (name || 'this') + '?')) put(list.filter((x) => (x.k || x.id) !== key)); },
});
const INS_OPTS = ['Through work', 'My own', 'Not sure', "Don't have"];
const ACC_OPTS = ['Using', 'Worth a look', 'Not using', 'Later'];
// A line with a name you can rename, and its amount underneath.
function EditRow({ e, name, onName, onRemove, src, glass = false, inline = false, children }) {
  return (
    <div className={'mp-erow' + (glass ? ' glass' : '') + (inline ? ' inline' : '')}>
      <div className="top">
        {e && <span className="mp-ic sm" style={{ background: glass ? 'var(--card)' : 'var(--field)' }}>{e}</span>}
        <input className="mp-name" value={name} onChange={(ev) => onName(ev.target.value)} />
        {inline && <span className="amts">{children}</span>}
        {onRemove && <button className="mp-x" aria-label="Remove" onClick={onRemove}>✕</button>}
      </div>
      {(!inline || src) && <div className="bot">{src || <span />}{!inline && <span className="amts">{children}</span>}</div>}
    </div>
  );
}
const pct = (a, b) => (b > 0 ? Math.round(a / b * 100) : 0);
// ef_01: months your safety net covers, with severance and EI on top of your savings.
const efCover = (plan, w) => { const extra = Math.max(0, num((plan.cushion || {}).extraMonths)); const savings = w.efMonthsNow || 0; return { savings, extra, total: savings + extra }; };
const tierTag = { must: ['mint', 'Must have'], love: ['blush', 'Would love'], nice: ['butter', 'Nice to have'] };

// patch 225: status pills, month labels and the shared money pieces
const STATUS = { on: ['mint', 'On track'], behind: ['peach', 'Behind'], notyet: ['butter', 'Not started'], later: ['sky', 'Later'], done: ['mint', 'Done!'], covered: ['mint', 'Covered'], off: ['sky', 'Off'] };
function Pill({ s }) {
  const [c, t] = STATUS[s] || STATUS.later;
  return <span className="mp-pill" style={{ background: 'var(--' + c + ')', color: 'var(--' + c + 'Ink)' }}><i style={{ background: 'var(--' + c + 'Ink)' }} />{t}</span>;
}
const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
// Worthly trial: "Sept 2029" when a goal has a month, "2029" when it doesn't.
const byLabel = (month, year) => (num(month) ? MONS[num(month) - 1] + ' ' : '') + year;
// Worthly trial: move a goal to Must have, Would love or Nice to have. Goals
// that already keep their own tier keep it there; the rest use plan.goalTiers.
const setGoalTier = (plan, set, id, tier) => {
  if (id === 'move') set({ move: { ...plan.move, tier } });
  else if (id === 'baby') set({ baby: { ...plan.baby, tier } });
  else if (id === 'resp') set({ resp: { ...plan.resp, tier } });
  else if ((plan.goals || []).some((g) => g.id === id)) set({ goals: plan.goals.map((g) => (g.id === id ? { ...g, tier } : g)) });
  else set({ goalTiers: { ...(plan.goalTiers || {}), [id]: tier } });
};
const TIER_NAMES = [['must', 'Must have'], ['love', 'Would love'], ['nice', 'Nice to have']];
// Worthly trial: your goals in the order you put them in (plan.goalOrder).
const orderGoals = (list, plan) => {
  const o = (plan && plan.goalOrder) || [];
  const at = (g, i) => { const k = o.indexOf(g.id); return k < 0 ? 1000 + i : k; };
  return list.map((g, i) => [g, at(g, i)]).sort((a, b) => a[1] - b[1]).map((x) => x[0]);
};
const periods = (step) => [step > 2 ? 'Jan–' + MONS[step - 2] : 'Jan', step < 12 ? MONS[step - 1] + '–Dec' : 'Dec'];
function MonthPick({ value, onChange, from = 2 }) {
  return (
    <select className="mp-sel" value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {MONS.map((m, i) => (i + 1 >= from ? <option key={m} value={i + 1}>{m}</option> : null))}
    </select>
  );
}
function MonthTable({ plan, w }) {
  const [aL, bL] = periods(w.step);
  const M = plan.money;
  return (
    <table className="mp-tbl">
      <thead><tr><th /><th>{aL}</th><th>{bL}</th></tr></thead>
      <tbody>
        <tr className="in"><td>Pay, after tax<small>Two paycheques</small></td><td>{fmt(w.payA)}</td><td>{fmt(w.payB)}</td></tr>
        <tr><td>Essentials</td><td>−{fmt(w.essMonth)}</td><td>−{fmt(w.essMonth)}</td></tr>
        <tr><td>RRSP</td><td>−{fmt(num(M.rrspA))}</td><td>−{fmt(num(M.rrspB))}</td></tr>
        <tr><td>Goal savings<small>{(w.queue || []).map((b) => b.t).join(', then ') || 'Your goals'}</small></td><td>−{fmt(num(M.efA))}</td><td>−{fmt(num(M.efB))}</td></tr>
        {num(M.pensionYou) > 0 && <tr><td>Your pension</td><td>−{fmt(num(M.pensionYou))}</td><td>−{fmt(num(M.pensionYou))}</td></tr>}
        <tr className="tot"><td>Left for fun</td><td>{fmt(w.leftA)}</td><td>{fmt(w.leftB)}</td></tr>
      </tbody>
    </table>
  );
}
function MonthDots({ value = [], onChange }) {
  const tap = (m) => onChange(value.includes(m) ? value.filter((x) => x !== m) : [...value, m].sort((a, b) => a - b));
  return <div className="mp-mons">{MONS.map((m, i) => <button key={m} className={value.includes(i + 1) ? 'on' : ''} onClick={() => tap(i + 1)}>{m.slice(0, 1)}</button>)}</div>;
}
const whenText = (when) => (when && when.length ? when.map((m) => MONS[m - 1]).join(', ') : 'Pick the months');
// up_01: your own dates in your money year. These three are where it starts.
const CAL_START = [
  { id: 'c-rrsp', m: 3, e: '🌳', t: 'RRSP deadline' },
  { id: 'c-tax', m: 4, e: '📄', t: 'Taxes due' },
  { id: 'c-sun', m: 12, e: '🏢', t: 'Sun Life statement' },
];
const calOf = (plan) => (Array.isArray(plan.calendar) ? plan.calendar : CAL_START);
// Dates that come from your other steps, so they're changed there.
const calAuto = (plan, w) => {
  const out = [];
  (w.extras || []).forEach((x) => (x.when || []).forEach((m) => out.push({ m, e: x.e, t: x.t, from: 'Your extras' })));
  const raise = num(plan.money.raiseMonth);
  if (raise) out.push({ m: raise, e: '📈', t: 'Raise' + (num(plan.money.raise) ? ', about ' + num(plan.money.raise) + '%' : ''), from: 'Each month' });
  if (w.step > 1) out.push({ m: w.step, e: '💰', t: 'Pay and savings step up', from: 'Each month' });
  const fin = plan.finishedAt ? new Date(plan.finishedAt).getMonth() + 1 : 10;
  out.push({ m: fin, e: '📆', t: 'Plan check-up', from: 'the month you made your plan' });
  return out.sort((a, b) => a.m - b.m);
};
function StepCalendar({ plan, set, w }) {
  const L = calOf(plan);
  const put = (list) => set({ calendar: list });
  const up = (id, p) => put(L.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const sorted = [...L].sort((a, b) => (num(a.m) || 1) - (num(b.m) || 1));
  return (
    <>
      <Card eb="🗓️ Your dates" ink="lavInk">
        {sorted.map((x) => (
          <EditRow key={x.id} e={x.e || '📌'} name={x.t} onName={(v) => up(x.id, { t: v })} onRemove={() => put(L.filter((y) => y.id !== x.id))} inline>
            <MonthPick from={1} value={num(x.m) || 1} onChange={(v) => up(x.id, { m: v })} />
          </EditRow>
        ))}
        {!sorted.length && <p className="mp-p first">No dates of your own yet.</p>}
        <AddLine placeholder="Add a date, like car insurance renews" onAdd={(t) => put([...L, { id: pid('c'), m: new Date().getMonth() + 1, e: '📌', t }])} />
        <Src k="y" />
      </Card>
      <Card eb="🧮 From your other steps" ink="skyInk">
        {calAuto(plan, w).map((x, i) => <Row key={i} e={x.e} bg="field" t={x.t} s={'Changes with ' + x.from} right={MONS[x.m - 1]} />)}
        <Src k="c" />
      </Card>
      <Reminders plan={plan} set={set} />
    </>
  );
}
// up_03: reminders that show up in your next steps
function Reminders({ plan, set }) {
  const R = remindersOf(plan);
  const put = (v) => set({ reminders: v });
  const o = listOps(R, put);
  return (
    <Card eb="🔔 Reminders in your next steps" ink="peachInk">
      {R.map((r, n) => (
        <ListItem key={r.id} x={{ ...r, e: '🔔' }} last={n === R.length - 1} onChange={(p) => o.up(r.id, p)} onRemove={() => o.rm(r.id, r.t)}>
          <div className="mp-field"><span>Shows up from</span><MonthPick from={1} value={Math.round(num(r.from)) || 1} onChange={(v) => o.up(r.id, { from: v })} /></div>
          <div className="mp-field last"><span>For<small>Leave it empty to keep it there until you tick it off</small></span><Amt plain prefix="" value={r.for} onChange={(v) => o.up(r.id, { for: v })} suffix="months" small /></div>
        </ListItem>
      ))}
      <AddLine placeholder="Add a reminder" onAdd={(t) => put([...R, { id: pid('r'), t, s: '', from: new Date().getMonth() + 1, for: 1 }])} />
      <p className="mp-hint left">Each one comes back every year in the months you pick, so you can tick it off again.</p>
    </Card>
  );
}
// up_01: your guesses about the future, moved here from How numbers work.
function StepGuesses({ plan, set }) {
  const A = plan.assume;
  const up = (p) => set({ assume: { ...A, ...p } });
  return (
    <>
      <Card>
        <div className="mp-field"><span>Prices go up</span><Amt prefix="" value={A.prices} onChange={(v) => up({ prices: v })} suffix="% a year" small /></div>
        <div className="mp-field"><span>Investments grow</span><Amt prefix="" value={A.growth} onChange={(v) => up({ growth: v })} suffix="% a year" small /></div>
        <div className="mp-field"><span>Growth once retired</span><Amt prefix="" value={A.retGrowth} onChange={(v) => up({ retGrowth: v })} suffix="% a year" small /></div>
        <div className="mp-field last"><span>Plan until</span><Amt plain prefix="" value={A.until} onChange={(v) => up({ until: v })} suffix="years old" small /></div>
        <Src k="y" />
      </Card>
      <Card eb="💡 Today's dollars" ink="skyInk">
        <p className="mp-p first">Big future amounts are shown in today's dollars, so $9,800 means what $9,800 buys now. Your plan grows them with prices when it works out what to set aside each month.</p>
      </Card>
    </>
  );
}

function MoneyYear({ plan, w }) {
  const ev = {};
  const add = (m, cls, t) => { (ev[m] = ev[m] || []).push([cls, t]); };
  (w.extras || []).forEach((x) => (x.when || []).forEach((m) => add(m, /cheque/i.test(x.t) ? 'e-cheque' : /refund/i.test(x.t) ? 'e-tax' : 'e-bonus', x.e + ' ' + x.t.replace(/uses$/, 'us').replace(/ques$/, 'que'))));
  const raise = num(plan.money.raiseMonth);
  if (raise) add(raise, 'e-pay', '📈 Raise' + (num(plan.money.raise) ? ', about ' + num(plan.money.raise) + '%' : ''));
  if (w.step > 1) { add(w.step - 1, 'e-check', '🔎 Check a pay stub'); add(w.step, 'e-pay', '💰 Pay goes up'); add(w.step, 'e-pay', '🌱 Savings step up'); }
  const fin = plan.finishedAt ? new Date(plan.finishedAt).getMonth() + 1 : 10;
  add(fin, 'e-check', '📆 Plan check-up');
  calOf(plan).forEach((x) => add(num(x.m) || 1, /rrsp|tax/i.test(x.t || '') ? 'e-tax' : 'e-check', (x.e ? x.e + ' ' : '') + (x.t || ''))); // up_01
  return (
    <div className="mp-year">
      {MONS.map((m, i) => (ev[i + 1] ? (
        <div key={m} className="mp-ym"><span className="mp-ymo">{m}</span><span className="mp-yev">{ev[i + 1].map(([c, t], k) => <span key={k} className={c}>{t}</span>)}</span></div>
      ) : null))}
    </div>
  );
}

/* ---------------------------------------------------------------- 1 · Your life */
function StepLife({ plan, set }) {
  const L = plan.life;
  const up = (p) => set({ life: { ...L, ...p } });
  return (
    <>
      <Card>
        <div className="mp-field"><span>Age</span><Amt plain prefix="" value={L.age} onChange={(v) => up({ age: v, ageYear: new Date().getFullYear() })} suffix="years old" small /></div>
        <div className="mp-field"><span>Living in</span><Txt value={L.city} onChange={(v) => up({ city: v })} /></div>
        <div className="mp-field last"><span>Living with</span><Txt value={L.livingWith} onChange={(v) => up({ livingWith: v })} placeholder="Just me" /></div>
      </Card>
      {L.livingWith && (
        <Card>
          <Q sub="This changes how shared costs and future family costs show up.">How do you and {L.livingWith} handle money?</Q>
          <Chips options={['All separate', 'Some shared', 'All shared']} value={L.together} onChange={(v) => up({ together: v })} />
        </Card>
      )}
      <Card>
        <Q>How does your pay come in?</Q>
        <Chips multi options={['Every two weeks', 'Every month', 'Quarterly bonus', 'Yearly bonus', 'Pay from Worthly HQ', 'Side work']} value={L.pay} onChange={(v) => up({ pay: v })} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 2 · What's ahead */
const DOTS = ['skyInk', 'blushInk', 'lavInk', 'butterInk', 'mintInk', 'peachInk'];
function StepAhead({ plan, set }) {
  const [open, setOpen] = useState(null);
  const list = plan.ahead || [];
  const upItem = (id, p) => set({ ahead: list.map((a) => (a.id === id ? { ...a, ...p } : a)) });
  return (
    <>
      <Card>
        <div className="mp-tl">
          {list.map((a, i) => (
            <div key={a.id} className="mp-ti" style={{ '--c': 'var(--' + DOTS[i % DOTS.length] + ')' }}>
              {open === a.id ? (
                <div className="mp-tiedit">
                  <Txt value={a.when} onChange={(v) => upItem(a.id, { when: v })} placeholder="When" />
                  <Txt value={a.t} onChange={(v) => upItem(a.id, { t: v })} placeholder="What" />
                  <Txt value={a.s} onChange={(v) => upItem(a.id, { s: v })} placeholder="A little note" />
                  <div className="mp-tiacts">
                    <button className="mp-link" onClick={() => setOpen(null)}>Done</button>
                    <button className="mp-link faint" onClick={() => { if (window.confirm('Remove ' + a.t + '?')) set({ ahead: list.filter((x) => x.id !== a.id) }); }}>Remove</button>
                  </div>
                </div>
              ) : (
                <button className="mp-tib" onClick={() => setOpen(a.id)}>
                  <span className="yr">{a.when}</span>
                  <b>{a.t} {a.e}</b>
                  {a.s && <small>{a.s}</small>}
                </button>
              )}
            </div>
          ))}
        </div>
        <button className="mp-more" onClick={() => { const id = pid('ah'); set({ ahead: [...list, { id, e: '✨', t: 'Something new', when: 'When?', s: '' }] }); setOpen(id); }}>+ Add a big moment</button>
      </Card>
      <p className="mp-hint">Tap any moment to change its timing. Your goals use these years too!</p>
    </>
  );
}

/* ---------------------------------------------------------------- 3 · What matters */
function StepValues({ plan, set, why, topValues }) {
  const V = plan.values;
  const up = (p) => set({ values: { ...V, ...p } });
  return (
    <>
      <Card tint="sky" eb="🌱 My why" ink="skyInk">
        <b className="mp-lead">{why && why.text ? why.text : 'Add your why on the Money tab, and it shows up here!'}</b>
      </Card>
      {topValues.length > 0 && (
        <Card eb="🧭 Most important" ink="lavInk">
          {topValues.map((v, i) => <Row key={v.id} e={v.emoji} bg={['blush', 'butter', 'sky', 'mint', 'peach'][i % 5]} t={v.label} />)}
        </Card>
      )}
      <Card>
        <Q>When money's tighter, what do you protect first?</Q>
        <Chips options={[...topValues.map((v) => v.label), 'Seeing family', 'Peace of mind'].filter((x, i, a) => a.indexOf(x) === i)} value={V.protect} onChange={(v) => up({ protect: v })} />
      </Card>
      <Card>
        <Q>In one sentence, what would make this plan feel like a success?</Q>
        <Txt area value={V.success} onChange={(v) => up({ success: v })} placeholder="Moving to Europe with a cushion…" />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 4 · What you own and owe */
function StepNetWorth({ plan, set, W, w }) {
  const NW = plan.nw;
  const upInv = (id, p) => set({ nw: { ...NW, inv: { ...NW.inv, [id]: { ...(NW.inv[id] || {}), ...p } } } });
  const upMan = (id, p) => set({ nw: { ...NW, manual: NW.manual.map((m) => (m.id === id ? { ...m, ...p } : m)) } });
  const addMan = (kind) => set({ nw: { ...NW, manual: [...NW.manual, { id: pid('nw'), e: kind === 'debt' ? '💳' : kind === 'cash' ? '🏦' : '📈', t: kind === 'debt' ? 'Something you owe' : kind === 'cash' ? 'Savings' : 'Investments', s: '', kind, amount: 0 }] } });
  const delMan = (m) => { if (window.confirm('Remove ' + m.t + '?')) set({ nw: { ...NW, manual: NW.manual.filter((x) => x.id !== m.id) } }); };
  const manRow = (m) => (
    <EditRow key={m.id} e={m.e} name={m.t} onName={(v) => upMan(m.id, { t: v })} onRemove={() => delMan(m)} src={<Src k="y" text={m.s || undefined} />}>
      <Amt value={m.amount} onChange={(v) => upMan(m.id, { amount: v })} small />
    </EditRow>
  );
  return (
    <>
      <Card tint="mint" eb="💚 Your net worth today" ink="mintInk">
        <div className="mp-big">{fmt(w.netWorth)}</div>
        <p className="mp-p">Everything you own minus everything you owe, right now. Worthly HQ isn't counted.</p>
      </Card>
      <Card eb="🏦 Cash and savings" ink="skyInk">
        {W.funds.map((f) => <Row key={f.id} e={f.emoji} bg="sky" t={f.name} right={fmt(f.balance)}>{<Src k="w" text={f.counted ? undefined : 'not in net worth'} />}</Row>)}
        <Row e="💳" bg="peach" t="Everyday cash" right={fmt(W.cash)}><Src k="w" /></Row>
        {NW.manual.filter((m) => m.kind === 'cash').map(manRow)}
        <button className="mp-more" onClick={() => addMan('cash')}>+ Add savings Worthly doesn't have</button>
      </Card>
      <Card eb="📈 Investments" ink="lavInk">
        {W.invs.map((a) => {
          const off = (NW.inv[a.id] || {}).count === false;
          return (
            <Row key={a.id} e="🌳" bg="lav" t={a.name} right={<span className={off ? 'mp-off' : ''}>{fmt(a.balance)}</span>}>
              <Src k="w" />
              <button className="mp-mini" onClick={() => upInv(a.id, { count: off })}>{off ? 'Count it in my plan' : 'Leave it out'}</button>
            </Row>
          );
        })}
        {NW.manual.filter((m) => m.kind === 'invest').map(manRow)}
        <button className="mp-more" onClick={() => addMan('invest')}>+ Add investments Worthly doesn't have</button>
      </Card>
      <Card eb="🚗 What you owe" ink="peachInk">
        {W.debts.map((d) => <Row key={d.id} e="🚙" bg="peach" t={d.name} right={'−' + fmt(d.balance)}><Src k="w" /></Row>)}
        {NW.manual.filter((m) => m.kind === 'debt').map(manRow)}
        {!W.debts.length && !NW.manual.some((m) => m.kind === 'debt') && <p className="mp-p">Nothing owed in Worthly!</p>}
        <button className="mp-more" onClick={() => addMan('debt')}>+ Add something you owe</button>
      </Card>
      {W.assets.length > 0 && (
        <Card eb="🏷️ Other things you own" ink="butterInk">
          {W.assets.map((a) => <Row key={a.id} e="🏷️" bg="butter" t={a.name} right={fmt(a.balance)}><Src k="w" /></Row>)}
        </Card>
      )}
      <p className="mp-hint">Anything from Worthly updates itself. Change those in Worthly, and the rest right here.</p>
    </>
  );
}

/* ---------------------------------------------------------------- 5 · Each month */
function StepMoney({ plan, set, w }) {
  const M = plan.money;
  const up = (p) => set({ money: { ...M, ...p } });
  const [aL, bL] = periods(w.step);
  const ess = M.essentials || [];
  const upE = (id, p) => up({ essentials: ess.map((e) => (e.id === id ? { ...e, ...p } : e)) });
  return (
    <>
      <Card><MonthTable plan={plan} w={w} /></Card>
      <p className="mp-hint left mp-out">In a month with a third paycheque, the extra one counts on the next step, Your extras.</p>
      <Card eb="💼 Your pay, after tax" ink="mintInk">
        <div className="mp-field"><span>Two paycheques, {aL}</span><Amt value={M.payA} onChange={(v) => up({ payA: v })} small /></div>
        <div className="mp-field"><span>Two paycheques, {bL}</span><Amt value={M.payB} onChange={(v) => up({ payB: v })} small /></div>
        <div className="mp-field last"><span>Pay goes up in<small>When CPP and EI are maxed out</small></span><MonthPick value={w.step} onChange={(v) => up({ stepMonth: v })} /></div>
      </Card>
      <Card eb="🌱 Saved from your pay" ink="skyInk">
        <div className="mp-field"><span>RRSP<small>{fmt(w.rrspYear)} a year</small></span><span className="mp-pair"><Amt value={M.rrspA} onChange={(v) => up({ rrspA: v })} suffix={aL} small /><Amt value={M.rrspB} onChange={(v) => up({ rrspB: v })} suffix={bL} small /></span></div>
        <div className="mp-field last"><span>Goal savings<small>{fmt(w.efYear)} a year · fills your goals below, one at a time</small></span><span className="mp-pair"><Amt value={M.efA} onChange={(v) => up({ efA: v })} suffix={aL} small /><Amt value={M.efB} onChange={(v) => up({ efB: v })} suffix={bL} small /></span></div>
      </Card>
      <Card eb="🎯 Your goal savings, in order" ink="lavInk"><SaveLine plan={plan} set={set} w={w} /></Card>
      <Card eb="🧾 Your essentials" ink="peachInk">
        {ess.map((e) => (
          <EditRow key={e.id} inline e={e.e} name={e.t} onName={(v) => upE(e.id, { t: v })} onRemove={() => { if (window.confirm('Remove ' + e.t + '?')) up({ essentials: ess.filter((x) => x.id !== e.id) }); }}>
            <Amt value={e.amount} onChange={(v) => upE(e.id, { amount: v })} small />
          </EditRow>
        ))}
        <button className="mp-more" onClick={() => up({ essentials: [...ess, { id: pid('e'), e: '🧾', t: 'Something else', amount: 0 }] })}>+ Add an essential</button>
        <Calc rows={[Object.assign(['Each month', fmt(w.essMonth)], { tot: true })]} />
      </Card>
      <Card tint="sky" eb="🛟 Only for an emergency month" ink="skyInk">
        <div className="mp-field"><span>Life buffer<small>For nice-to-haves if you lost your job. Day to day, it's part of your fun money.</small></span><Amt value={M.buffer} onChange={(v) => up({ buffer: v })} small /></div>
        <Calc rows={[['Essentials', fmt(w.essMonth)], ['Life buffer', fmt(w.buffer)], Object.assign(['An emergency month', fmt(w.emergencyMonth)], { tot: true })]} />
      </Card>
      <Card tint="lav" eb="🏢 Work pension" ink="lavInk">
        <div className="mp-field"><span>Your employer puts in<small>A month · it never touches your pay</small></span><Amt value={M.pension} onChange={(v) => up({ pension: v })} small /></div>
        <div className="mp-field last"><span>You put in<small>A month, taken off your pay</small></span><Amt value={M.pensionYou} onChange={(v) => up({ pensionYou: v })} small /></div>
        <Calc rows={[Object.assign(['A year, all together', fmt(w.pensionYear)], { tot: true })]} />
      </Card>
    </>
  );
}

// ln_31: where your goal savings go, in order. Each goal fills up, then the next one starts.
function SaveLine({ plan, set, w }) {
  const M = plan.money;
  const ids = (w.queue || []).map((b) => b.id);
  const tidy = (q) => [...q.filter((x) => x !== 'retire'), ...(q.includes('retire') ? ['retire'] : [])];
  const setQ = (q) => set({ money: { ...M, queue: tidy(q) } });
  const shift = (i, d) => { const q = [...ids]; const j = i + d; if (j < 0 || j >= q.length || q[i] === 'retire' || q[j] === 'retire') return; [q[i], q[j]] = [q[j], q[i]]; setQ(q); };
  // every goal you have, now or later, can join the line
  const pool = allGoals(plan, w).map((g) => ({ id: g.id, e: g.e, t: g.t })).filter((x) => !ids.includes(x.id));
  const filling = (w.queue || []).find((b) => !b.needs && !(b.have >= b.target));
  const B = Array.isArray(M.boosts) ? M.boosts : [];
  const upB = (id, p) => set({ money: { ...M, boosts: B.map((b) => (b.id === id ? { ...b, ...p } : b)) } });
  const ymOf = (b) => { const [yy, mm] = String(b.start || '').split('-').map(Number); return [yy || new Date().getFullYear() + 1, mm || 1]; };
  return (
    <>
      <p className="mp-p first">Your goal savings fill one goal at a time. When one is full, the money moves to the next. Change the amounts or the order, and every date updates.</p>
      <div className="ln31-q">
        {(w.queue || []).map((b, i) => {
          const full = !b.needs && b.have >= b.target;
          const when = b.needs ? 'Add an amount on its page to give it a date'
            : full ? 'Full now'
            : b.keeps ? (b === filling ? 'Filling now' : b.startAt ? 'From ' + monthYear(b.startAt) : 'Not started') + ', until you retire'
            : (b === filling ? 'Filling now' : b.startAt ? 'Starts ' + monthYear(b.startAt) : '') + (b.fullAt ? ' · full ' + monthYear(b.fullAt) : ' · not full yet');
          const late = b.st === 'behind' && b.by;
          return (
            <div key={b.id} className={'ln31-qi' + (b === filling ? ' on' : '')}>
              <span className="n">{i + 1}</span>
              <span className="e">{b.e}</span>
              <span className="tx"><b>{b.t}</b><small>{b.keeps ? fmt(b.have) + ' so far' : b.needs ? 'No amount yet' : fmt(b.have) + ' of ' + fmt(b.target)}</small><em className={late ? 'late' : ''}>{when}{late ? ' · wanted by ' + monthYear(b.by) : ''}</em></span>
              <span className="acts">
                <button aria-label="Move up" disabled={i === 0 || b.id === 'retire'} onClick={() => shift(i, -1)}>↑</button>
                <button aria-label="Move down" disabled={i === ids.length - 1 || ids[i + 1] === 'retire' || b.id === 'retire'} onClick={() => shift(i, 1)}>↓</button>
                <button aria-label="Take out" onClick={() => setQ(ids.filter((x) => x !== b.id))}>✕</button>
              </span>
            </div>
          );
        })}
      </div>
      {w.queueDoneAt && !ids.includes('retire') && <p className="mp-hint left">From {monthYear(w.queueDoneAt)} everything above is full, so your goal savings have nowhere to go. Add another goal, or Retirement.</p>}
      {pool.length > 0 && (
        <>
          <p className="mp-flab" style={{ margin: '14px 2px 8px' }}>Add a goal to the line</p>
          <div className="ln31-add">{pool.map((a) => <button key={a.id} onClick={() => setQ([...ids, a.id])}>+ {a.e} {a.t}</button>)}</div>
        </>
      )}
      <p className="mp-flab" style={{ margin: '18px 2px 4px' }}>Money that frees up later</p>
      <p className="mp-hint left" style={{ marginTop: 0 }}>From its start month, it joins your goal savings and goes to whichever goal is filling.</p>
      {B.map((b) => {
        const [yy, mm] = ymOf(b);
        return (
          <div key={b.id} className="mp-boost">
            <div className="mp-boost-top">
              <span className="mp-ic sm" style={{ background: 'var(--card)' }}>{b.e}</span>
              <input className="mp-name" value={b.t} onChange={(ev) => upB(b.id, { t: ev.target.value })} />
              <button className="mp-x" aria-label="Remove" onClick={() => { if (window.confirm('Remove ' + b.t + '?')) set({ money: { ...M, boosts: B.filter((x) => x.id !== b.id) } }); }}>✕</button>
            </div>
            <div className="mp-boost-grid">
              <div className="mp-boost-cell"><small>A month</small><Amt value={b.amount} onChange={(v) => upB(b.id, { amount: v })} small /></div>
              <div className="mp-boost-cell"><small>Starts</small><span className="mp-boost-when"><MonthPick from={1} value={mm} onChange={(v) => upB(b.id, { start: yy + '-' + String(v).padStart(2, '0') })} /><Amt plain prefix="" value={yy} onChange={(v) => upB(b.id, { start: Math.round(num(v)) + '-' + String(mm).padStart(2, '0') })} small /></span></div>
            </div>
          </div>
        );
      })}
      <button className="mp-more" onClick={() => set({ money: { ...M, boosts: [...B, { id: pid('b'), e: '✨', t: 'Something that frees up', amount: 0, start: (new Date().getFullYear() + 1) + '-01' }] } })}>+ Add money that frees up later</button>
    </>
  );
}

/* ---------------------------------------------------------------- 5b · Your extras */
function StepExtras({ plan, set, w }) {
  const X = plan.extras || [];
  const S = plan.sinking || [];
  const upX = (id, p) => set({ extras: X.map((x) => (x.id === id ? { ...x, ...p } : x)) });
  const upS = (id, p) => set({ sinking: S.map((x) => (x.id === id ? { ...x, ...p } : x)) });
  return (
    <>
      <div className="mp-stage" style={{ background: 'var(--butter)' }}>
        <p className="eb" style={{ color: 'var(--butterInk)' }}>💛 Coming in, a year</p>
        {X.map((x) => (
          <div key={x.id} className="mp-erow glass">
            <div className="top"><input className="mp-name" value={x.t} onChange={(e) => upX(x.id, { t: e.target.value })} /><span className="amts"><Amt value={x.amount} onChange={(v) => upX(x.id, { amount: v })} small /></span><button className="mp-x" aria-label="Remove" onClick={() => { if (window.confirm('Remove ' + x.t + '?')) set({ extras: X.filter((y) => y.id !== x.id) }); }}>✕</button></div>
            <div className="bot"><MonthDots value={x.when || []} onChange={(v) => upX(x.id, { when: v })} /></div>
          </div>
        ))}
        <button className="mp-more" onClick={() => set({ extras: [...X, { id: pid('x'), e: '✨', t: 'Something else', when: [], amount: 0 }] })}>+ Add an extra</button>
        <Calc rows={[Object.assign(['A year', fmt(w.extrasIn)], { tot: true })]} />
      </div>
      <div className="mp-arrow">↓</div>
      <div className="mp-stage" style={{ background: 'var(--blush)' }}>
        <p className="eb" style={{ color: 'var(--blushInk)' }}>🫙 Fills your sinking funds</p>
        {w.sinking.map((f) => (
          <div key={f.id} className="mp-erow glass">
            <div className="top"><span className="mp-emo">{f.e}</span><input className="mp-name" value={f.t} onChange={(e) => upS(f.id, { t: e.target.value })} /><span className="amts">{f.kind === 'travel' ? <span className="mp-fixed">{fmt(f.amount)}</span> : <Amt value={f.amount} onChange={(v) => upS(f.id, { amount: v })} small />}</span><button className="mp-x" aria-label="Remove" onClick={() => { if (window.confirm('Remove ' + f.t + '?')) set({ sinking: S.filter((y) => y.id !== f.id) }); }}>✕</button></div>
            <div className="bot">
              {f.kind === 'travel' ? <Src k="c" text="from Seeing family" /> : <button className={'mp-once' + (f.once ? ' on' : '')} onClick={() => upS(f.id, { once: !f.once })}>{f.once ? 'Just this year' : 'Every year'}</button>}
            </div>
          </div>
        ))}
        <button className="mp-more" onClick={() => set({ sinking: [...S, { id: pid('s'), e: '🫙', t: 'Another fund', amount: 0 }] })}>+ Add a sinking fund</button>
        <Calc rows={[Object.assign(['A year', fmt(w.fundsYear)], { tot: true })]} />
      </div>
      <div className="mp-arrow">↓</div>
      {w.extrasLeft >= 0 ? (
        <div className="mp-stage" style={{ background: 'var(--mint)' }}>
          <p className="eb" style={{ color: 'var(--mintInk)' }}>🌱 What's left is free</p>
          <div className="mp-bigrow"><span className="mp-big">{fmt(w.extrasLeft)}</span><span>left over</span></div>
          <p className="mp-p">Your plan doesn't count on it, so if you dip into your sinking funds during the year, nothing changes. Anything left at the end is yours to use however you like!</p>
        </div>
      ) : (
        <div className="mp-stage" style={{ background: 'var(--peach)' }}>
          <p className="eb" style={{ color: 'var(--peachInk)' }}>🌼 A little short</p>
          <b className="mp-lead">Your sinking funds need {fmt(-w.extrasLeft)} more than your extras bring in.</b>
          <p className="mp-p">You could lower a fund, or count on a bigger bonus.</p>
        </div>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- 6 · What you owe */
function StepDebt({ plan, set, w }) {
  const up = (id, p) => set({ debts: { ...plan.debts, [id]: { ...(w.debts.find((d) => d.id === id) ? { rate: w.debts.find((d) => d.id === id).rate, pay: w.debts.find((d) => d.id === id).pay, every: w.debts.find((d) => d.id === id).every, ends: w.debts.find((d) => d.id === id).ends } : {}), ...(plan.debts[id] || {}), ...p } } });
  if (!w.debts.length) return <Card tint="mint" eb="🎉 Nothing owed" ink="mintInk"><p className="mp-p">You don't owe anything in Worthly, so this step is done!</p></Card>;
  return (
    <>
      {w.debts.map((d) => (
        <Card key={d.id} eb={'🚙 ' + d.name} ink="peachInk">
          <div className="mp-big">{fmt(d.balance)}</div>
          <Src k="w" text="left to pay" />
          {d.done != null && <><Bar pct={d.done} ink="peachInk" /><p className="mp-hint left">{d.done}% paid off!</p></>}
          <div className="mp-field"><span>Interest rate</span><Amt prefix="" value={d.rate} onChange={(v) => up(d.id, { rate: v })} suffix="%" small /></div>
          <div className="mp-field"><span>Payment</span><Amt value={d.pay} onChange={(v) => up(d.id, { pay: v })} small /></div>
          <Chips options={['Every week', 'Every two weeks', 'Every month']} value={d.every} onChange={(v) => up(d.id, { every: v })} />
          <div className="mp-field last" style={{ marginTop: 10 }}><span>Last payment</span><input className="mp-date" type="date" value={d.ends} onChange={(e) => up(d.id, { ends: e.target.value })} /></div>
          <Calc rows={[['A month', fmt(d.monthly)]]} />
        </Card>
      ))}
      <Card>
        <Q sub="At your rate, every extra dollar saves you that much interest for sure. Investing might earn more, but it isn't guaranteed.">Pay it off faster?</Q>
        <Chips options={['Add a little extra', 'Keep it as it is', 'Decide at my check-up']} value={plan.debtChoice} onChange={(v) => set({ debtChoice: v })} />
      </Card>
      <Card tint="mint"><p className="mp-p first">When it's paid off, your plan asks where the {fmt(w.carMonthly)} a month should go! 🚗💚</p></Card>
    </>
  );
}

/* ---------------------------------------------------------------- 7 · Your cushion */
function StepCushion({ plan, set, W, w }) {
  const C = plan.cushion;
  const up = (p) => set({ cushion: { ...C, ...p } });
  return (
    <>
      <Card tint="sky" eb={'🛟 ' + (w.efFund ? w.efFund.name : 'Emergency & future fund')} ink="skyInk">
        <div className="mp-bigrow"><span className="mp-big">{fmt(w.efBal)}</span><span>of {fmt(w.efTarget)}</span></div>
        <Bar pct={pct(w.efBal, w.efTarget)} ink="skyInk" track="var(--glass)" />
        <div className="mp-split"><span>Covers {efCover(plan, w).total.toFixed(1)} months</span><span>{w.efFull === 'Full!' ? 'Full! 🎉' : w.efFull ? 'Full about ' + w.efFull : ''}</span></div>
      </Card>
      <Card eb="🔗 Saved in" ink="mintInk">{/* up_02 */}
        <FundPicker W={W} w={w} noneValue="none" value={C.fundId || (w.efFund && w.efFund.id)} onPick={(v) => up({ fundId: v })} />
        {!w.efFund && <div className="mp-field" style={{ marginTop: 12 }}><span>Have now</span><Amt value={C.saved || 0} onChange={(v) => up({ saved: v })} small /></div>}
        <div className="mp-field" style={{ marginTop: 14 }}><span>Goal</span><Amt value={C.target} onChange={(v) => up({ target: v })} small /></div>
        <div className="mp-field"><span>Extra months from severance and EI<small>Added on top of what your savings cover. Leave it empty to count your savings only.</small></span><Amt plain prefix="" value={C.extraMonths} onChange={(v) => up({ extraMonths: v })} suffix="months" small /></div>
        <div className="mp-field last"><span>Want it full by<small>A month and year, or leave the year empty</small></span><span className="mp-pair"><MonthPick from={1} value={C.byMonth || 12} onChange={(v) => up({ byMonth: v })} /><Amt plain prefix="" value={C.by} onChange={(v) => up({ by: Math.round(num(v)) || '' })} small /></span></div>
      </Card>
      <Card eb="🧮 How it's worked out" ink="peachInk">
        <Calc rows={[
          ['Essentials a month', fmt(w.essMonth)],
          ['Life buffer', fmt(w.buffer)],
          Object.assign(['An emergency month', fmt(w.emergencyMonth)], { tot: true }),
          ['Covered now', w.efMonthsNow.toFixed(1) + ' months'],
          ['Covered when full', w.efMonthsFull.toFixed(1) + ' months'],
          ['Going in a year, from pay and extras', fmt(w.efAll)],
          ...(w.efLeft > 0 && w.efAll > 0 ? [Object.assign([fmt(w.efLeft) + ' to go', 'full about ' + w.efFull], { b: true })] : []),
        ]} />
        <p className="mp-hint left">Change your essentials and life buffer on the Each month step.</p>
        <p className="mp-hint left">Once it's full, your pay savings go to your future fund instead.</p>
      </Card>
      <Card>
        <Q>What's it for?</Q>
        <Txt area value={C.purpose} onChange={(v) => up({ purpose: v })} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 8 · Insurance */
function StepInsurance({ plan, set }) {
  const L = insOf(plan);
  const put = (v) => set({ insList: v });
  const o = listOps(L, put);
  return (
    <>
      <Card>
        {L.map((i, n) => (
          <ListItem key={i.k} x={i} last={n === L.length - 1} onChange={(p) => o.up(i.k, p)} onRemove={() => o.rm(i.k, i.t)}>
            <Chips options={i.opts || INS_OPTS} value={plan.insurance[i.k]} onChange={(v) => set({ insurance: { ...plan.insurance, [i.k]: v } })} />
          </ListItem>
        ))}
        <AddLine placeholder="Add another kind of insurance" onAdd={(t) => put([...L, { k: pid('i'), e: '🛡️', t, s: '', opts: INS_OPTS }])} />
      </Card>
      <Card>
        <Q sub="Your plan brings this back up when a baby's on the way, since that's when life insurance matters more.">Does anyone rely on your income?</Q>
        <Chips options={['Not yet', 'Yes']} value={plan.rely} onChange={(v) => set({ rely: v })} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 9 · Paperwork */
function StepPaper({ plan, set }) {
  const L = paperOf(plan);
  const put = (v) => set({ paperList: v });
  const o = listOps(L, put);
  return (
    <Card>
      {L.map((p, n) => (
        <ListItem key={p.k} x={p} last={n === L.length - 1} onChange={(q) => o.up(p.k, q)} onRemove={() => o.rm(p.k, p.t)}>
          <Chips options={['Done', 'Not yet']} value={plan.paper[p.k] ? 'Done' : 'Not yet'} onChange={(v) => set({ paper: { ...plan.paper, [p.k]: v === 'Done' } })} />
        </ListItem>
      ))}
      <AddLine placeholder="Add something, like update my will" onAdd={(t) => put([...L, { k: pid('p'), e: '📄', t, s: '' }])} />
    </Card>
  );
}

/* ---------------------------------------------------------------- 10 · Goals */
function GoalEditor({ g, funds, onSave, onDelete, onClose }) {
  const [x, setX] = useState(g);
  const u = (p) => setX({ ...x, ...p });
  const linked = x.fundId ? funds.find((f) => f.id === String(x.fundId)) : null;
  return (
    <SheetWrap>
      <div className="sheet-dim" onClick={onClose} />
      <div className="sheet mp-sh">
        <div className="mp-shead"><b>{g.isNew ? 'New goal' : 'This goal'}</b><button className="mp-link" onClick={onClose}>Cancel</button></div>
        <div className="mp-field col"><span>Goal</span><div className="mp-namerow"><input className="mp-txt emoji" value={x.e} onChange={(e) => u({ e: e.target.value })} /><Txt area value={x.t} onChange={(v) => u({ t: v })} placeholder="What for?" /></div></div>
        <div className="mp-field"><span>Amount</span><Amt value={x.amount} onChange={(v) => u({ amount: v })} small /></div>
        <div className="mp-field"><span>By</span><span className="mp-pair"><MonthPick from={1} value={x.month || 12} onChange={(v) => u({ month: v })} /><Amt plain prefix="" value={x.year} onChange={(v) => u({ year: v ? Math.round(v) : '' })} suffix="year" small /></span></div>
        <div className="mp-field col"><span>A note</span><Txt value={x.s} onChange={(v) => u({ s: v })} placeholder="Optional" /></div>
        <p className="mp-flab">Saved in</p>
        <Chips options={['Typed in', ...funds.map((f) => f.emoji + ' ' + f.name)]} value={linked ? linked.emoji + ' ' + linked.name : 'Typed in'}
          onChange={(v) => { const f = funds.find((ff) => ff.emoji + ' ' + ff.name === v); u({ fundId: f ? f.id : null }); }} />
        <div className="mp-field"><span>Have now</span>{linked ? <span className="mp-fixed">{fmt(linked.balance)}</span> : <Amt value={x.saved || 0} onChange={(v) => u({ saved: v })} small />}</div>
        <div className="mp-field"><span>Putting in a month</span><Amt value={x.monthly || 0} onChange={(v) => u({ monthly: v })} small /></div>
        <p className="mp-flab">Colour</p>{/* plan_36 */}
        <ColourRow value={x.color || 'peach'} onChange={(v) => u({ color: v })} />
        <p className="mp-flab">How important</p>
        <Chips options={['Must have', 'Would love', 'Nice to have']} value={tierTag[x.tier][1]} onChange={(v) => u({ tier: v === 'Must have' ? 'must' : v === 'Would love' ? 'love' : 'nice' })} />
        <button className="mp-btn" disabled={!String(x.t || '').trim()} onClick={() => onSave(x)}>Save</button>
        {!g.isNew && <button className="mp-link mid" onClick={onDelete}>Remove this goal</button>}
      </div>
    </SheetWrap>
  );
}

function SpecialEditor({ kind, plan, set, W, w, onClose }) {
  const B = plan.baby, RS = plan.resp;
  const ub = (p) => set({ baby: { ...B, ...p } });
  const linked = B.fundId ? W.funds.find((f) => f.id === String(B.fundId)) : null;
  return (
    <SheetWrap>
      <div className="sheet-dim" onClick={onClose} />
      <div className="sheet mp-sh">
        <div className="mp-shead"><b>{kind === 'baby' ? ((nameOf(plan, 'baby') || {}).t || 'Life events') : ((nameOf(plan, 'resp') || {}).t || "Kids' education")}</b><button className="mp-link" onClick={onClose}>Done</button></div>
        {kind === 'baby' ? (
          <>
            <p className="mp-flab">What it needs</p>
            <div className="mp-field"><span>Around</span><span className="mp-pair"><MonthPick from={1} value={B.month || 1} onChange={(v) => ub({ month: v })} /><Amt plain prefix="" value={B.year} onChange={(v) => ub({ year: Math.round(v) })} suffix="year" small /></span></div>
            <div className="mp-field"><span>Months of leave</span><Amt plain prefix="" value={B.months} onChange={(v) => ub({ months: v })} suffix="months" small /></div>
            <div className="mp-field"><span>EI a month<small>55% of your pay, up to that year's EI cap</small></span><Amt value={B.ei} onChange={(v) => ub({ ei: v })} small /></div>
            <div className="mp-field"><span>Weeks topped up by work</span><Amt plain prefix="" value={B.topWeeks} onChange={(v) => ub({ topWeeks: v })} suffix="weeks" small /></div>
            <div className="mp-field"><span>Top-up a month, while it lasts<small>{B.topUp == null ? 'Up to your full pay' : <button className="mp-mini" onClick={() => ub({ topUp: null })}>Use up to my full pay</button>}</small></span><Amt value={B.topUp} auto={w.topUpMonthly} onChange={(v) => ub({ topUp: v })} small /></div>
            <Calc rows={[
              ['Your pay, ' + num(B.months) + ' months', fmt(w.babyPay)],
              ['EI, ' + num(B.months) + ' months', '−' + fmt(w.babyEi)],
              ['Top-up, ' + num(B.topWeeks) + ' weeks', '−' + fmt(w.topUpTotal)],
              Object.assign(['The gap to cover', fmt(w.babyGapCalc)], { tot: true }),
            ]} />
            <div className="mp-field" style={{ marginTop: 12 }}><span>Your goal<small>Worked out: {fmt(w.babyGapCalc)}. Clear it to use that.</small></span><Amt value={B.target || null} auto={Math.round(w.babyGapCalc)} onChange={(v) => ub({ target: v })} small /></div>{/* up_02 */}
            <p className="mp-flab">How you're saving for it</p>
            <Chips options={['Typed in', ...W.funds.map((f) => f.emoji + ' ' + f.name)]} value={linked ? linked.emoji + ' ' + linked.name : 'Typed in'}
              onChange={(v) => { const f = W.funds.find((ff) => ff.emoji + ' ' + ff.name === v); ub({ fundId: f ? f.id : null }); }} />
            {!linked && <p className="mp-hint left">Make a fund for it in Worthly, then pick it here and its balance fills in on its own.</p>}
            <div className="mp-field"><span>Have now</span>{linked ? <span className="mp-fixed">{fmt(linked.balance)}</span> : <Amt value={B.saved} onChange={(v) => ub({ saved: v })} small />}</div>
            <div className="mp-field"><span>Putting in a month</span><Amt value={B.monthly} onChange={(v) => ub({ monthly: v })} small /></div>
            <Calc rows={[[fmt(Math.max(0, w.babyGap - w.babyHave)) + ' over ' + w.babyMonthsTo + ' months', fmt(w.babyNeedMonthly) + ' a month']]}>
              <div className="ln tot"><span>Status</span><Pill s={w.status.baby} /></div>
            </Calc>
            <p className="mp-flab">How important</p>
            <Chips options={['Must have', 'Would love', 'Nice to have']} value={tierTag[B.tier][1]} onChange={(v) => ub({ tier: v === 'Must have' ? 'must' : v === 'Would love' ? 'love' : 'nice' })} />
            <button className="mp-link mid" onClick={() => { ub({ on: !B.on }); onClose(); }}>{B.on ? 'Take this out of my plan' : 'Put this back in my plan'}</button>
          </>
        ) : (
          <>
            <div className="mp-field"><span>Children</span><Amt plain prefix="" value={RS.kids} onChange={(v) => set({ resp: { ...RS, kids: v } })} small /></div>
            <div className="mp-field"><span>Into an RESP a year</span><Amt value={RS.yearly} onChange={(v) => set({ resp: { ...RS, yearly: v } })} small /></div>
            <Calc rows={[['You put in a year', fmt(num(RS.yearly))], ['Government adds 20%', '+' + fmt(w.respGrant)], Object.assign(['By 18, with growth', short(w.respTotal)], { tot: true })]} />
            <p className="mp-hint left">Per child, in today's dollars. The government top-up stops at $7,200 per child.</p>
            <p className="mp-flab">How important</p>
            <Chips options={['Must have', 'Would love', 'Nice to have']} value={tierTag[RS.tier][1]} onChange={(v) => set({ resp: { ...RS, tier: v === 'Must have' ? 'must' : v === 'Would love' ? 'love' : 'nice' } })} />
            <button className="mp-link mid" onClick={() => { set({ resp: { ...RS, on: !RS.on } }); onClose(); }}>{RS.on ? 'Take this out of my plan' : 'Put this back in my plan'}</button>
          </>
        )}
      </div>
    </SheetWrap>
  );
}

// plan_32: your own name and emoji for a built-in goal (plan.goalNames[id]).
const nameOf = (plan, id) => ((plan && plan.goalNames) || {})[id] || null;
const BUILT_NAMES = { efund: 'Emergency fund', travel: 'Seeing family', move: 'Moving abroad', baby: 'Life events', retire: 'Retirement', resp: "Kids' education" }; // up_02
const applyNames = (plan, list) => list.map((g) => {
  const o = !g.custom && nameOf(plan, g.id);
  return o ? { ...g, t: o.t || g.t, e: o.e || g.e, bg: o.c || g.bg, ink: o.c ? o.c + 'Ink' : g.ink, renamed: true } : g; // plan_36: colour too
});
// plan_36: goal colours
const GOAL_COLOURS = [['blush', 'Pink'], ['lav', 'Lavender'], ['sky', 'Blue'], ['mint', 'Green'], ['butter', 'Yellow']];
const ColourRow = ({ value, onChange }) => (
  <div className="mp-colours">{GOAL_COLOURS.map(([c, l]) => <button key={c} type="button" aria-label={l} className={c === value ? 'on' : ''} style={{ background: 'var(--' + c + 'Mid)' }} onClick={() => onChange(c)} />)}</div>
);
const NAME_EMOJIS = ['\u{1F33F}', '\u{1F331}', '\u{1FAB4}', '\u{1F338}', '\u{1F337}', '\u{1F33B}', '\u{1F6DF}', '✈️', '\u{1F30D}', '\u{1F3E1}', '\u{1F393}', '\u{1F48E}', '⭐', '\u{1F381}', '\u{1F49B}', '\u{1F4AB}'];
function NameEditor({ g, original, onSave, onReset, onClose }) {
  const [t, setT] = useState(g.t);
  const [e, setE] = useState(g.e);
  const [c, setC] = useState(g.bg);
  return (
    <SheetWrap>
      <div className="sheet-dim" onClick={onClose} />
      <div className="sheet mp-sh">
        <div className="mp-shead"><b>Name, emoji and colour</b><button className="mp-link" onClick={onClose}>Cancel</button></div>
        <div className="mp-field col"><span>Goal</span><div className="mp-namerow"><input className="mp-txt emoji" value={e} onChange={(ev) => setE(ev.target.value)} /><Txt value={t} onChange={setT} placeholder="Name" /></div></div>
        <div className="mp-emojis">{NAME_EMOJIS.map((x) => <button key={x} type="button" className={x === e ? 'on' : ''} onClick={() => setE(x)}>{x}</button>)}</div>
        <p className="mp-flab">Colour</p>
        <ColourRow value={c} onChange={setC} />
        <button className="mp-btn" disabled={!String(t || '').trim()} onClick={() => onSave({ t: String(t).trim(), e: String(e || '').trim() || g.e, c })}>Save</button>
        {g.renamed && <button className="mp-link mid" onClick={onReset}>Go back to "{original}"</button>}
      </div>
    </SheetWrap>
  );
}

// Every goal in one list, so the plan page, the goals step and the goals page agree.
function allGoals(plan, w) {
  const B = plan.baby, RS = plan.resp;
  const yr = (v) => fmt(v) + '/yr', mo = (v) => fmt(v) + '/mo';
  return applyNames(plan, [ // plan_32
    { id: 'efund', e: '🛟', t: w.efFund ? w.efFund.name : 'Emergency fund', tier: (plan.goalTiers || {}).efund || 'must', status: w.status.efund, bg: 'sky', ink: 'skyInk', page: 'cushion',
      sub: fmt(w.efTarget) + (w.efFull ? (w.efFull === 'Full!' ? ' · full!' : ' · full about ' + w.efFull) : ''),
      bars: [[pct(w.efBal, w.efTarget), 'skyInk']], cells: [['Have', fmt(w.efBal)], ['From pay', yr(w.efYear)]] },
    { id: 'travel', e: '🇳🇬', t: 'Seeing family', tier: (plan.goalTiers || {}).travel || 'must', status: w.status.travel, bg: 'lav', ink: 'lavInk', page: 'travel',
      sub: fmt(w.travelGoal) + ' this year',
      bars: [[pct(w.travelUsed, w.travelGoal), 'lavSoft'], [pct(w.travelFundBal, w.travelGoal), 'lavInk']], cells: [['Used', fmt(w.travelUsed)], ['In the fund', fmt(w.travelFundBal)], ['From extras', yr(w.travelNowYear)]] },
    { id: 'move', e: '✈️', t: 'Moving abroad', tier: plan.move.tier || 'must', status: w.status.move, bg: 'blush', ink: 'blushInk', page: 'move',
      sub: fmt(w.moveTotal) + (w.futureFull ? ' · full about ' + w.futureFull : ''),
      bars: [[pct(w.futureHave, w.moveTotal), 'blushInk']], cells: [['Have', fmt(w.futureHave)], ['Starts', w.efLeft <= 0 ? 'Now' : w.futureStart ? monthYear(w.futureStart) : '—'], ['Then', fmt(w.futureMonthly) + '/mo']],
      note: w.futureFund ? null : 'Make a Future Fund in Worthly and pick it on the Moving step.' },
    ...(B.on ? [{ id: 'baby', e: '\u{1F33F}', t: 'Life events', tier: B.tier, status: w.status.baby, bg: 'blush', ink: 'blushInk', special: 'baby',
      sub: fmt(w.babyGap) + ' by ' + byLabel(B.month, B.year) + (w.babyFund ? ' · ' + w.babyFund.name : ''),
      bars: [[pct(w.babyHave, w.babyGap), 'blushInk']], cells: [['Have', fmt(w.babyHave)], ['Needs', mo(w.babyNeedMonthly)], ['Putting in', mo(w.babyMonthly)]],
      note: w.status.baby === 'notyet' && !w.babyFund ? 'Starts once you make a fund for it in Worthly.' : null }] : []),
    { id: 'retire', e: '🌻', t: 'Retirement', tier: (plan.goalTiers || {}).retire || 'must', status: w.status.retire, bg: 'mint', ink: 'mintInk', page: 'retire',
      sub: short(w.retNeed) + ' by ' + w.retAge,
      bars: [[pct(w.invested, w.retNeed), 'mintInk']], cells: [['Have', short(w.invested)], ['You add', yr(w.rrspAll)], ['Employer adds', yr(w.colgateYear)]] },
    ...(RS.on ? [{ id: 'resp', e: '🎓', t: "Kids' education", tier: RS.tier, status: 'later', bg: 'butter', ink: 'butterInk', special: 'resp', sub: 'Starts when baby arrives' }] : []),
    ...w.goals.map((g) => ({ ...g, custom: true, bg: g.color || 'peach', ink: (g.color || 'peach') + 'Ink', // plan_36
      sub: (num(g.amount) ? fmt(num(g.amount)) : 'Add an amount to start') + (g.year ? ' by ' + byLabel(g.month, g.year) : '') + ((w.queue || []).some((b) => b.id === g.id && b.fullAt) ? ' · full about ' + monthYear(w.queue.find((b) => b.id === g.id).fullAt) : '') + (g.s ? ' · ' + g.s : ''),
      bars: num(g.amount) ? [[g.pct, 'peachInk']] : null, cells: num(g.amount) ? [['Have', fmt(g.saved)], ['Needs', mo(g.need)], ['Putting in', mo(g.monthly)]] : null })),
  ]);
}
function GoalLine({ g, onClick }) {
  return (
    <div className={'mp-gl' + (onClick ? ' tap' : '')} onClick={onClick}>
      <div className="gh"><span className="mp-ic sm" style={{ background: 'var(--' + g.bg + ')' }}>{g.e}</span><span className="t">{g.t}<small>{g.sub}</small></span><Pill s={g.status} /></div>
      {g.bars && <div className="mp-gbar">{g.bars.map(([p, c], i) => <i key={i} style={{ width: Math.max(0, Math.min(100, p || 0)) + '%', background: 'var(--' + c + ')' }} />)}</div>}
      {g.cells && <div className="mp-gcells">{g.cells.map(([l, v]) => <div key={l}>{l}<b>{v}</b></div>)}</div>}
      {g.note && <p className="mp-gnote">{g.note}</p>}
    </div>
  );
}

function StepGoals({ plan, set, W, w }) {
  const [edit, setEdit] = useState(null);
  const [special, setSpecial] = useState(null);
  const [naming, setNaming] = useState(null); // up_02
  const list = allGoals(plan, w);
  const saveGoal = (x) => {
    const clean = { ...x }; delete clean.isNew;
    const exists = plan.goals.some((g) => g.id === x.id);
    set({ goals: exists ? plan.goals.map((g) => (g.id === x.id ? clean : g)) : [...plan.goals, clean], order: exists ? plan.order : [...plan.order, x.id] });
    setEdit(null);
  };
  const tap = (g) => (g.custom ? setEdit(plan.goals.find((x) => x.id === g.id)) : g.special ? setSpecial(g.special) : null);
  return (
    <>
      {['must', 'love', 'nice'].map((tier) => {
        const gs = list.filter((g) => g.tier === tier);
        if (!gs.length) return null;
        return (
          <div key={tier}>
            <p className="eb mp-ebout" style={{ color: 'var(--' + tierTag[tier][0] + 'Ink)' }}>{tier === 'must' ? '💚' : tier === 'love' ? '💗' : '⭐'} {tierTag[tier][1]}</p>
            <div className="mp-card">{gs.map((g) => <GoalLine key={g.id} g={g} onClick={g.custom || g.special ? () => tap(g) : undefined} />)}</div>
          </div>
        );
      })}
      <button className="mp-more full" onClick={() => setEdit({ id: pid('g'), e: '✨', t: '', tier: 'love', amount: 0, year: '', s: '', monthly: 0, isNew: true })}>+ Add a goal</button>
      <p className="mp-hint">Tap baby, kids' education or your own goals to change them. Your cushion, moving, seeing family and retirement each have their own step!</p>
      {!plan.baby.on && <button className="mp-more" onClick={() => set({ baby: { ...plan.baby, on: true } })}>+ Put baby and parental leave back</button>}
      {!plan.resp.on && <button className="mp-more" onClick={() => set({ resp: { ...plan.resp, on: true } })}>+ Put kids' education back</button>}
      {edit && <GoalEditor g={edit} funds={W.funds} onSave={saveGoal} onClose={() => setEdit(null)}
        onDelete={() => { if (window.confirm('Remove ' + edit.t + '?')) { set({ goals: plan.goals.filter((g) => g.id !== edit.id), order: plan.order.filter((k) => k !== edit.id) }); setEdit(null); } }} />}
      {special && <SpecialEditor kind={special} plan={plan} set={set} W={W} w={w} onClose={() => setSpecial(null)} />}
      <Card eb="✏️ Names, emoji and colours" ink="lavInk">{/* up_02 */}
        {list.filter((g) => !g.custom).map((g) => <Row key={g.id} e={g.e} bg={g.bg} t={g.t} s={g.renamed ? 'Was ' + BUILT_NAMES[g.id] : null} right="›" onClick={() => setNaming(g)} />)}
        <p className="mp-hint left">Your own goals are named when you tap them above.</p>
      </Card>
      {naming && (
        <NameEditor
          g={naming}
          original={BUILT_NAMES[naming.id] || naming.t}
          onClose={() => setNaming(null)}
          onSave={(o) => { set({ goalNames: { ...(plan.goalNames || {}), [naming.id]: o } }); setNaming(null); }}
          onReset={() => { const n = { ...(plan.goalNames || {}) }; delete n[naming.id]; set({ goalNames: n }); setNaming(null); }}
        />
      )}
    </>
  );
}

/* ---------------------------------------------------------------- 11 · Moving */
function MoveMaths({ plan, w }) {
  const yr = MONS[(num(plan.move.readyMonth) || 1) - 1] + ' ' + plan.move.ready;
  return (
    <>
      <Calc rows={[
        ['Your half of the move', fmt(w.moveHalf)],
        [num(plan.move.europe && plan.move.europe.months) + ' months of living in Europe, grown to ' + yr, fmt(w.runway)],
        Object.assign(['Worked out', fmt(w.moveCalc)], { tot: true }),
        ...(num(plan.move.target) ? [Object.assign(['Your target', fmt(w.moveTotal)], { b: true })] : []),
      ]} />
      <Calc rows={[
        ['Have now', fmt(w.futureHave)],
        ['From your goal savings, in your lineup', (w.futureStart ? 'from ' + monthYear(w.futureStart) : '—')],
        ['Going in, an average month', fmt(w.futureMonthly)],
        Object.assign(['Full', w.futureFull ? 'about ' + w.futureFull : 'not yet'], { b: true }),
        Object.assign([w.moveCovered ? 'Ready by ' + yr + ' 🎉' : 'Not quite by ' + yr, w.moveCovered ? '✓' : fmt(Math.max(0, w.moveTotal - (w.fuAtMove || 0))) + ' short'], { tot: true }),
      ]} />
      <Src k="c" text="your pay savings and the emergency fund's half of your extras" />
    </>
  );
}
function EuList({ list, onChange, label }) {
  return (
    <>
      <p className="mp-flab" style={{ margin: '12px 2px 2px' }}>{label}</p>
      {list.map((c) => (
        <EditRow key={c.id} inline e={c.e} name={c.t} onName={(v) => onChange(list.map((x) => (x.id === c.id ? { ...x, t: v } : x)))} onRemove={() => onChange(list.filter((x) => x.id !== c.id))}>
          <Amt value={c.amount} onChange={(v) => onChange(list.map((x) => (x.id === c.id ? { ...x, amount: v } : x)))} small />
        </EditRow>
      ))}
      <button className="mp-more" onClick={() => onChange([...list, { id: pid('eu'), e: '🧾', t: 'Something else', amount: 0 }])}>+ Add</button>
    </>
  );
}
function StepMove({ plan, set, W, w }) {
  const MV = plan.move;
  const up = (p) => set({ move: { ...MV, ...p } });
  const EU = MV.europe || { shared: [], personal: [], months: 6 };
  const upEu = (p) => up({ europe: { ...EU, ...p } });
  const upCost = (id, p) => up({ costs: MV.costs.map((c) => (c.id === id ? { ...c, ...p } : c)) });
  const linked = w.futureFund;
  return (
    <>
      <Card>
        <Q>When?</Q>
        <div className="mp-field"><span>Ready by</span><span className="mp-pair"><MonthPick from={1} value={num(MV.readyMonth) || 1} onChange={(v) => up({ readyMonth: v })} /><Amt plain prefix="" value={MV.ready} onChange={(v) => up({ ready: Math.round(v) })} small /></span></div>
        <div className="mp-field last"><span>Most likely</span><Txt value={MV.likely} onChange={(v) => up({ likely: v })} /></div>
      </Card>
      <Card eb="🎯 Your target" ink="blushInk">
        <div className="mp-field last"><span>Future fund target<small>Worked out from below: {fmt(w.moveCalc)}. Clear it to use that.</small></span><Amt value={MV.target} onChange={(v) => up({ target: v })} small /></div>
      </Card>
      <Card eb="✈️ Your half of the move" ink="blushInk">
        {MV.costs.map((c) => (
          <EditRow key={c.id} inline e={c.e} name={c.t} onName={(v) => upCost(c.id, { t: v })} onRemove={() => { if (window.confirm('Remove ' + c.t + '?')) up({ costs: MV.costs.filter((x) => x.id !== c.id) }); }}>
            <Amt value={c.amount} onChange={(v) => upCost(c.id, { amount: v })} small />
          </EditRow>
        ))}
        <button className="mp-more" onClick={() => up({ costs: [...MV.costs, { id: pid('c'), e: '✨', t: 'Something else', amount: 0 }] })}>+ Add a cost</button>
      </Card>
      <Card eb="🏙️ Living in Europe, a month" ink="skyInk">
        <EuList label="Shared, the full amount (you pay half)" list={EU.shared || []} onChange={(v) => upEu({ shared: v })} />
        <EuList label="Just yours" list={EU.personal || []} onChange={(v) => upEu({ personal: v })} />
        <Calc rows={[['Shared ' + fmt(w.euShared) + ', your half', fmt(w.euShared / 2)], ['Just yours', fmt(w.euPersonal)], Object.assign(['Your month in Europe', fmt(w.euMonth)], { tot: true })]} />
        <div className="mp-field last" style={{ marginTop: 12 }}><span>Months to cover after you move</span><Amt plain prefix="" value={EU.months} onChange={(v) => upEu({ months: v })} suffix="months" small /></div>
      </Card>
      <Card eb="🌱 Your future fund" ink="mintInk">
        <p className="mp-flab" style={{ margin: '0 2px 8px' }}>Saved in</p>
        <Chips options={['Typed in', ...W.funds.filter((f) => f !== w.efFund).map((f) => f.emoji + ' ' + f.name)]} value={linked && MV.fundId !== 'none' ? linked.emoji + ' ' + linked.name : 'Typed in'}
          onChange={(v) => { const f = W.funds.find((ff) => ff.emoji + ' ' + ff.name === v); up({ fundId: f ? f.id : 'none' }); }} />
        {!linked && <p className="mp-hint left">Make a Future Fund in Worthly, then pick it here and its balance fills in on its own.</p>}
        <div className="mp-field" style={{ marginTop: 10 }}><span>Have now</span>{linked ? <span className="mp-fixed">{fmt(linked.balance)}</span> : <Amt value={MV.saved} onChange={(v) => up({ saved: v })} small />}</div>
        <MoveMaths plan={plan} w={w} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 12 · Seeing family */
function TravelYear({ plan, set, w, hideHero = false, readOnly = false }) {
  const TR = plan.travel;
  const used = TR.used || [];
  const upU = (id, p) => set({ travel: { ...TR, used: used.map((u) => (u.id === id ? { ...u, ...p } : u)) } });
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      {!hideHero && <Card tint="lav" eb={'✈️ ' + w.y} ink="lavInk">
        <div className="mp-bigrow"><span className="mp-big">{fmt(w.travelHave)}</span><span>of {fmt(w.travelNowYear)}</span></div>
        <div className="mp-gbar big" style={{ background: 'var(--glass)', marginLeft: 0 }}><i style={{ width: pct(w.travelUsed, w.travelNowYear) + '%', background: 'var(--lavSoft)' }} /><i style={{ width: pct(w.travelFundBal, w.travelNowYear) + '%', background: 'var(--lavInk)' }} /></div>
        <div className="mp-leg"><span><i style={{ background: 'var(--lavSoft)' }} />Used {fmt(w.travelUsed)}</span><span><i style={{ background: 'var(--lavInk)' }} />In the fund {fmt(w.travelFundBal)}</span>{w.travelToGo > 0 && <span><i style={{ background: '#fff' }} />To go {fmt(w.travelToGo)}</span>}</div>
      </Card>}
      <Card eb="🧾 Used this year" ink="ink2">
        {w.travelUsedList.filter((u) => u.from === 'w').map((u) => <Row key={u.id} t={u.t} s={'From ' + (w.travelFund ? w.travelFund.name : 'your travel fund') + (u.date ? ' · ' + dateLabel(u.date) : '')} right={fmt(u.amount)} />)}
        {readOnly && used.filter((u) => String(u.date || '').slice(0, 4) === String(w.y)).map((u) => <Row key={u.id} t={u.t} s={dateLabel(u.date)} right={fmt(u.amount)} />)}
        {!readOnly && used.filter((u) => String(u.date || '').slice(0, 4) === String(w.y)).map((u) => (
          <EditRow key={u.id} name={u.t} onName={(v) => upU(u.id, { t: v })} onRemove={() => set({ travel: { ...TR, used: used.filter((x) => x.id !== u.id) } })} src={<Src k="y" text={dateLabel(u.date)} />}>
            <Amt value={u.amount} onChange={(v) => upU(u.id, { amount: v })} small />
          </EditRow>
        ))}
        {!w.travelUsedList.length && <p className="mp-p first">Nothing used yet this year.</p>}
        {!readOnly && <button className="mp-more" onClick={() => set({ travel: { ...TR, used: [...used, { id: pid('u'), t: 'Something I paid for', amount: 0, date: today }] } })}>+ Add something you've paid for</button>}
        <p className="mp-hint left">{readOnly ? 'Money taken out of your travel fund in Worthly shows up here on its own. Add things you paid for some other way on the Seeing family step.' : 'Money taken out of your travel fund in Worthly shows up here on its own, so only add things you paid for some other way. It starts fresh every January!'}</p>
      </Card>
    </>
  );
}

function StepTravel({ plan, set, W, w }) {
  const TR = plan.travel;
  const up = (p) => set({ travel: { ...TR, ...p } });
  const upList = (key, id, p) => up({ [key]: TR[key].map((x) => (x.id === id ? { ...x, ...p } : x)) });
  const delList = (key, x) => { if (window.confirm('Remove ' + x.t + '?')) up({ [key]: TR[key].filter((y) => y.id !== x.id) }); };
  const yrs = [w.y, w.laterFrom, w.laterFrom + 10];
  return (
    <>
      <TravelYear plan={plan} set={set} w={w} />
      <Card eb="🔗 Saved in" ink="mintInk">{/* up_02 */}
        <FundPicker W={W} w={w} noneValue="none" value={TR.fundId || (w.travelFund && w.travelFund.id)} exclude={[w.efFund]} onPick={(v) => up({ fundId: v })} />
        <div className="mp-field last" style={{ marginTop: 12 }}><span>Goal this year<small>Worked out from your trips: {fmt(w.travelNowYear)}. Clear it to use that.</small></span><Amt value={TR.target || null} auto={Math.round(w.travelNowYear)} onChange={(v) => up({ target: v })} small /></div>
      </Card>
      <p className="mp-intro">This isn't a goal you save for once, it's a cost that stays with you every year. Planners handle it in two ways: it goes into your yearly budget now, and it's built into how much you'll need in retirement.</p>
      <div className="mp-stage" style={{ background: 'var(--butter)' }}>
        <p className="eb" style={{ color: 'var(--butterInk)' }}>Now · in Canada</p>
        {TR.now.map((x) => (
          <EditRow key={x.id} glass inline name={x.t} onName={(v) => upList('now', x.id, { t: v })} onRemove={() => delList('now', x)}>
            <Amt value={x.amount} onChange={(v) => upList('now', x.id, { amount: v })} small />
          </EditRow>
        ))}
        <button className="mp-more" onClick={() => up({ now: [...TR.now, { id: pid('t'), t: 'Another trip', amount: 0 }] })}>+ Add a trip</button>
        <Calc rows={[Object.assign(['A year', fmt(w.travelNowYear)], { tot: true }), Object.assign(['Into your travel fund', fmt(w.travelNowYear / 12) + ' a month'], { b: true })]} />
      </div>
      <div className="mp-stage" style={{ background: 'var(--lav)' }}>
        <p className="eb" style={{ color: 'var(--lavInk)' }}>From <input className="mp-yr" inputMode="numeric" value={TR.laterFrom} onChange={(e) => up({ laterFrom: num(e.target.value) })} /> · in Europe</p>
        {TR.later.map((x) => (
          <EditRow key={x.id} glass name={x.t} onName={(v) => upList('later', x.id, { t: v })} onRemove={() => delList('later', x)}>
            <Amt plain prefix="" value={x.trips || 1} onChange={(v) => upList('later', x.id, { trips: v })} suffix="× a year" small />
            <Amt value={x.amount} onChange={(v) => upList('later', x.id, { amount: v })} suffix="each" small />
          </EditRow>
        ))}
        <button className="mp-more" onClick={() => up({ later: [...TR.later, { id: pid('t'), t: 'Another trip', trips: 1, amount: 0 }] })}>+ Add a trip</button>
        <Calc rows={[Object.assign(["A year, in today's dollars", fmt(w.travelLaterYear)], { tot: true }), Object.assign(['Into your travel fund', fmt(w.travelLaterYear / 12) + ' a month'], { b: true })]} />
      </div>
      <div className="mp-stage" style={{ background: 'var(--blush)' }}>
        <p className="eb" style={{ color: 'var(--blushInk)' }}>Once kids come along</p>
        <div className="mp-row glass"><span className="mp-rt"><b>Extra ticket per trip</b><small>Add this when the time comes</small></span><span className="mp-rr"><Amt value={TR.kidTicket} onChange={(v) => up({ kidTicket: v })} small /></span></div>
      </div>
      <Card eb="📈 With prices going up" ink="skyInk">
        <p className="mp-p first">Tickets get pricier over time, so your plan grows these amounts by {plan.assume.prices}% a year. That's the number your travel fund target follows.</p>
        {yrs.map((yy) => <Row key={yy} t={String(yy)} right={fmt(w.travelNominal(yy))} />)}
        <Src k="c" text={plan.assume.prices + "% a year, change it in Your guesses"} />
      </Card>
      <Card tint="mint" eb="💚 Over your lifetime" ink="mintInk">
        <div className="mp-big">{short(w.travelLifetime)}</div>
        <p className="mp-p">In today's dollars, from now until {plan.assume.until}. That's why it lives in your monthly budget and your retirement number, rather than being a pot you fill once!</p>
        <Calc rows={[
          [w.y + ' to ' + (w.laterFrom - 1) + ': ' + w.nowYears + ' years × ' + fmt(w.travelNowYear), fmt(w.nowYears * w.travelNowYear)],
          [w.laterFrom + ' to ' + w.lastYear + ': ' + w.laterYears + ' years × ' + fmt(w.travelLaterYear), fmt(w.laterYears * w.travelLaterYear)],
        ]} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 13 · What comes first */
const ORDER_NAMES = { funds: ['🫙', 'Sinking funds'], efund: ['🛟', 'Emergency & future fund'], move: ['✈️', 'Moving abroad'], baby: ['\u{1F33F}', 'Life events'], retire: ['🌻', 'Retirement'], resp: ['🎓', "Kids' education"] };
function StepOrder({ plan, set, w }) {
  const ids = plan.order.filter((k) => ORDER_NAMES[k] ? (k === 'baby' ? plan.baby.on : k === 'resp' ? plan.resp.on : true) : plan.goals.some((g) => g.id === k));
  const name = (k) => (nameOf(plan, k) && ORDER_NAMES[k] ? [nameOf(plan, k).e || ORDER_NAMES[k][0], nameOf(plan, k).t || ORDER_NAMES[k][1]] : null) || ORDER_NAMES[k] || ((g) => [g.e, g.t])(plan.goals.find((g) => g.id === k));
  const move = (i, d) => { const a = [...ids]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set({ order: [...a, ...plan.order.filter((k) => !a.includes(k))] }); };
  const [aL, bL] = periods(w.step);
  const goalNeed = (plan.baby.on ? w.babyNeedMonthly : 0) + w.goals.reduce((s, g) => s + g.need, 0);
  const goalIn = (plan.baby.on ? w.babyMonthly : 0) + w.goals.reduce((s, g) => s + g.monthly, 0);
  return (
    <>
      <Card eb="💼 From your pay, each month" ink="mintInk">
        <Calc rows={[[aL + ', left for fun', fmt(w.leftA)], [bL + ', left for fun', fmt(w.leftB)]]} />
        <p className="mp-p">{w.leftA >= 0 && w.leftB >= 0 ? 'Your pay covers your essentials and savings, with fun money left over!' : 'Your essentials and savings come to more than your pay in part of the year.'}</p>
      </Card>
      <Card eb="🎉 From your extras, each year" ink="butterInk">
        <Calc rows={[['Coming in', fmt(w.extrasIn)], ['Sinking funds', '−' + fmt(w.fundsYear)], Object.assign([w.extrasLeft >= 0 ? 'Left over, free' : 'Short', fmt(Math.abs(w.extrasLeft))], { tot: true })]} />
      </Card>
      {goalNeed > 0 && (
        <Card eb="🎯 Goals saving each month" ink="blushInk">
          <Calc rows={[['They need', fmt(goalNeed) + ' a month'], ["You're putting in", fmt(goalIn) + ' a month'], Object.assign([goalIn >= goalNeed ? 'All covered' : 'Still to find', goalIn >= goalNeed ? '✓' : fmt(goalNeed - goalIn) + ' a month'], { tot: true })]} />
          <p className="mp-hint left">Baby and your own goals. Your cushion, travel and retirement are covered above.</p>
        </Card>
      )}
      <p className="eb mp-ebout" style={{ color: 'var(--lavInk)' }}>🔢 When there's extra money, it goes to</p>
      {ids.map((k, i) => (
        <div key={k} className="mp-rank">
          <span className="n">{i + 1}</span>
          <span className="t">{name(k)[0]} {name(k)[1]}</span>
          <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">▲</button>
          <button onClick={() => move(i, 1)} disabled={i === ids.length - 1} aria-label="Move down">▼</button>
        </div>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- 14 · Investing */
function StepInvest({ plan, set }) {
  const I = plan.invest;
  const up = (p) => set({ invest: { ...I, ...p } });
  return (
    <>
      <Card eb="🥧 Your mix" ink="lavInk">
        <Txt value={I.mix} onChange={(v) => up({ mix: v })} />
        <Txt area value={I.why} onChange={(v) => up({ why: v })} placeholder="Why this mix?" />
        <Src k="y" />
      </Card>
      <Card>
        <Q>If your investments dropped 30% in a year, you'd…</Q>
        <Chips options={['Want to sell', 'Feel uneasy but wait', 'Keep going as normal', 'Buy more']} value={I.drop} onChange={(v) => up({ drop: v })} />
      </Card>
      <Card eb="🗂️ Your accounts" ink="skyInk">
        {accOf(plan).map((a, n, AL) => (
          <ListItem key={a.k} x={a} last={n === AL.length - 1} onChange={(p) => listOps(AL, (v) => set({ accList: v })).up(a.k, p)} onRemove={() => listOps(AL, (v) => set({ accList: v })).rm(a.k, a.t)}>
            <Chips options={a.opts || ACC_OPTS} value={I.accounts[a.k]} onChange={(v) => up({ accounts: { ...I.accounts, [a.k]: v } })} />
          </ListItem>
        ))}
        <AddLine placeholder="Add another account" onAdd={(t) => set({ accList: [...accOf(plan), { k: pid('a'), e: '🏦', t, s: '', opts: ACC_OPTS }] })} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 15 · Tax */
// up_02: your tax dates, starting with these two
const TAX_START = [{ id: 'td1', e: '📄', t: 'File your taxes', s: '', d: 'Apr 30' }, { id: 'td2', e: '💼', t: 'Worthly HQ year end', s: 'January to December', d: 'Dec 31' }];
const taxOf = (plan) => (Array.isArray(plan.taxDates) ? plan.taxDates : TAX_START);
function StepTax({ plan, set, w }) {
  const T = plan.tax;
  const y = w.y;
  const rrspDay = (() => { const d = new Date(y + 1, 0, 1); d.setDate(d.getDate() + 59); return d; })();
  return (
    <>
      <Card eb="🗓️ Dates for this year" ink="butterInk">
        <Row e="🌳" bg="butter" t="RRSP deadline" s={'Counts toward your ' + y + ' refund · worked out each year'} right={'Mar ' + rrspDay.getDate()} />
        {taxOf(plan).map((x, n, TL) => (
          <ListItem key={x.id} x={x} last={n === TL.length - 1} onChange={(p) => listOps(TL, (v) => set({ taxDates: v })).up(x.id, p)} onRemove={() => listOps(TL, (v) => set({ taxDates: v })).rm(x.id, x.t)}>
            <div className="mp-field last"><span>Date</span><Txt value={x.d || ''} onChange={(v) => listOps(TL, (q) => set({ taxDates: q })).up(x.id, { d: v })} placeholder="Like Apr 30" /></div>
          </ListItem>
        ))}
        <AddLine placeholder="Add a tax date" onAdd={(t) => set({ taxDates: [...taxOf(plan), { id: pid('td'), e: '📌', t, s: '', d: '' }] })} />
      </Card>
      <Card>
        <Q>Where does your tax refund go?</Q>
        <Chips options={['Sinking funds', 'Back into investing', 'Split it']} value={T.refund} onChange={(v) => set({ tax: { ...T, refund: v } })} />
      </Card>
      <Card>
        <Q sub="Your plan brings this back when Worthly starts making money, since how you pay yourself changes your tax and your RRSP room.">Are you paying yourself from Worthly HQ yet?</Q>
        <Chips options={['Not yet', 'Salary', 'Dividends']} value={T.payself} onChange={(v) => set({ tax: { ...T, payself: v } })} />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- 16 · Leaving Canada */
function StepLeaving({ plan, set }) {
  const L = plan.leavingList;
  return (
    <>
      <Card>{/* up_02 */}
        {leavingOf(plan).map((x, n, LV) => <ListItem key={x.id} x={x} last={n === LV.length - 1} onChange={(p) => listOps(LV, (v) => set({ leavingNotes: v })).up(x.id, p)} onRemove={() => listOps(LV, (v) => set({ leavingNotes: v })).rm(x.id, x.t)} />)}
        <AddLine placeholder="Add an account or a note" onAdd={(t) => set({ leavingNotes: [...leavingOf(plan), { id: pid('lv'), e: '📌', t, s: '' }] })} />
      </Card>
      <Card eb="✈️ A year before you go" ink="lavInk">
        {L.map((x) => <Check key={x.id} on={x.done} t={x.t} onClick={() => set({ leavingList: L.map((y) => (y.id === x.id ? { ...y, done: !y.done } : y)) })} onRemove={() => set({ leavingList: L.filter((y) => y.id !== x.id) })} />)}
        <AddLine placeholder="Add something" onAdd={(t) => set({ leavingList: [...L, { id: pid('l'), t, done: false }] })} />
      </Card>
      <Card tint="butter"><p className="mp-p first">This list shows up on your plan a year before your move, so it's ready when you need it! 🌍</p></Card>
    </>
  );
}

/* ---------------------------------------------------------------- 17 · Retirement */
function StepRetire({ plan, set, w }) {
  const R = plan.retire;
  const up = (p) => set({ retire: { ...R, ...p } });
  return (
    <>
      <Card>
        <Q>Around what age?</Q>
        <div className="mp-field last"><span>Retire at</span><Amt plain prefix="" value={R.age} onChange={(v) => up({ age: v })} suffix="years old" small /></div>{/* up_01 */}
      </Card>
      <Card eb="🎯 Your goal" ink="mintInk">{/* up_02 */}
        <div className="mp-field last"><span>Goal<small>Worked out: {short(w.retNeedCalc)}. Clear it to use that.</small></span><Amt value={R.target || null} auto={Math.round(w.retNeedCalc)} onChange={(v) => up({ target: v })} small /></div>
      </Card>
      <Card>
        <Q>What should it feel like?</Q>
        <Chips options={['Simple', 'Comfortable, like now', 'Lots of travel']} value={R.feel} onChange={(v) => up({ feel: v })} />
      </Card>
      <Card eb="🛍️ A year in retirement, in today's dollars" ink="peachInk">
        <Row e="🏡" bg="peach" t="Everyday life" s="Rent, food, fun and everything else" right={<Amt value={R.everyday} onChange={(v) => up({ everyday: v })} small />}><Src k="y" /></Row>
        <Row e="🇳🇬" bg="butter" t="Seeing family" s="From your Seeing family step" right={fmt(w.travelLaterYear)}><Src k="c" /></Row>
        <Row e="🍁" bg="sky" t="Government pensions" s="Canada and Europe together, a year" right={<Amt value={R.gov} onChange={(v) => up({ gov: v })} small />}><Src k="y" text="a guess for now" /></Row>
      </Card>
      <Card eb="🌻 How your number is worked out" ink="mintInk">
        <Calc rows={[
          ['Everyday life', fmt(num(R.everyday))],
          ['Seeing family', fmt(w.travelLaterYear)],
          Object.assign(['A year in retirement', fmt(w.spendRet)], { tot: true }),
          ['Less government pensions', '−' + fmt(num(R.gov))],
          Object.assign(['From your savings a year', fmt(w.fromSavings)], { tot: true }),
          Object.assign(['Lasting ' + w.yearsIn + ' years, until ' + plan.assume.until, short(w.retNeed) + ' needed'], { b: true }),
        ]} />
        <p className="mp-hint left">Your pot keeps growing while you spend from it, about {plan.assume.retGrowth}% a year with prices going up {plan.assume.prices}%. Your first year uses {w.firstYearPct.toFixed(1)}% of it.</p>
        <Src k="c" text="today's dollars" />
      </Card>
      <Card eb="💰 Where it comes from" ink="skyInk">
        <Row e="🌳" bg="lav" t="RRSP and investments" s={fmt(w.invested) + ' today · ' + fmt(w.rrspAll) + ' a year going in, from pay and extras'} />
        <Row e="🏢" bg="mint" t="Work pension" s={fmt(w.pensionYear) + ' a year going in'} />
        <Row e="🍁" bg="sky" t="Canada Pension Plan" s="Smaller, since you'll leave. Canada has a pension agreement with France, so years in both can count." />
        <Row e="🇪🇺" bg="blush" t="State pension in Europe" />
      </Card>
      <Card tint={w.retOnTrack ? 'mint' : 'butter'} eb={w.retOnTrack ? '💚 Looking good' : '🌼 Worth a look'} ink={w.retOnTrack ? 'mintInk' : 'butterInk'}>
        <Calc rows={[['Needed by ' + w.retAge, short(w.retNeed)], Object.assign(['Heading for by ' + w.retAge, 'about ' + short(w.retHeading)], { b: true }), ...(w.moveHit ? [['After using investments for the move', 'included']] : [])]} />
        <p className="mp-p">{w.retOnTrack ? (w.retCoveredAge ? "At your pace, you'd be covered by about " + w.retCoveredAge + '!' : 'At your pace, you get there!') : 'To get there, about ' + fmt(w.retNeedYear / 12) + ' a month would need to go in, all together.'}</p>
      </Card>
    </>
  );
}

const STEP_COMPONENTS = { life: StepLife, ahead: StepAhead, values: StepValues, networth: StepNetWorth, money: StepMoney, extras: StepExtras, debt: StepDebt, cushion: StepCushion, insurance: StepInsurance, paper: StepPaper, goals: StepGoals, move: StepMove, travel: StepTravel, order: StepOrder, invest: StepInvest, tax: StepTax, leaving: StepLeaving, retire: StepRetire, calendar: StepCalendar, guesses: StepGuesses };
const STEP_SUBS = {
  life: 'A quick picture of where things stand, so the plan fits your life!',
  ahead: 'The big moments your money needs to be ready for.',
  values: 'Your plan works best when it lines up with these. They come from your money diary!',
  networth: 'Pulled from Worthly. Check it all looks right, and add anything Worthly doesn\'t know about!',
  money: 'Your pay covers each month: essentials, savings and fun. Tap any amount to change it!',
  extras: "Bonuses, your tax refund and third paycheques. They fill your sinking funds, and anything left over is free.",
  debt: 'This step skips itself whenever you have nothing to pay off.',
  cushion: "The money that keeps surprises small and means you're never forced to sell investments at a bad time!",
  insurance: "Most of yours probably comes through work! It's worth knowing what you have, since it changes if you switch jobs or move.",
  paper: "Tick these off once and they're done for years! No rush, they'll sit in your plan until you get to them.",
  goals: 'Everything you want to save for. Tap any goal to change it!',
  move: 'Your half of the move plus a few months of living in Europe. Your future fund pays for it.',
  travel: 'Flying home to see family, now and for life.',
  order: 'A quick check that your pay and your extras both cover what they need to.',
  invest: 'Your mix, how you feel about ups and downs, and what each account is for!',
  tax: 'Small choices here add up to a lot over the years!',
  leaving: "What happens to each account when you leave Canada, so nothing's a surprise later!",
  retire: "Not about stopping work early, just knowing you're looked after later!",
  calendar: 'The dates in your money year and the reminders in your next steps. Add anything you want to remember, and change or remove any of them!',
  guesses: 'Every planner uses a few guesses about the future. These are yours, and every number that grows over time uses them.',
};

/* ---------------------------------------------------------------- the plan page */
// mp_01: rings, arcs, waves and the future fund chart
function Ring({ pct: p = 0, ink, size = 96, stroke = 10, track = 'var(--card)' }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, v = Math.max(0, Math.min(100, p || 0));
  return (
    <svg className="mp-ringsvg" width={size} height={size} viewBox={'0 0 ' + size + ' ' + size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
      {v > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={'var(--' + ink + ')'} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={Math.max(1, c * v / 100) + ' ' + c} transform={'rotate(-90 ' + size / 2 + ' ' + size / 2 + ')'} />}
    </svg>
  );
}
function Arc({ pct: p = 0, ink }) {
  const L = 69.1, v = Math.max(0, Math.min(100, p || 0));
  return (
    <svg className="mp-arcsvg" viewBox="0 0 52 30">
      <path d="M4 28 A22 22 0 0 1 48 28" fill="none" stroke="var(--card)" strokeWidth="6" strokeLinecap="round" />
      {v > 0 && <path d="M4 28 A22 22 0 0 1 48 28" fill="none" stroke={'var(--' + ink + ')'} strokeWidth="6" strokeLinecap="round" strokeDasharray={Math.max(1, L * v / 100) + ' ' + L} />}
    </svg>
  );
}
const WAVES = ['M0 18 C20 8 35 22 55 12 S85 4 100 10 L100 26 L0 26Z', 'M0 16 C25 22 40 8 60 14 S90 20 100 12 L100 26 L0 26Z', 'M0 12 C20 20 45 6 65 16 S90 10 100 18 L100 26 L0 26Z'];
const Wave = ({ n = 0 }) => <svg className="mp-wave" viewBox="0 0 100 26" preserveAspectRatio="none"><path d={WAVES[n % 3]} /></svg>;
function FutureChart({ w }) {
  const S = w.fuSeries || [];
  if (S.length < 2) return null;
  const n = S[S.length - 1].i, W2 = 320, H = 150, top = 26;
  const maxV = Math.max(w.moveTotal * 1.05, S[S.length - 1].v);
  const x = (i) => (i / n) * W2, yv = (v) => H - 2 - (v / maxV) * (H - top - 4);
  const pts = S.filter((pt, k) => k % 2 === 0 || k === S.length - 1).map((pt) => x(pt.i).toFixed(1) + ' ' + yv(pt.v).toFixed(1));
  const line = 'M' + pts.join(' L');
  const ty = yv(w.moveTotal);
  const full = S.find((pt) => pt.v >= w.moveTotal);
  const d0 = new Date(), yr = (i) => new Date(d0.getFullYear(), d0.getMonth() + i, 1).getFullYear();
  const ticks = [0, 1, 2, 3].map((k) => yr(Math.round(n * k / 3)));
  return (
    <div className="mp-chart">
      <svg viewBox={'0 0 ' + W2 + ' ' + H} preserveAspectRatio="none">
        <line x1="0" y1={ty} x2={W2} y2={ty} stroke="var(--blushInk)" strokeWidth="1.5" strokeDasharray="4 4" opacity=".6" />
        <path d={line + ' L' + W2 + ' ' + H + ' L0 ' + H + 'Z'} fill="var(--blush)" />
        <path d={line} fill="none" stroke="var(--blushInk)" strokeWidth="2.5" />
        {full && <circle cx={x(full.i)} cy={yv(full.v)} r="5" fill="var(--blushInk)" />}
      </svg>
      <div className="mp-chartlab" style={{ top: Math.max(0, (ty / H) * 150 - 18) }}>{fmt(w.moveTotal)} target</div>
      <div className="mp-axis">{ticks.map((t, k) => <span key={k}>{t}</span>)}</div>
    </div>
  );
}
const goalPct = (g) => (g.bars ? Math.min(100, g.bars.reduce((s, b) => s + (b[0] || 0), 0)) : 0);
function FundPicker({ W, w, value, onPick, exclude = [], noneValue = null /* plan_34 */ }) {
  const funds = W.funds.filter((f) => !exclude.includes(f));
  const cur = value ? funds.find((f) => f.id === String(value)) : null;
  return <Chips options={['Typed in', ...funds.map((f) => f.emoji + ' ' + f.name)]} value={cur ? cur.emoji + ' ' + cur.name : 'Typed in'} onChange={(v) => { const f = funds.find((ff) => ff.emoji + ' ' + ff.name === v); onPick(f ? f.id : noneValue); }} />;
}

// mp_02: a growth chart, paycheque bars and an extras-through-the-year chart
function GrowChart({ pts, ink, fill, marks = [], money = true }) {
  if (!pts || pts.length < 2) return null;
  const W2 = 320, H = 160, top = 30, n = pts.length - 1;
  const maxV = Math.max(...pts.map((p) => p.v)) * 1.08, minV = Math.min(0, ...pts.map((p) => p.v));
  const x = (i) => (i / n) * W2, yv = (v) => H - 2 - ((v - minV) / (maxV - minV || 1)) * (H - top - 4);
  const line = 'M' + pts.map((p, i) => x(i).toFixed(1) + ' ' + yv(p.v).toFixed(1)).join(' L');
  const ticks = [0, Math.round(n / 3), Math.round(2 * n / 3), n].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <div className="mp-chart">
      <svg viewBox={'0 0 ' + W2 + ' ' + H} preserveAspectRatio="none">
        <path d={line + ' L' + W2 + ' ' + H + ' L0 ' + H + 'Z'} fill={'var(--' + fill + ')'} />
        <path d={line} fill="none" stroke={'var(--' + ink + ')'} strokeWidth="2.5" />
      </svg>
      {marks.map((m) => (
        <span key={m.i} className="mp-cmark" style={{ left: (m.i / n * 100) + '%', top: (yv(pts[m.i].v) / H * 160) + 'px', '--k': 'var(--' + ink + ')' }}>
          <i /><em className={m.i > n * 0.7 ? 'r' : m.i < n * 0.15 ? 'l' : ''}>{m.t}<b>{money ? short(pts[m.i].v) : pts[m.i].v}</b></em>
        </span>
      ))}
      <div className="mp-axis">{ticks.map((t) => <span key={t}>{pts[t].label}</span>)}</div>
    </div>
  );
}
function SplitBar({ parts }) {
  const tot = parts.reduce((s, p) => s + Math.max(0, p.v), 0) || 1;
  return <div className="mp-split2">{parts.filter((p) => p.v > 0).map((p) => <i key={p.t} style={{ width: (p.v / tot * 100) + '%', background: 'var(--' + p.c + ')' }} />)}</div>;
}
function Legend({ parts, total }) {
  const tot = total || parts.reduce((s, p) => s + Math.max(0, p.v), 0) || 1;
  return (
    <div className="mp-legend">
      {parts.map((p) => <div key={p.t}><i style={{ background: 'var(--' + p.c + ')' }} /><span>{p.t}</span><small>{Math.round(Math.max(0, p.v) / tot * 100)}%</small><b>{p.neg ? '−' : ''}{fmt(Math.abs(p.v))}</b></div>)}
    </div>
  );
}
function ExtrasChart({ w }) {
  const by = Array(12).fill(0);
  (w.extras || []).forEach((x) => { const wh = x.when && x.when.length ? x.when : []; wh.forEach((m) => { by[m - 1] += num(x.amount) / wh.length; }); });
  const max = Math.max(...by, 1);
  return (
    <div className="mp-cols">
      {by.map((v, i) => (
        <div key={i} className={v ? 'on' : ''}>
          <span><i style={{ height: Math.max(v ? 10 : 4, v / max * 100) + '%' }} /></span>
          <em>{MONS[i].slice(0, 1)}</em>
        </div>
      ))}
    </div>
  );
}

// ln_19: a clearer money plan. Your path at the top with your next step, key numbers, goals with photos, next steps and your pay.
const HDR = [
  { n: 'white', bg: 'var(--card)', acc: '#9B7BE8', trk: 'var(--lav)', glow: 'rgba(155,123,232,.18)', ink: 'var(--lavInk)', box: 'var(--lav)', badge: 'var(--lav)' },
  { n: 'lavender', bg: 'var(--lav)', acc: '#8E6FE0', trk: 'var(--glass)', glow: 'var(--glass)', ink: 'var(--lavInk)', box: 'var(--glass)', badge: 'var(--glass)' },
  { n: 'mint', bg: 'var(--mint)', acc: '#4FAE7B', trk: 'var(--glass)', glow: 'var(--glass)', ink: 'var(--mintInk)', box: 'var(--glass)', badge: 'var(--glass)' },
  { n: 'sky', bg: 'var(--sky)', acc: '#5B8FD8', trk: 'var(--glass)', glow: 'var(--glass)', ink: 'var(--skyInk)', box: 'var(--glass)', badge: 'var(--glass)' },
];
const MILE = { efund: 'Emergency fund', baby: 'Life events', move: 'Moving fund', retire: 'Retire well' };
const isTodoDone = (plan, t) => (t.paper ? plan.paper[t.paper] : t.k === 'pension-mix' ? plan.invest.pensionMix === 'done' : plan.done[t.k]);
const tickTodo = (plan, set, t) => (t.paper ? set({ paper: { ...plan.paper, [t.paper]: !plan.paper[t.paper] } }) : t.k === 'pension-mix' ? set({ invest: { ...plan.invest, pensionMix: plan.invest.pensionMix === 'done' ? '' : 'done' } }) : set({ done: { ...plan.done, [t.k]: !plan.done[t.k] } }));
// your own order (plan.todoOrder); anything new goes after, in its usual place
function orderedTodos(plan, w) {
  const list = todos(plan, new Date(), w);
  const ord = plan.todoOrder || [];
  const at = (t, i) => { const j = ord.indexOf(t.k); return j < 0 ? 1000 + i : j; };
  return list.map((t, i) => ({ t, o: at(t, i) })).sort((a, b) => a.o - b.o).map((x) => x.t);
}
// how much you have and the target, for every kind of goal
function goalAmt(g, w, plan) {
  if (g.id === 'efund') return { have: w.efBal, of: w.efTarget, when: w.efFull === 'Full!' ? 'Full!' : w.efFull ? 'Full about ' + w.efFull : '' };
  if (g.id === 'travel') return { have: w.travelHave, of: w.travelNowYear, when: 'This year' };
  if (g.id === 'move') return { have: w.futureHave, of: w.moveTotal, when: w.futureFull ? 'Full about ' + w.futureFull : '' };
  if (g.id === 'baby') return { have: w.babyHave, of: w.babyGap, when: 'By ' + byLabel(plan.baby.month, plan.baby.year) };
  if (g.id === 'retire') return { have: w.invested, of: w.retNeed, when: 'By ' + w.retAge };
  if (g.id === 'resp') return { have: 0, of: null, when: 'Starts when baby arrives' };
  return { have: g.saved || 0, of: num(g.amount) ? g.then : null, when: g.year ? 'By ' + byLabel(g.month, g.year) : '' };
}
// ln_21: each goal shows its emoji, no photos
function GoalPic({ g }) {
  return <span className="ln19-gpic" style={{ background: 'linear-gradient(135deg, var(--' + g.bg + 'Mid), var(--' + g.bg + '))' }}><span className="e">{g.e}</span></span>;
}
function GoalRow({ g, w, plan, onClick, tools }) {
  const a = goalAmt(g, w, plan);
  const p = Math.round(goalPct(g));
  const inner = (
    <>
      <GoalPic g={g} />
      <span className="tx">
        {/* Worthly trial: each goal shows whether it's on track or behind */}
        <span className="ln19-gt"><b>{g.t}</b>{!tools && <Pill s={g.status} />}</span>
        <span className="am"><span><strong>{fmt(a.have)}</strong>{a.of ? ' / ' + short(a.of) : ''}</span>{a.of ? <strong>{p}%</strong> : null}</span>
        {a.of ? <span className="bar"><i style={{ width: Math.max(2, Math.min(100, p)) + '%', background: 'var(--' + g.bg + 'Mid)' }} /></span> : null}
        {a.when && <span className="tg">{a.when}</span>}
      </span>
      {tools}
    </>
  );
  return tools
    ? <div className="ln19-goal">{inner}</div>
    : <button className="ln19-goal" onClick={onClick}>{inner}</button>;
}

function PlanHome({ plan, W, w, go, set }) {
  const { data, setData } = useStore();
  const goals = allGoals(plan, w);
  const must = goals.filter((g) => g.tier === 'must');
  const good = (s) => s === 'on' || s === 'done' || s === 'covered';
  const goodN = must.filter((g) => good(g.status)).length;
  const due = checkupDue(plan);
  const nYear = Math.max(1, (num(plan.move.ready) || w.y + 5) - w.y);
  const insOpen = insOf(plan).filter((i) => plan.insurance[i.k] === 'Not sure').length;
  const paperOpen = paperOf(plan).filter((p) => !plan.paper[p.k]).length;
  const open = orderedTodos(plan, w).filter((t) => !isTodoDone(plan, t));
  // plan_21: goals that filled up in the last week
  const justFull = Object.entries(plan.celebrated || {})
    .filter(([id, d]) => d !== 'before' && Date.now() - new Date(d).getTime() < 7 * 864e5 && (plan.banHide || {})[id] !== d) // plan_40: unless you closed it
    .map(([id]) => (w.queue || []).find((q) => q.id === id)).filter((q) => q && q.st === 'done');
  const fillingNow = (w.queue || []).find((q) => !q.needs && q.st !== 'done');
  // the path: plan made, then your big funds in order
  const lineup = [...(w.queue || []).map((b) => b.id).filter((id) => id !== 'retire'), 'retire']; // ln_33: in your lineup's order, retirement last
  const steps = [{ k: 'plan', e: '📝', t: 'Plan made', done: !!plan.finished }]
    .concat(lineup.map((id) => goals.find((g) => g.id === id)).filter(Boolean)
      .map((g) => ({ k: g.id, e: g.e, t: (g.renamed ? g.t : MILE[g.id]) || g.t, done: g.status === 'done' || goalPct(g) >= 100 })));
  const curI = steps.findIndex((s) => !s.done);
  const cur = curI >= 0 ? steps[curI] : null;
  const doneN = steps.filter((s) => s.done).length;
  // the card colour you swiped to last
  const hi = Math.max(0, Math.min(HDR.length - 1, Number(data.planHdr) || 0));
  const H = HDR[hi];
  const sw = useRef(null);
  const swipe = (dir) => setData((d) => ({ ...d, planHdr: (hi + dir + HDR.length) % HDR.length }));
  // your pay this part of the year
  const m = new Date().getMonth() + 1;
  const later = m >= w.step;
  const M = plan.money;
  const pay = later ? w.payB : w.payA;
  const left = later ? w.leftB : w.leftA;
  const parts = [
    { t: 'Essentials', v: w.essMonth, c: 'blushMid' },
    { t: 'RRSP', v: num(later ? M.rrspB : M.rrspA), c: 'lavMid' },
    { t: 'Goal savings', v: num(later ? M.efB : M.efA), c: 'skyMid' },
    ...(num(M.pensionYou) ? [{ t: 'Your pension', v: num(M.pensionYou), c: 'butterMid' }] : []),
    { t: 'Left for fun', v: left, c: 'mintMid' },
  ].filter((p) => p.v > 0);
  const Key = ({ bg, ink, e, t, v, s, to }) => (
    <button className="ln19-key" style={{ background: 'var(--' + bg + ')' }} onClick={() => go(to)}>
      <i>{e}</i><small>{t}</small><b>{v}</b><em style={{ color: 'var(--' + ink + ')' }}>{s}</em>
    </button>
  );
  return (
    <>
      <div className="ln19-hd">
        <div><p className="ph">your money plan</p><p className="phs">Next check-up {nextCheckup(plan)}</p></div>
        <button className="ln19-cal" aria-label="Check-up and changes" onClick={() => go({ area: 'keep' })}>🗓️</button>
      </div>
      {justFull.map((q) => (
        <div key={q.id} className="ln19-card mp-fullban"><span className="e">{q.e}</span><span className="tx"><b>Your {q.t.toLowerCase()} is full! 🎉</b><small>{fillingNow ? (fillingNow.keeps ? fillingNow.t + ' gets your goal savings now' : fillingNow.t + ' is filling now') : 'Every goal in your lineup is full'}</small></span><button type="button" className="mp-fullx" aria-label="Close" onClick={() => set({ banHide: { ...(plan.banHide || {}), [q.id]: plan.celebrated[q.id] } })}>✕</button></div>
      ))}
      {(() => { /* tax_01: last year's snapshot, in January */
        const Y = new Date().getFullYear();
        const r0 = (plan.years || {})[Y - 1];
        if (new Date().getMonth() > 0 || !r0 || !r0.end || (plan.yearSeen || {})[Y - 1]) return null;
        return <button className="ln19-card mp-fullban" onClick={() => go({ page: 'year', y: Y - 1 })}><span className="e">📸</span><span className="tx"><b>Your {Y - 1} plan is saved! 🎉</b><small>See how your year went</small></span></button>;
      })()}
      {due && (
        <div className="ln19-card">
          <h3 className="ln19-h">📆 Check-up time!</h3>
          <p className="ln19-p">It's been a year since your last look. About 15 minutes, and your plan is fresh again!</p>
          <button className="ln19-btn wide" onClick={() => go({ page: 'checkup' })}>Start my check-up</button>
        </div>
      )}

      <div className="ln19-card ln19-path" style={{ background: H.bg, '--acc': H.acc, '--trk': H.trk, '--glow': H.glow, '--hink': H.ink }}
        onTouchStart={(e) => { sw.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { if (sw.current == null) return; const dx = e.changedTouches[0].clientX - sw.current; sw.current = null; if (Math.abs(dx) > 50) swipe(dx < 0 ? 1 : -1); }}>
        <div className="ln19-st">
          <div>
            <h2>{goodN >= must.length - 1 ? "You're on track ✨" : "Let's catch up 💪🏾"}</h2>
            <p>{goodN} of your {must.length} must-haves {goodN === 1 ? 'is' : 'are'} on track.{cur ? " Right now you're working on " + (cur.k === 'retire' ? 'retiring well' : 'your ' + cur.t.toLowerCase()) + '.' : ' Every step is done! 🎉'}</p>
          </div>
          <span className="badge" style={{ background: H.badge }}>{cur ? cur.e : '🎉'}</span>
        </div>
        <div className="path">
          <div className="ln"><i style={{ width: steps.length > 1 ? Math.min(100, Math.max(0, doneN - 0.5) / (steps.length - 1) * 100) + '%' : 0 }} /></div>
          {steps.map((s, i) => (
            <div key={s.k} className={'s' + (s.done ? ' is-done' : i === curI ? ' is-now' : '')}>
              <span className="c">{s.done ? <svg viewBox="0 0 14 14"><path d="M3 7.5l2.6 2.5L11 4.5" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg> : <span>{s.e}</span>}</span>
              <small>{s.t}</small>
            </div>
          ))}
        </div>
        <div className="next" style={{ background: H.box }}>
          {open[0] ? (
            <>
              <span>Your next step<b>{open[0].t}</b></span>
              <button className="ln19-btn" onClick={() => tickTodo(plan, set, open[0])}>Done</button>
            </>
          ) : <span>Your next step<b>Nothing to do right now! 🎉</b></span>}
        </div>
      </div>
      <div className="ln19-dots">{HDR.map((x, i) => <button key={x.n} aria-label={x.n} className={i === hi ? 'on' : ''} onClick={() => setData((d) => ({ ...d, planHdr: i }))} />)}</div>

      <div className="ln19-card">
        <div className="ln19-ch"><h3>Key numbers</h3><button onClick={() => go({ area: 'numbers' })}>How they work ›</button></div>
        <div className="ln19-keys">
          <Key bg="mint" ink="mintInk" e="💚" t="Net worth" v={fmt(w.netWorth)} s={'↗ about ' + short(w.nwAt(nYear)) + ' by ' + (w.y + nYear)} to={{ area: 'networth' }} />
          <Key bg="lav" ink="lavInk" e="📈" t="Invested" v={short(w.invested)} s={'Retirement ' + (w.retOnTrack ? 'on track' : 'behind')} to={{ area: 'invest' }} />
          <Key bg="sky" ink="skyInk" e="🛡️" t="Safety net" v={efCover(plan, w).total.toFixed(1) + ' months'} s={efCover(plan, w).extra > 0 ? efCover(plan, w).savings.toFixed(1) + ' on savings alone' : (insOpen + paperOpen ? insOpen + paperOpen + ' to look at' : 'All looked at')} to={{ area: 'safety' }} />
          <Key bg="blush" ink="blushInk" e="💰" t="Left for fun" v={fmt(left)} s="a month" to={{ area: 'money' }} />
        </div>
      </div>

      <div className="ln19-card">
        <div className="ln19-ch"><h3>My goals</h3><button onClick={() => go({ area: 'goals' })}>See all ›</button></div>
        {(() => { const og = orderGoals(goals, plan); return og.filter((g) => g.tier === 'must').concat(og.filter((g) => g.tier !== 'must')); })().slice(0, 3).map((g) => <GoalRow key={g.id} g={g} w={w} plan={plan} onClick={() => go({ goal: g.id })} />)}
      </div>

      {open.length > 1 && (
        <div className="ln19-card">
          <div className="ln19-ch"><h3>Next steps</h3><button onClick={() => go({ area: 'steps' })}>{open.length} to do ›</button></div>
          {open.slice(1, 4).map((t) => (
            <div key={t.k} className="ln19-todo">
              <button className="ck" aria-label="Done" onClick={() => tickTodo(plan, set, t)} />
              <span>{t.t}</span>
            </div>
          ))}
        </div>
      )}

      <button className="ln19-card ln19-pay" onClick={() => go({ area: 'money' })}>
        <div className="ln19-ch"><h3>Where your pay goes</h3><span>See more ›</span></div>
        <div className="two"><span><small>Pay, after tax</small><b>{fmt(pay)}</b></span><span><small>Left for fun</small><b>{fmt(left)}</b></span></div>
        <div className="sb">{parts.map((p) => <i key={p.t} style={{ flex: p.v, background: 'var(--' + p.c + ')' }} />)}</div>
      </button>


      <div className="ln19-links">
        <button onClick={() => go({ area: 'keep' })}>🗓️ Check-up and changes</button>
        <button onClick={() => go({ area: 'numbers' })}>🧮 How numbers work</button>
      </div>
      <button className="mp-btn" onClick={() => go({ page: 'update' })}>✏️ Update your plan</button>{/* up_01 */}
    </>
  );
}

// plan_37: your notes on a goal, and editing them. (plan_37b: updated notes)
// Saved on your plan as goalNotes[goalId] = a list of blocks:
//   { t: 'lead' | 'p' | 'sm', x }  text (**two stars** make words bold)
//   { t: 'h', x }                  a heading
//   { t: 'kv', k, s, v }           a line: label, small line under it, amount
//   { t: 'table', cols: [...], rows: [{ c: [...], hi }] }
//                                  in a cell, a new line becomes the small text
//   { t: 'more' }                  everything after it waits for "Read all your notes"
const NOTE_DEFAULTS = {
  efund: [
    { t: 'lead', x: "**Target: $15,000**, that's $12,000 plus a $3,000 buffer. Separate account, cash at EQ Bank. With $15k, I'd realistically be covered for 8 to 9 months." },
    { t: 'h', x: "What it's for" },
    { t: 'p', x: 'Job loss and genuine surprises only. Car repairs, insurance, travel and gifts all have their own sinking funds.' },
    { t: 'h', x: 'How my $4,200 breaks down' },
    { t: 'kv', k: 'True essentials', s: 'Rent, bills, car loan', v: '$2,749' },
    { t: 'kv', k: 'Groceries', s: '', v: '$550' },
    { t: 'kv', k: 'Hair', s: '', v: '$250' },
    { t: 'kv', k: 'Buffer', s: '', v: '$651' },
    { t: 'p', x: 'Crisis mode is **$3,500**: drop the buffer, trim groceries, no commuting gas.' },
    { t: 'h', x: 'What EI covers' },
    { t: 'p', x: "Maximum insurable earnings for 2026 are $68,900 at 55%, so I'd get the cap of **$729 a week** rather than 55% of what I actually earn. After about 20% tax that's **$583 a week**, or $2,527 a month." },
    { t: 'p', x: 'There\'s a one-week unpaid waiting period, and benefits run 14 to 45 weeks depending on the regional unemployment rate and my hours.' },
    { t: 'h', x: 'Severance: about 9 weeks' },
    { t: 'p', x: "Two months is fair for my years at Colgate. It's allocated at my normal weekly pay, so those weeks are fully covered and I don't touch savings." },
    { t: 'h', x: "How long I'd be covered" },
    { t: 'table', cols: ['EI lasts', 'Normal\n$4,200', 'Crisis\n$3,500'], rows: [
      { c: ['0 weeks', '5.5 mo\n24 wks', '6.2 mo\n27 wks'] },
      { c: ['14 weeks', '7.4 mo\n32 wks', '8.5 mo\n37 wks'] },
      { c: ['20 weeks\nMost likely', '8.3 mo\n36 wks', '9.7 mo\n42 wks'], hi: true },
      { c: ['26 weeks\nMost likely', '9.2 mo\n40 wks', '10.6 mo\n46 wks'], hi: true },
      { c: ['36 weeks', '10.6 mo\n46 wks', '12.2 mo\n53 wks'] },
      { c: ['45 weeks', '10.6 mo\n46 wks', '13.9 mo\n60 wks'] },
    ] },
    { t: 'p', x: "**Most likely: the 20 to 26 week rows.** Full-time year-round puts me at the top of the hours scale, but Toronto's low unemployment rate pulls duration back down. So realistically **8 to 9 months**, or 10 if I cut back." },
    { t: 'more' },
    { t: 'h', x: 'Worst case' },
    { t: 'p', x: 'I quit or am dismissed for cause, so no EI and possibly no severance: **15 weeks on savings alone**.' },
    { t: 'h', x: 'Why $15,000 and not more' },
    { t: 'p', x: "Beyond about 26 weeks of EI, extra cash barely moves the total. The fund's job is the early gap, not the tail. Cash at 2.75% nets 1.56% after tax, below inflation, so I'm paying roughly $150 a year for the certainty." },
  ],
  move: [
    { t: 'lead', x: '**Target: $40,000.** Regular savings account, separate from the emergency fund. Expected full around mid-2031.' },
    { t: 'h', x: "What it's for" },
    { t: 'p', x: "Your half of the move to Europe, plus six months of living costs once you're there. This is the money that means a bad market can't force your hand. If markets are down when you want to go, you don't have to sell investments at the worst possible price just because you've committed to a date." },
    { t: 'h', x: 'How you got to $40,000' },
    { t: 'kv', k: 'Your half of a $40,000 couple move', s: 'By 2032', v: '~$22,900' },
    { t: 'kv', k: 'Six months of your half of European living', s: '', v: '~$18,100' },
    { t: 'kv', k: 'Total', s: 'Rounded to $40,000', v: '~$41,000' },
    { t: 'more' },
    { t: 'h', x: 'The couple total' },
    { t: 'p', x: '**$80,000 between you**, so $40,000 each if you save the same. That covers a mid-range move including a shipping container, plus six months of living for both of you.' },
    { t: 'kv', k: 'Lean move + 3 months', s: '', v: '$39,840' },
    { t: 'kv', k: 'Lean move + 6 months', s: '', v: '$55,680' },
    { t: 'kv', k: 'Mid move + 6 months', s: '', v: '$71,680' },
    { t: 'kv', k: 'Heavy move + 6 months', s: '', v: '$91,680' },
    { t: 'p', x: "$80,000 is safe for everything except the heaviest version, and even then you'd be most of the way there." },
    { t: 'h', x: 'Your half of European living: $2,640 a month' },
    { t: 'kv', k: 'Shared and halved', s: 'Rent $1,200 · groceries $450 · utilities $75 · internet $25', v: '$1,750' },
    { t: 'kv', k: 'All yours', s: 'Phone $80 · transit pass $110 · personal and misc $700', v: '$890' },
    { t: 'p', x: "That's down from $4,161 in Toronto, because rent gets split and there's no car." },
    { t: 'h', x: 'The move, $40,000 for the two of you' },
    { t: 'kv', k: 'Flights for two, one way with bags', s: '', v: '$2,500' },
    { t: 'kv', k: 'Shipping, 20ft container', s: '', v: '$10,000' },
    { t: 'kv', k: 'Temporary stay while flat hunting, 4 weeks', s: '', v: '$4,000' },
    { t: 'kv', k: 'Rental deposit and agency fees', s: '', v: '$8,000' },
    { t: 'kv', k: "First month's rent", s: '', v: '$2,600' },
    { t: 'kv', k: 'Visa, immigration and legal', s: '', v: '$3,000' },
    { t: 'kv', k: 'Furnishing gaps and appliances', s: '', v: '$5,000' },
    { t: 'kv', k: 'Admin, phones, bank setup', s: '', v: '$2,000' },
    { t: 'kv', k: 'Buffer', s: '', v: '$3,000' },
    { t: 'p', x: "**The biggest variable is shipping.** A container is $10,000 and selling everything is about $1,500. That one decision swings the couple total by nearly $9,000, and it's the first thing to settle together." },
    { t: 'h', x: 'How it gets funded' },
    { t: 'p', x: '$700 a month January to July, $1,000 a month August to December, from your regular pay. The same stream that fills the emergency fund first. Once that hits $15,000 in September 2027, everything flows here automatically.' },
    { t: 'p', x: 'Once this fund is full, those contributions go to your RRSP, because per dollar the RRSP is worth more at your horizon.' },
    { t: 'p', x: '**Total liquid at departure: $55,000.** $15,000 emergency plus $40,000 future fund.' },
    { t: 'h', x: 'Why cash and not investments' },
    { t: 'p', x: "The whole point is that it's there regardless of what markets do. The alternative is funding the move from your RRSP, which costs 25% on withdrawal as a non-resident, or around 40% if you end up in England outside the first four years. On $40,000 that's $10,000 to $16,000 of tax. Cash isn't the expensive option for money you'll definitely spend, it's the cheap one." },
    { t: 'h', x: 'When to revisit this number' },
    { t: 'p', x: 'If you decide to sell everything instead of shipping, the target drops by around $4,500.' },
    { t: 'p', x: 'If either of you gets a job with relocation support, companies often cover shipping, flights and temporary accommodation, which could take $15,000 to $20,000 off.' },
    { t: 'p', x: 'Once you pick a city, since Paris and London rents are very different from Lisbon or Berlin.' },
    { t: 'p', x: 'And the visa route may set a minimum balance. Several European visas need proof of funds, and if so, that becomes the target whatever you\'d otherwise plan.' },
  ],
  baby: [
    { t: 'lead', x: '**My target: $35,000**, planning for an 18-month leave. The calculated need is $33,691.' },
    { t: 'h', x: 'My spending while on leave: $4,370 a month' },
    { t: 'kv', k: 'True essentials', s: 'Rent, bills, car loan', v: '$2,749' },
    { t: 'kv', k: 'Groceries', s: '', v: '$550' },
    { t: 'kv', k: 'Hair', s: '', v: '$250' },
    { t: 'kv', k: 'Buffer', s: '', v: '$651' },
    { t: 'kv', k: 'Less commuting gas', s: '', v: '−$80' },
    { t: 'kv', k: 'My half of baby costs', s: 'Of $500 total, split in half', v: '+$250' },
    { t: 'h', x: "Colgate's top-up: about $14,687" },
    { t: 'p', x: 'The Maternity Leave Supplement Program tops up government payments for the first 17 weeks. **Assuming 90%** of my weekly pay, that\'s $1,809 a week, of which EI covers $729, so Colgate pays $1,080 a week. Over 17 weeks that\'s $18,359 gross, roughly $14,687 after tax.' },
    { t: 'p', x: "It's fixed at 17 weeks whichever option I choose." },
    { t: 'h', x: 'The three options' },
    { t: 'p', x: 'Maternity is always 15 weeks at 55%, capped at $729 a week. What changes is the parental portion: **standard** pays 55% ($729) for 35 weeks, **extended** pays 33% ($437) for up to 61 weeks.' },
    { t: 'table', cols: ['', 'EI per month', 'My gap', 'I need'], rows: [
      { c: ['12 months\n15 + 35 standard', '$2,525', '$1,845', '$8,365'] },
      { c: ['15 months\n15 + 50 extended', '$1,747', '$2,623', '$26,435'] },
      { c: ['18 months\n15 + 61 extended', '$1,713', '$2,657', '$33,691'], hi: true },
    ] },
    { t: 'sm', x: 'Each includes $1,750 for my half of the crib, stroller, car seat and clothes, and subtracts the $14,687 top-up.' },
    { t: 'more' },
    { t: 'h', x: 'Why longer costs so much more' },
    { t: 'p', x: "Extended parental is roughly the same pot of EI money stretched over more months, so the monthly payment drops by about a third. Going from 12 to 15 months costs an extra $18,070. The step from 15 to 18 is smaller at $7,256, because by then I'm already covering most of my own costs." },
    { t: 'h', x: 'What could still move it' },
    { t: 'p', x: 'The 90% top-up assumption, which HR will confirm. Every 10 percentage points is worth around $2,700.' },
    { t: 'p', x: 'The $500 monthly baby estimate, which tends to run higher than planned.' },
    { t: 'p', x: 'The car loan clears in December 2028, freeing $546 a month from the essentials figure.' },
  ],
};

// **bold** → <b>bold</b>
const richText = (x) => String(x || '').split(/(\*\*[^*]+\*\*)/g).map((part, i) => (/^\*\*[^*]+\*\*$/.test(part) ? <b key={i}>{part.slice(2, -2)}</b> : part));
// "Big\nsmall" → Big with small text under it
const twoLine = (x) => { const [a, ...rest] = String(x || '').split('\n'); return <>{richText(a)}{rest.length > 0 && <small>{richText(rest.join(' '))}</small>}</>; };

function NoteBlocks({ blocks, all }) {
  const cut = blocks.findIndex((b) => b.t === 'more');
  const shown = all || cut < 0 ? blocks : blocks.slice(0, cut);
  return shown.map((b, i) => {
    if (b.t === 'more') return null;
    if (b.t === 'h') return <p key={i} className="ln20-h">{richText(b.x)}</p>;
    if (b.t === 'lead') return <p key={i} className="ln20-lead">{richText(b.x)}</p>;
    if (b.t === 'sm') return <p key={i} className="ln20-sm">{richText(b.x)}</p>;
    if (b.t === 'kv') return <div key={i} className="ln20-kv"><span>{richText(b.k)}{b.s ? <small>{richText(b.s)}</small> : null}</span><b>{twoLine(b.v)}</b></div>;
    if (b.t === 'table') {
      const n = Math.max(1, (b.cols || []).length);
      return (
        <div key={i} className="ln20-tbl mp-ntbl" style={{ gridTemplateColumns: '1.05fr repeat(' + (n - 1) + ', 1fr)' }}>
          {(b.cols || []).map((c, k) => <b key={'h' + k} className={k === 0 ? 'l' : ''}>{twoLine(c)}</b>)}
          {(b.rows || []).map((r, ri) => (
            <div key={ri} className={'ln20-tr' + (r.hi ? ' likely' : '')}>
              {Array.from({ length: n }, (_, k) => (k === 0 ? <span key={k}>{twoLine((r.c || [])[k])}</span> : <em key={k}>{twoLine((r.c || [])[k])}</em>))}
            </div>
          ))}
        </div>
      );
    }
    return <p key={i}>{richText(b.x)}</p>;
  });
}

const NOTE_KINDS = [['h', 'Heading'], ['p', 'Text'], ['kv', 'Line with amount'], ['table', 'Table'], ['more', '"Read all" break']];
const newBlock = (t) => (t === 'kv' ? { t, k: '', s: '', v: '' } : t === 'table' ? { t, cols: ['', 'Column', 'Column'], rows: [{ c: ['', '', ''] }] } : t === 'more' ? { t } : { t, x: '' });

function NoteEditor({ start, onSave, onCancel }) {
  const [bl, setBl] = useState(() => JSON.parse(JSON.stringify(start)));
  const upd = (i, p) => setBl((a) => a.map((b, k) => (k === i ? { ...b, ...p } : b)));
  const move = (i, d) => setBl((a) => { const j = i + d; if (j < 0 || j >= a.length) return a; const c = a.slice(); [c[i], c[j]] = [c[j], c[i]]; return c; });
  const del = (i) => setBl((a) => a.filter((_, k) => k !== i));
  const label = { lead: 'Big text', p: 'Text', sm: 'Small text', h: 'Heading', kv: 'Line with amount', table: 'Table', more: '"Read all" break' };
  return (
    <div className="mp-ned">
      <p className="mp-ned-help">Put <b>**two stars**</b> around words to make them bold. In a table cell or an amount, a new line becomes the small text under it.</p>
      {bl.map((b, i) => (
        <div key={i} className="mp-ned-b">
          <div className="mp-ned-top">
            {b.t === 'p' || b.t === 'lead' || b.t === 'sm' ? (
              <select className="mp-ned-kind" value={b.t} onChange={(e) => upd(i, { t: e.target.value })}>
                <option value="lead">Big text</option><option value="p">Text</option><option value="sm">Small text</option>
              </select>
            ) : <span className="mp-ned-kind">{label[b.t]}</span>}
            <span className="mp-ned-tools">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === bl.length - 1} aria-label="Down">↓</button>
              <button type="button" onClick={() => del(i)} aria-label="Remove">✕</button>
            </span>
          </div>
          {b.t === 'h' && <input className="mp-ned-in" value={b.x || ''} onChange={(e) => upd(i, { x: e.target.value })} placeholder="Heading" />}
          {(b.t === 'p' || b.t === 'lead' || b.t === 'sm') && <textarea className="mp-ned-in" rows={Math.max(2, Math.min(12, Math.ceil(String(b.x || '').length / 32) + 1))} value={b.x || ''} onChange={(e) => upd(i, { x: e.target.value })} placeholder="Write here" />}
          {b.t === 'kv' && (
            <>
              <input className="mp-ned-in" value={b.k || ''} onChange={(e) => upd(i, { k: e.target.value })} placeholder="Label" />
              <input className="mp-ned-in" value={b.s || ''} onChange={(e) => upd(i, { s: e.target.value })} placeholder="Small line under it (optional)" />
              <textarea className="mp-ned-in" rows={2} value={b.v || ''} onChange={(e) => upd(i, { v: e.target.value })} placeholder="Amount" />
            </>
          )}
          {b.t === 'more' && <p className="mp-ned-help" style={{ margin: 0 }}>Everything below this waits for "Read all your notes".</p>}
          {b.t === 'table' && (() => {
            const n = (b.cols || []).length;
            const setCol = (k, v) => upd(i, { cols: b.cols.map((c, j) => (j === k ? v : c)) });
            const setCell = (ri, k, v) => upd(i, { rows: b.rows.map((r, j) => (j === ri ? { ...r, c: Array.from({ length: n }, (_, x) => (x === k ? v : (r.c || [])[x] || '')) } : r)) });
            return (
              <div className="mp-ned-tbl">
                <p className="mp-ned-sub">Column titles</p>
                <div className="mp-ned-row" style={{ gridTemplateColumns: 'repeat(' + n + ', minmax(62px, 1fr))' }}>
                  {b.cols.map((c, k) => <textarea key={k} rows={3} className="mp-ned-in sm" value={c} onChange={(e) => setCol(k, e.target.value)} />)}
                </div>
                {b.rows.map((r, ri) => (
                  <div key={ri}>
                    <div className="mp-ned-rowtop">
                      <span>Row {ri + 1}</span>
                      <label><input type="checkbox" checked={!!r.hi} onChange={(e) => upd(i, { rows: b.rows.map((x, j) => (j === ri ? { ...x, hi: e.target.checked } : x)) })} /> Highlight</label>
                      <button type="button" onClick={() => upd(i, { rows: b.rows.filter((_, j) => j !== ri) })}>Remove</button>
                    </div>
                    <div className="mp-ned-row" style={{ gridTemplateColumns: 'repeat(' + n + ', minmax(62px, 1fr))' }}>
                      {Array.from({ length: n }, (_, k) => <textarea key={k} rows={3} className="mp-ned-in sm" value={(r.c || [])[k] || ''} onChange={(e) => setCell(ri, k, e.target.value)} />)}
                    </div>
                  </div>
                ))}
                <div className="mp-ned-adds">
                  <button type="button" onClick={() => upd(i, { rows: b.rows.concat([{ c: Array.from({ length: n }, () => '') }]) })}>+ Row</button>
                  <button type="button" onClick={() => upd(i, { cols: b.cols.concat(['Column']), rows: b.rows.map((r) => ({ ...r, c: (r.c || []).concat(['']) })) })}>+ Column</button>
                  {n > 2 && <button type="button" onClick={() => upd(i, { cols: b.cols.slice(0, -1), rows: b.rows.map((r) => ({ ...r, c: (r.c || []).slice(0, n - 1) })) })}>− Last column</button>}
                </div>
              </div>
            );
          })()}
        </div>
      ))}
      <p className="mp-ned-sub" style={{ marginTop: 14 }}>Add</p>
      <div className="mp-ned-adds">
        {NOTE_KINDS.map(([t, l]) => <button key={t} type="button" onClick={() => setBl((a) => a.concat([newBlock(t)]))}>+ {l}</button>)}
      </div>
      <button className="mp-btn" onClick={() => onSave(bl)}>Done</button>
      <button className="mp-link mid" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function GoalNotes({ id, plan, set, ink = 'lavInk', bg = 'lav' }) {
  const saved = ((plan.goalNotes || {})[id]);
  const blocks = Array.isArray(saved) ? saved : (NOTE_DEFAULTS[id] || []);
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState(false);
  const save = (bl) => { set({ goalNotes: { ...(plan.goalNotes || {}), [id]: bl } }); setEditing(false); };
  if (!blocks.length && !editing) {
    return <button type="button" className="mp-change" onClick={() => setEditing(true)}>📝 Add notes</button>;
  }
  const hasMore = blocks.some((b) => b.t === 'more');
  return (
    <Card eb="📝 Your notes" ink={ink} className="ln20-notes mp-notes">
      <div style={{ '--nk': 'var(--' + ink + ')', '--nb': 'var(--' + bg + ')' }}>
      {!editing && <button type="button" className="mp-notes-edit" onClick={() => setEditing(true)}>Edit</button>}
      {editing
        ? <NoteEditor start={blocks.length ? blocks : [newBlock('p')]} onSave={save} onCancel={() => setEditing(false)} />
        : (
          <>
            <NoteBlocks blocks={blocks} all={all} />
            {hasMore && <button className="ln20-more" onClick={() => setAll(!all)}>{all ? 'Show less' : 'Read all your notes ›'}</button>}
          </>
        )}
      </div>
    </Card>
  );
}

// ln_32: on a goal's page, where it sits in your goal savings lineup
function LineupCard({ id, plan, set, w, go }) {
  const q = w.queue || [];
  const i = q.findIndex((b) => b.id === id);
  if (i < 0) return null;
  const b = q[i];
  const full = !b.needs && b.have >= b.target;
  const filling = q.find((x) => !x.needs && !(x.have >= x.target)) === b;
  return (
    <Card eb="🎯 From your goal savings" ink="lavInk">
      <Calc rows={[
        ['Place in your lineup', (i + 1) + ' of ' + q.length],
        ['After', i === 0 ? 'Nothing, it goes first' : q[i - 1].t],
        ['Starts', b.needs ? 'Needs an amount first' : full ? 'Full already' : filling ? 'Filling now' : b.startAt ? monthYear(b.startAt) : '—'],
        Object.assign(['Full', b.keeps ? 'Keeps going until you retire' : full ? 'Now' : b.fullAt ? 'about ' + monthYear(b.fullAt) : 'not yet'], { tot: true }),
        ...(b.by && !b.keeps ? [[b.st === 'behind' ? 'Wanted by, so it’s late' : 'Wanted by', monthYear(b.by)]] : []),
      ]} />
      <button className="mp-change" onClick={() => go({ step: stepIndex('money'), solo: true })}>Change the order or amounts</button>
    </Card>
  );
}

function GoalPage({ id, plan, set, W, w, go }) {
  const [sheet, setSheet] = useState(null);
  const g = allGoals(plan, w).find((x) => x.id === id);
  if (!g) return <p className="mp-p">This goal isn't here any more.</p>;
  const Change = ({ step, label, onClick }) => <button className="mp-change" onClick={onClick || (() => go({ step: stepIndex(step), solo: true }))}>{label}</button>;
  const B = plan.baby;
  let hero = { have: 0, of: '', cells: [] };
  let body = null;
  if (id === 'efund') {
    hero = { have: w.efBal, of: 'of ' + fmt(w.efTarget) + (w.efFull ? (w.efFull === 'Full!' ? ' · full!' : ' · full about ' + w.efFull) : ''), cells: efCover(plan, w).extra > 0 ? [['Covers', efCover(plan, w).total.toFixed(1) + ' months'], ['Savings alone', efCover(plan, w).savings.toFixed(1) + ' months'], ['From pay', fmt(w.efYear) + '/yr']] : [['Covers', w.efMonthsNow.toFixed(1) + ' months'], ['From pay', fmt(w.efYear) + '/yr']] };
    body = (
      <>
        <Card eb="🔗 Saved in" ink="mintInk">{/* up_02 */}
          <Row e="🛟" bg="sky" t={w.efFund ? w.efFund.name : 'Typed in'} s={w.efFund ? null : 'Not linked to a Worthly fund'} right={fmt(w.efBal)}><Src k={w.efFund ? 'w' : 'y'} /></Row>
          <Row e="🎯" bg="mint" t="Goal" right={fmt(w.efTarget)}><Src k="y" /></Row>
          {efCover(plan, w).extra > 0 && <Row e="💼" bg="peach" t="Severance and EI" right={'+' + efCover(plan, w).extra + ' months'}><Src k="y" /></Row>}
          {num(plan.cushion.by) > 0 && <Row e="🗓️" bg="lav" t="Want it full by" right={MONS[(num(plan.cushion.byMonth) || 12) - 1] + ' ' + plan.cushion.by}><Src k="y" /></Row>}
        </Card>
        <Card eb="🧮 How it's worked out" ink="skyInk" style={{ '--k': 'var(--skyInk)' }}>
          <Calc rows={[['Essentials a month', fmt(w.essMonth)], ['Life buffer', fmt(w.buffer)], Object.assign(['An emergency month', fmt(w.emergencyMonth)], { tot: true }), ...(efCover(plan, w).extra > 0 ? [['Covered now, savings alone', efCover(plan, w).savings.toFixed(1) + ' months'], ['Severance and EI', '+' + efCover(plan, w).extra.toFixed(1) + ' months'], Object.assign(['Covered now, all together', efCover(plan, w).total.toFixed(1) + ' months'], { tot: true })] : []), ['Covered when full, savings alone', w.efMonthsFull.toFixed(1) + ' months'], ['Then your pay savings go to', 'your future fund']]} />
        </Card>
        <GoalNotes id="efund" plan={plan} set={set} />
        <Change step="cushion" label="Change your cushion" />
        <Change step="money" label="Change essentials or savings" />
      </>
    );
  } else if (id === 'travel') {
    hero = { have: w.travelHave, of: 'of ' + fmt(w.travelGoal) + ' this year', cells: [['Used', fmt(w.travelUsed)], ['In the fund', fmt(w.travelFundBal)], ['To go', fmt(w.travelToGo)]] };
    body = (
      <>
        <div className="mp-stack"><TravelYear plan={plan} set={set} w={w} hideHero readOnly /></div>
        <Card eb="🔗 Saved in" ink="mintInk" className="mp-gap">{/* up_02 */}
          <Row e="🇳🇬" bg="lav" t={w.travelFund ? w.travelFund.name : 'Not linked to a fund'} right={fmt(w.travelFundBal)}><Src k="w" /></Row>
          <Row e="🎯" bg="mint" t="Goal this year" right={fmt(w.travelGoal)}>{num(plan.travel.target) > 0 ? <Src k="y" /> : <Src k="c" text="from your trips" />}</Row>
        </Card>
        <Card eb="🗓️ How it changes" ink="lavInk" className="mp-gap">
          <Row t="Now" s={plan.travel.now.map((x) => x.t).join(' + ')} right={fmt(w.travelNowYear)} />
          <Row t={'From ' + w.laterFrom} s={plan.travel.later.map((x) => (num(x.trips) > 1 ? x.trips + ' × ' : '') + x.t).join(' + ')} right={fmt(w.travelLaterYear)} />
          <p className="mp-hint left">Over your lifetime, about {short(w.travelLifetime)} in today's dollars. It's part of your retirement number too.</p>
        </Card>
        <Change step="travel" label="Change trips or prices" />
      </>
    );
  } else if (id === 'move') {
    hero = { have: w.futureHave, of: 'of ' + fmt(w.moveTotal) + (w.futureFull ? ' · full about ' + w.futureFull : ''), cells: [['Starts', w.efLeft <= 0 ? 'Now' : w.futureStart ? monthYear(w.futureStart) : '—'], ['Then', fmt(w.futureMonthly) + '/mo'], ['Ready by', MONS[(num(plan.move.readyMonth) || 1) - 1] + ' ' + plan.move.ready]] };
    body = (
      <>
        <Card eb="🌊 How your future fund grows" ink="blushInk">
          <FutureChart w={w} />
          <p className="mp-hint left">Flat until the goals before it in your lineup are full, then your goal savings fill it.</p>
        </Card>
        <Card eb="🔗 Saved in" ink="mintInk">{/* up_02 */}
          <Row e="🌱" bg="blush" t={w.futureFund ? w.futureFund.name : 'Typed in'} right={fmt(w.futureHave)}><Src k={w.futureFund ? 'w' : 'y'} /></Row>
          <Row e="🎯" bg="mint" t="Goal" right={fmt(w.moveTotal)}>{num(plan.move.target) > 0 ? <Src k="y" /> : <Src k="c" text="from the move and months in Europe" />}</Row>
        </Card>
        <GoalNotes id="move" plan={plan} set={set} ink="blushInk" bg="blush" />
        <Card>
          <Row e="✈️" bg="blush" t="Your half of the move" right={fmt(w.moveHalf)} />
          <Row e="🏙️" bg="sky" t={w.runwayMonths + ' months in Europe'} s={fmt(w.euMonth) + ' a month, grown to ' + plan.move.ready} right={fmt(w.runway)} />
          <Row e="🌍" bg="lav" t="A year before you go" s={plan.leavingList.filter((x) => !x.done).length + ' things to do'} right="›" onClick={() => go({ step: stepIndex('leaving'), solo: true })} />
        </Card>
        <Change step="move" label="Change the move" />
      </>
    );
  } else if (id === 'baby') {
    hero = { have: w.babyHave, of: 'of ' + fmt(w.babyGap) + ' by ' + B.year, cells: [['Needs', fmt(w.babyNeedMonthly) + '/mo'], ['Putting in', fmt(w.babyMonthly) + '/mo'], ['Months left', String(w.babyMonthsTo)]] };
    body = (
      <>
        <Card eb="🔗 Saved in" ink="mintInk">{/* up_02 */}
          <Row e={'\u{1F33F}'} bg="blush" t={w.babyFund ? w.babyFund.name : 'Typed in'} s={w.babyFund ? null : 'Make a fund for it in Worthly and pick it in Change'} right={fmt(w.babyHave)}><Src k={w.babyFund ? 'w' : 'y'} /></Row>
          <Row e="💸" bg="sky" t="Putting in a month" right={fmt(w.babyMonthly)}><Src k="y" /></Row>
          <Row e="🎯" bg="mint" t="Goal" right={fmt(w.babyGap)}>{num(B.target) > 0 ? <Src k="y" /> : <Src k="c" text="the gap leave leaves" />}</Row>
        </Card>
        <GoalNotes id="baby" plan={plan} set={set} ink={g.ink} bg={g.bg} />{/* plan_37 */}
        <Change label="Change life events" onClick={() => setSheet('baby')} />
      </>
    );
  } else if (id === 'retire') {
    hero = { have: w.invested, of: 'of ' + short(w.retNeed) + ' by ' + w.retAge, /* plan_35 */ cells: [['You add', fmt(w.rrspAll) + '/yr'], ['Employer adds', fmt(w.colgateYear) + '/yr'], ['Heading for', short(w.retHeading)]] };
    body = (
      <>
        <Card eb="🎯 Your goal" ink="mintInk">{/* up_02 */}
          <Row t="Goal" right={short(w.retNeed)}>{num(plan.retire.target) > 0 ? <Src k="y" /> : <Src k="c" text="worked out below" />}</Row>
        </Card>
        <Card eb={'🧾 Where ' + short(w.retNeedCalc) + ' comes from'} ink="mintInk" style={{ '--k': 'var(--mintInk)' }}>
          <Calc rows={[['Everyday life a year', fmt(num(plan.retire.everyday))], ['Seeing family a year', fmt(w.travelLaterYear)], ['Government pensions', '−' + fmt(num(plan.retire.gov))], Object.assign(['From savings a year', fmt(w.fromSavings)], { tot: true }), ['Lasting ' + w.yearsIn + ' years', short(w.retNeedCalc)]]} />
        </Card>
        <Card eb="🔭 Heading for" ink="skyInk">
          <Calc rows={(() => { const to = (s) => () => go({ step: stepIndex(s), solo: true }); const L = (a, b, f) => Object.assign([a, b], { go: f }); return [L('Invested today', fmt(w.invested), to('networth')), L('RRSP from pay a year', fmt(w.rrspYear), to('money')), L('Pension a year', fmt(w.pensionYear), to('money')), ...(w.queueDoneAt ? [L('Goal savings, from ' + monthYear(w.queueDoneAt), fmt(w.efAll) + '/yr', to('order'))] : []), L('Growth after prices', (w.real * 100).toFixed(1) + '% a year', () => go({ area: 'numbers' }))]; })()} />
          <p className="mp-hint left">Tap a line to change it.</p>
        </Card>
        <Change step="retire" label="Change retirement" />
      </>
    );
  } else if (id === 'resp') {
    hero = { have: 0, of: 'starts when baby arrives', cells: [['A year', fmt(num(plan.resp.yearly))], ['Top-up', '+' + fmt(w.respGrant)], ['By 18', short(w.respTotal)]] };
    body = <Change label="Change kids' education" onClick={() => setSheet('resp')} />;
  } else {
    const raw = plan.goals.find((x) => x.id === id);
    hero = { have: g.saved || 0, of: num(g.amount) ? 'of ' + fmt(g.then) + (g.year ? ' by ' + byLabel(g.month, g.year) : '') : 'Add an amount to start', cells: num(g.amount) ? [['Needs', fmt(g.need) + '/mo'], ['Putting in', fmt(g.monthly) + '/mo'], ['By', g.year ? String(g.year) : '—']] : [] };
    body = <Change label="Change this goal" onClick={() => setSheet({ kind: 'goal', g: raw })} />;
  }
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--' + g.ink + ')' }}>🎯 Goals</p>
      <p className="ph">{g.t.toLowerCase()}</p>
      {g.s && g.custom && <p className="phs">{g.s}</p>}
      <div className="mp-hero" style={{ background: 'var(--' + g.bg + ')', '--k': 'var(--' + g.ink + ')' }}>
        <div className="gauge">
          <span className="rw"><Ring pct={goalPct(g)} ink={g.ink} /><span className="re">{g.e}</span></span>
          <div><Pill s={g.status} /><div className="mp-big" style={{ marginTop: 8 }}>{fmt(hero.have)}</div><div className="of">{hero.of}</div></div>
        </div>
        {hero.cells.length > 0 && <div className="mp-cells">{hero.cells.map(([l, v]) => <div key={l}><small>{l}</small><b>{v}</b></div>)}</div>}
      </div>
      <div className="mp-stack mp-gap2"><LineupCard id={id} plan={plan} set={set} w={w} go={go} />{body}{!['efund', 'move', 'baby'].includes(id) && <GoalNotes key={'n-' + id} id={id} plan={plan} set={set} ink={g.ink} bg={g.bg} />}</div>
      {sheet === 'baby' || sheet === 'resp' ? <SpecialEditor kind={sheet} plan={plan} set={set} W={W} w={w} onClose={() => setSheet(null)} /> : null}
      {sheet && sheet.kind === 'goal' && <GoalEditor g={sheet.g} funds={W.funds} onClose={() => setSheet(null)}
        onSave={(x) => { const c = { ...x }; delete c.isNew; set({ goals: plan.goals.map((y) => (y.id === x.id ? c : y)) }); setSheet(null); }}
        onDelete={() => { if (window.confirm('Remove ' + sheet.g.t + '?')) { set({ goals: plan.goals.filter((y) => y.id !== sheet.g.id), order: plan.order.filter((k) => k !== sheet.g.id) }); setSheet(null); go(null); } }} />}
    </>
  );
}

function GoalsList({ plan, set, W, w, go }) {
  const [edit, setEdit] = useState(null);
  const [sorting, setSorting] = useState(false);
  const list = orderGoals(allGoals(plan, w), plan);
  // Worthly trial: move a goal up or down within its group. Your top three
  // must-haves show on your plan's main page.
  const shift = (id, dir) => {
    const ids = list.map((g) => g.id);
    const tier = (list.find((g) => g.id === id) || {}).tier;
    const same = list.filter((g) => g.tier === tier).map((g) => g.id);
    const i = same.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= same.length) return;
    [same[i], same[j]] = [same[j], same[i]];
    let k = 0;
    set({ goalOrder: ids.map((x) => ((list.find((g) => g.id === x) || {}).tier === tier ? same[k++] : x)) });
  };
  const good = (s) => s === 'on' || s === 'done' || s === 'covered';
  return (
    <>
      <div className="ln19-hd">
        <div><p className="ph">your goals</p><p className="phs">Tap a goal to see everything about it.</p></div>
        <button className="ln19-cal" aria-label="Add a goal" onClick={() => setEdit({ id: pid('g'), e: '✨', t: '', tier: 'love', amount: 0, year: '', s: '', monthly: 0, isNew: true })}>＋</button>
      </div>
      {['must', 'love', 'nice'].map((tier) => {
        const gs = list.filter((g) => g.tier === tier);
        if (!gs.length) return null;
        return (
          <div key={tier} className="ln19-card">
            <div className="ln19-ch"><h3>{tierTag[tier][1]}</h3>{tier === 'must' && <span>{gs.filter((g) => good(g.status)).length} of {gs.length} on track</span>}</div>
            {tier === 'must' && sorting && <p className="ln19-sorthint">Your top three show on your plan's main page.</p>}
            {gs.map((g, gi) => (sorting
              ? (
                <div key={g.id} className="ln19-sortrow">
                  <GoalRow g={g} w={w} plan={plan} tools={(
                    <span className="ln19-sort">
                      <button aria-label="Move up" disabled={gi === 0} onClick={() => shift(g.id, -1)}>↑</button>
                      <button aria-label="Move down" disabled={gi === gs.length - 1} onClick={() => shift(g.id, 1)}>↓</button>
                    </span>
                  )} />
                  <label className="ln19-tier">
                    <span>Group</span>
                    <select value={g.tier} onChange={(e) => setGoalTier(plan, set, g.id, e.target.value)}>
                      {TIER_NAMES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                    </select>
                  </label>
                </div>
              )
              : <GoalRow key={g.id} g={g} w={w} plan={plan} onClick={() => go({ goal: g.id })} />))}
          </div>
        );
      })}
      {sorting
        ? <button className="ln19-btn wide" style={{ marginTop: 16 }} onClick={() => setSorting(false)}>Done</button>
        : (
          <>
            <button className="ln19-sortlink" onClick={() => setSorting(true)}>↕ Change the order or groups</button>
            <button className="ln19-btn wide" style={{ marginTop: 16 }} onClick={() => setEdit({ id: pid('g'), e: '✨', t: '', tier: 'love', amount: 0, year: '', s: '', monthly: 0, isNew: true })}>+ Add a goal</button>
          </>
        )}
      {edit && <GoalEditor g={edit} funds={W.funds} onClose={() => setEdit(null)}
        onSave={(x) => { const c = { ...x }; delete c.isNew; set({ goals: [...plan.goals, c], order: [...plan.order, x.id] }); setEdit(null); go({ goal: x.id }); }} onDelete={() => setEdit(null)} />}
    </>
  );
}

function SafetyArea({ plan, set, w, go }) {
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--skyInk)' }}>🛡️ Safety net</p>
      <p className="ph">your safety net</p>
      <p className="phs">Your cushion, insurance and paperwork, all in one place.</p>
      <button className="mp-hero tap" style={{ background: 'var(--sky)', '--k': 'var(--skyInk)' }} onClick={() => go({ goal: 'efund' })}>
        <div className="gauge">
          <span className="rw"><Ring pct={pct(w.efBal, w.efTarget)} ink="skyInk" /><span className="re">🛟</span></span>
          <div><small className="lab">Emergency fund</small><div className="mp-big">{efCover(plan, w).total.toFixed(1)} months</div><div className="of">{efCover(plan, w).extra > 0 ? efCover(plan, w).savings.toFixed(1) + ' months on savings alone · ' : ''}{fmt(w.efBal)} of {fmt(w.efTarget)}</div></div>
        </div>
      </button>
      <Card eb="🩹 Insurance" ink="skyInk" className="mp-gap mp-roomy">
        {insOf(plan).map((i) => <Row key={i.k} e={i.e} bg="field" t={i.t} right={<Tag c={plan.insurance[i.k] === 'Not sure' ? 'butter' : /don't/i.test(plan.insurance[i.k]) ? 'sky' : 'mint'}>{plan.insurance[i.k]}</Tag>} />)}
        <button className="mp-more" onClick={() => go({ step: stepIndex('insurance'), solo: true })}>Change insurance</button>
      </Card>
      <Card eb={'📝 Paperwork · ' + paperOf(plan).filter((p) => plan.paper[p.k]).length + ' of ' + paperOf(plan).length} ink="peachInk" className="mp-roomy">
        {paperOf(plan).map((p) => <Check key={p.k} on={!!plan.paper[p.k]} t={p.t} s={p.s} />)}
        <button className="mp-more" onClick={() => go({ step: stepIndex('paper'), solo: true })}>Change paperwork</button>{/* up_02 */}
      </Card>
    </>
  );
}
// ln_19: your to-do list in your own order. Drag the dots to move one; the top one is your next step.
function StepsArea({ plan, set, w }) {
  const list = orderedTodos(plan, w);
  const open = list.filter((t) => !isTodoDone(plan, t)), done = list.filter((t) => isTodoDone(plan, t));
  const [showDone, setShowDone] = useState(false);
  const [drag, setDrag] = useState(null); // { k, y }
  const rows = useRef({});
  const order = open.map((t) => t.k);
  const save = (keys) => set({ todoOrder: [...keys, ...done.map((t) => t.k)] });
  const down = (e, k) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
    setDrag({ k, y: e.clientY });
  };
  const move = (e) => {
    if (!drag) return;
    const i = order.indexOf(drag.k);
    const el = rows.current[drag.k];
    const h = el ? el.getBoundingClientRect().height + 8 : 56;
    const dy = e.clientY - drag.y;
    if (dy > h / 2 && i < order.length - 1) {
      const n = [...order]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; save(n); setDrag({ k: drag.k, y: drag.y + h });
    } else if (dy < -h / 2 && i > 0) {
      const n = [...order]; [n[i], n[i - 1]] = [n[i - 1], n[i]]; save(n); setDrag({ k: drag.k, y: drag.y - h });
    }
  };
  const up = () => setDrag(null);
  return (
    <>
      <div className="ln19-hd">
        <div><p className="ph">next steps</p><p className="phs">Drag ⋮⋮ to put them in your own order. The top one shows as your next step.</p></div>
      </div>
      <div className="ln19-card">
        {open.map((t, i) => (
          <div key={t.k} ref={(el) => { rows.current[t.k] = el; }} className={'ln19-todo big' + (drag && drag.k === t.k ? ' dragging' : '')}>
            <span className="h" onPointerDown={(e) => down(e, t.k)} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>⋮⋮</span>
            <button className="ck" aria-label="Done" onClick={() => tickTodo(plan, set, t)} />
            <span className={i === 0 ? 'first' : ''}>{t.t}{i === 0 && <em className="nx">Next</em>}<small>{t.area}</small></span>
          </div>
        ))}
        {!open.length && <p className="ln19-p">All done! Nothing to look at right now. 🎉</p>}
        <AddLine placeholder="Add your own" onAdd={(t) => set({ extraTodos: [...(plan.extraTodos || []), { id: pid('td'), t }] })} />
        {done.length > 0 && <button className="ln19-donelink" onClick={() => setShowDone(!showDone)}>✓ {done.length} done {showDone ? '⌃' : '›'}</button>}
        {showDone && done.map((t) => (
          <div key={t.k} className="ln19-todo big is-ticked">
            <button className="ck on" aria-label="Not done" onClick={() => tickTodo(plan, set, t)}>✓</button>
            <span>{t.t}</span>
          </div>
        ))}
      </div>
    </>
  );
}
// up_01: Update your plan. Every step in one list, with what's set now.
const STEP_E = { life: '🌷', ahead: '🔭', values: '💛', networth: '💚', money: '💵', extras: '🎉', debt: '🚗', cushion: '🛟', insurance: '🩹', paper: '📝', goals: '🎯', move: '✈️', travel: '🇳🇬', order: '↕️', invest: '🥧', tax: '📄', leaving: '🌍', retire: '🌻', calendar: '🗓️', guesses: '🔮' };
const stepNow = (k, plan, w) => {
  try {
    const A = plan.assume;
    const ex = Math.max(0, num((plan.cushion || {}).extraMonths));
    const [pa, pb] = periods(w.step);
    switch (k) {
      case 'life': return w.age + (plan.life.city ? ' · ' + plan.life.city : '');
      case 'ahead': return ((Array.isArray(plan.ahead) ? plan.ahead.length : 0) + (Array.isArray(plan.extraAhead) ? plan.extraAhead.length : 0)) + ' things ahead';
      case 'values': return plan.values.success || null;
      case 'networth': return 'Net worth ' + fmt(w.netWorth);
      case 'money': return fmt(w.payA) + ' ' + pa + ' · ' + fmt(w.payB) + ' ' + pb;
      case 'extras': return (w.extras || []).length + ' extras · ' + fmt(w.extrasIn) + ' a year';
      case 'debt': return (w.debts || []).length ? w.debts.map((d) => d.name).join(', ') : 'Nothing to pay off';
      case 'cushion': return fmt(w.efTarget) + ' goal' + (ex > 0 ? ' · ' + ex + ' months from severance and EI' : '');
      case 'insurance': { const n = insOf(plan).filter((i) => plan.insurance[i.k] === 'Not sure').length; return insOf(plan).length + ' kinds · ' + (n ? n + ' not sure yet' : 'all looked at'); }
      case 'paper': return paperOf(plan).filter((p) => plan.paper[p.k]).length + ' of ' + paperOf(plan).length + ' done';
      case 'goals': return allGoals(plan, w).length + ' goals';
      case 'move': return 'Ready by ' + MONS[(num(plan.move.readyMonth) || 1) - 1] + ' ' + plan.move.ready + ' · ' + fmt(w.moveTotal);
      case 'travel': return fmt(w.travelNowYear) + ' a year now';
      case 'order': return 'The order your goal savings fill your goals';
      case 'invest': return plan.invest.mix || null;
      case 'tax': return plan.tax.refund ? 'Refunds go to ' + String(plan.tax.refund).toLowerCase() : null;
      case 'leaving': return plan.leavingList.filter((x) => !x.done).length + ' things to do before you go';
      case 'retire': return 'Around ' + w.retAge + ' · ' + short(w.retNeed);
      case 'calendar': return calOf(plan).length + ' dates · ' + remindersOf(plan).length + ' reminders';
      case 'guesses': return 'Prices ' + A.prices + '% · growth ' + A.growth + '% · until ' + A.until;
      default: return null;
    }
  } catch (e) {
    return null;
  }
};
function UpdateArea({ plan, w, go }) {
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--lavInk)' }}>✏️ Update your plan</p>
      <p className="ph">update your plan</p>
      <p className="phs">Everything you set lives here. Change anything, and every page that uses it updates!</p>
      <Card className="mp-roomy">
        <Row e="📝" bg="sky" t="Go through every step" s={'All ' + STEPS.length + ' steps, one after another'} right="›" onClick={() => go({ step: 0, review: true })} />
      </Card>
      {CHAPTERS.map((c) => (
        <Card key={c.n} eb={c.e + ' ' + c.name} ink={c.ink} className="mp-roomy">
          {STEPS.map((s, i) => [s, i]).filter(([s]) => s.ch === c.n).map(([s, i]) => (
            <Row key={s.k} e={STEP_E[s.k] || c.e} bg={c.bg} t={s.t} s={stepNow(s.k, plan, w)} right="›" onClick={() => go({ step: i, solo: true })} />
          ))}
        </Card>
      ))}
    </>
  );
}

// up_03: every main number, where it comes from and where you change it
function NumberMap({ plan, w, go }) {
  const to = (k) => (k ? () => go({ step: stepIndex(k), solo: true }) : undefined);
  const ex = efCover(plan, w);
  const [pa, pb] = periods(w.step);
  const fromW = [
    ['🛟', 'Emergency fund', fmt(w.efBal), w.efFund ? 'Your ' + w.efFund.name + ' in Worthly' : 'Typed in on Your cushion', w.efFund ? null : 'cushion'],
    ['🌱', 'Future fund', fmt(w.futureHave), w.futureFund ? 'Your ' + w.futureFund.name + ' in Worthly' : 'Typed in on Moving abroad', w.futureFund ? null : 'move'],
    ['🇳🇬', 'Travel fund', fmt(w.travelFundBal), w.travelFund ? 'Your ' + w.travelFund.name + ' in Worthly' : 'Pick a fund on Seeing family', w.travelFund ? null : 'travel'],
    ['📈', 'Investments', fmt(w.invested), 'Worthly, plus anything you added on What you own and owe', 'networth'],
    ['🚗', 'What you owe', fmt(w.debtTotal), 'Your debts in Worthly', null],
  ];
  const fromYou = [
    ['💵', 'Pay after tax', fmt(w.payA) + ' / ' + fmt(w.payB), pa + ' and ' + pb + ' · Each month', 'money'],
    ['🏠', 'Essentials and buffer', fmt(w.emergencyMonth) + '/mo', 'Each month', 'money'],
    ['🌳', 'Savings from pay', fmt(w.setAsideMonthly) + '/mo', 'RRSP and goal savings, on average · Each month', 'money'],
    ['🎉', 'Extras', fmt(w.extrasIn) + '/yr', 'Bonuses, refund and third paycheques · Your extras', 'extras'],
    ['🎯', 'Emergency fund goal', fmt(w.efTarget), 'With severance and EI months · Your cushion', 'cushion'],
    ['🌻', 'Retiring at', String(w.retAge), 'With everyday life and pensions · Retirement', 'retire'],
    ['🔮', 'Your guesses', plan.assume.prices + '% · ' + plan.assume.growth + '%', 'Prices and growth · Your guesses', 'guesses'],
  ];
  const worked = [
    ['💚', 'Net worth', fmt(w.netWorth), 'Everything you own, less what you owe', 'networth'],
    ['💰', 'Left for fun', fmt(w.leftA) + ' / ' + fmt(w.leftB), 'Pay, less essentials, RRSP and goal savings', 'money'],
    ['🛡️', 'Safety net', ex.total.toFixed(1) + ' months', 'Emergency fund ÷ a month of essentials' + (ex.extra > 0 ? ', plus severance and EI' : ''), 'cushion'],
    ['🗓️', 'When each goal fills', '', 'Your goal savings, ' + pa + ' and ' + pb + ', in lineup order', 'money'],
    ['✈️', 'Moving abroad goal', fmt(w.moveTotal), num(plan.move.target) > 0 ? 'Your own target · Moving abroad' : 'The move plus months in Europe', 'move'],
    ['🇳🇬', 'Seeing family goal', fmt(w.travelGoal), num(plan.travel.target) > 0 ? 'Your own goal · Seeing family' : 'Your trips this year', 'travel'],
    ['🌻', 'Retirement number', short(w.retNeed), num(plan.retire.target) > 0 ? 'Your own goal · Retirement' : 'Everyday life and family travel, less pensions, for every year after you retire', 'retire'],
    ['🔭', 'Heading for', short(w.retHeading), "What's invested plus what goes in, grown with your guesses", 'retire'],
  ];
  const rows = (list, k) => list.map(([e, t, v, s, step]) => (
    <Row key={t} e={e} bg="field" t={t} s={s} right={v ? <>{v}{step ? ' ›' : ''}</> : (step ? '›' : null)} onClick={to(step)}><Src k={k} /></Row>
  ));
  return (
    <>
      <Card eb="🔗 From Worthly · updates itself" ink="skyInk" className="mp-roomy">{rows(fromW, 'w')}</Card>
      <Card eb="✏️ You set · in Update your plan" ink="peachInk" className="mp-roomy">{rows(fromYou, 'y')}</Card>
      <Card eb="🧮 Worked out · from the numbers above" ink="lavInk" className="mp-roomy">{rows(worked, 'c')}</Card>
      <p className="mp-hint">Tap any line to change what goes into it.</p>
    </>
  );
}

function KeepArea({ plan, go }) {
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--lavInk)' }}>📆 Check-up and changes</p>
      <p className="ph">keep it up to date</p>
      <p className="phs">Next check-up {nextCheckup(plan)}</p>
      {plan.values.success && <Card tint="sky" eb="🌱 In one sentence" ink="skyInk" className="mp-roomy"><p className="mp-p first" style={{ fontSize: 16 }}>{plan.values.success}</p></Card>}
      <Card className="mp-roomy">
        <Row e="📆" bg="lav" t="Yearly check-up" s={'Every year, next one ' + nextCheckup(plan)} right="›" onClick={() => go({ page: 'checkup' })} />
        <Row e="✨" bg="blush" t="Something changed" s="New job, baby, move or raise" right="›" onClick={() => go({ page: 'checkup', changed: true })} />
        <Row e="📸" bg="mint" t="Your year in your plan" s="How each goal moved this year" right="›" onClick={() => go({ page: 'year', y: new Date().getFullYear() })} />{/* tax_01 */}
        <Row e="🗂️" bg="butter" t="Past check-ups and plans" s={plan.checkups.length ? plan.checkups.length + ' so far' : 'Your history builds up here'} right="›" onClick={() => go({ page: 'history' })} />
        <Row e="📝" bg="sky" t="Go through every step" s="Walk through the whole plan again" right="›" onClick={() => go({ step: 0, review: true })} />
        <Row e="🌱" bg="peach" t="Start fresh" s="Make a brand new plan" right="›" onClick={() => go({ page: 'fresh' })} />
      </Card>
    </>
  );
}

function NetWorthArea({ plan, w, Change }) {
  const moveY = num(plan.move.ready) || w.y + 5;
  const N = Math.max(8, moveY - w.y + 2);
  const pts = Array.from({ length: N + 1 }, (_, n) => ({ v: n === 0 ? w.netWorth : w.nwAt(n), label: String(w.y + n) }));
  const nMove = Math.min(N, Math.max(1, moveY - w.y));
  const total = w.nwAt(nMove);
  const debtsPaid = w.debts.reduce((s, d) => s + Math.min(d.balance, d.monthly * 12 * nMove), 0);
  const efIn = w.efAll * nMove;
  const miles = [50000, 100000, 250000, 500000, 750000, 1000000].filter((m) => m > w.netWorth).map((m) => {
    for (let n = 1; n <= 40; n++) if (w.nwAt(n) >= m) return { m, y: w.y + n };
    return null;
  }).filter(Boolean).slice(0, 3);
  const parts = [
    { t: 'Investments', v: w.invested, c: 'lavMid' },
    { t: 'Sinking funds', v: w.fundsCounted, c: 'butterMid' },
    { t: 'Everyday cash', v: w.cash, c: 'mintMid' },
    ...(w.assets ? [{ t: 'Things you own', v: w.assets, c: 'skyMid' }] : []),
  ];
  const startNw = plan.startSnap ? plan.startSnap.netWorth : null;
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--mintInk)' }}>💚 Net worth</p>
      <p className="ph">your net worth</p>
      <p className="phs">Where you are today, and where your plan is heading if things go as planned.</p>
      <div className="mp-hero" style={{ background: 'var(--mint)', '--k': 'var(--mintInk)' }}>
        <small className="lab">Today</small>
        <div className="mp-big">{fmt(w.netWorth)}</div>
        <div className="of">About {short(total)} by {moveY}, in today's dollars{startNw != null && w.netWorth !== startNw ? ' · ' + (w.netWorth >= startNw ? 'up ' : 'down ') + fmt(Math.abs(w.netWorth - startNw)) + ' since your plan' : ''}</div>
        <GrowChart pts={pts} ink="mintInk" fill="glass" marks={[{ i: nMove, t: 'By ' + moveY }]} />
      </div>
      {miles.length > 0 && (
        <Card eb="🏁 Milestones on the way" ink="mintInk" className="mp-roomy">
          <div className="mp-miles">{miles.map((x, k) => <div key={x.m} style={{ background: 'var(--' + ['blush', 'lav', 'butter'][k] + ')', '--k': 'var(--' + ['blushInk', 'lavInk', 'butterInk'][k] + ')' }}><b>{x.m >= 1e6 ? '$' + x.m / 1e6 + 'M' : '$' + x.m / 1000 + 'k'}</b><small>about {x.y}</small></div>)}</div>
        </Card>
      )}
      <Card eb="🧺 What it's made of" ink="blushInk" className="mp-roomy">
        <SplitBar parts={parts} />
        <Legend parts={parts} />
        <div className="mp-legend minus"><div><i style={{ background: 'var(--blushMid)' }} /><span>What you owe</span><small /><b>−{fmt(w.debtTotal)}</b></div></div>
        <Src k="w" text="Worthly HQ not counted" />
      </Card>
      <Card eb={'🔭 How it gets to ' + short(total)} ink="skyInk" className="mp-roomy" >
        <Calc rows={[['Today', fmt(w.netWorth)], ['RRSP and pension, with growth', '+' + fmt(Math.max(0, total - w.netWorth - efIn - debtsPaid))], ['Emergency and future fund', '+' + fmt(efIn)], ['Debts paid down', '+' + fmt(debtsPaid)], Object.assign(['By ' + moveY, fmt(total)], { tot: true })]} />
        <Src k="c" text={'before the move costs · ' + plan.assume.growth + '% growth, ' + plan.assume.prices + '% prices'} />
      </Card>
      <Card eb="📈 At each check-up" ink="peachInk" className="mp-roomy">
        <Row t={dateLabel(plan.finishedAt || plan.createdAt)} s="Plan made" right={fmt(startNw != null ? startNw : w.netWorth)} />
        {plan.checkups.map((c) => <Row key={c.date} t={dateLabel(c.date)} s="Check-up" right={fmt(c.netWorth)} />)}
        {!plan.checkups.length && <p className="mp-hint left">Your first check-up adds a line here!</p>}
      </Card>
      <Change step="networth" />
    </>
  );
}

// ln_18: where your pay goes, as one ring with a switch between the two parts of the year
function PayRing({ labels, A, B, payA, payB }) {
  const [aL, bL] = labels;
  const two = aL !== bL && (payA !== payB || A.some((p, i) => !B[i] || B[i].v !== p.v));
  const [side, setSide] = useState(1);
  const parts = (two && !side ? A : B).filter((p) => p.v > 0);
  const pay = two && !side ? payA : payB;
  const tot = parts.reduce((s, p) => s + p.v, 0) || 1;
  const left = parts.find((p) => p.t === 'Left for fun');
  const r = 72, C = 2 * Math.PI * r;
  let off = 0;
  const arcs = parts.map((p) => {
    const L = C * p.v / tot;
    const el = <circle key={p.t} cx="95" cy="95" r={r} fill="none" stroke={'var(--' + p.c + ')'} strokeWidth="20" strokeDasharray={Math.max(0, L - 5) + ' ' + C} strokeDashoffset={-off} transform="rotate(-90 95 95)" strokeLinecap="round" />;
    off += L;
    return el;
  });
  const base = (c) => c.replace('Mid', '');
  return (
    <div className="mp-card mp-roomy ln18-pay">
      {two && (
        <div className="ln18-tog">
          <button className={!side ? 'on' : ''} onClick={() => setSide(0)}>{aL}</button>
          <button className={side ? 'on' : ''} onClick={() => setSide(1)}>{bL}</button>
        </div>
      )}
      <div className="ln18-ring">
        <svg viewBox="0 0 190 190">{arcs}</svg>
        <div className="c">
          <small>{left ? 'left for fun' : 'each month'}</small>
          <b>{fmt(left ? left.v : pay)}</b>
          <small>of {fmt(pay)} a month</small>
        </div>
      </div>
      <div className="ln18-leg">
        {parts.map((p) => (
          <div key={p.t} style={{ background: 'var(--' + base(p.c) + ')', color: 'var(--' + base(p.c) + 'Ink)' }}>
            <small>{p.t}</small><b>{fmt(p.v)}</b><span>{Math.round(p.v / Math.max(1, pay) * 100)}% of your pay</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MoneyArea({ plan, w, Change, go }) {
  const M = plan.money;
  const [aL, bL] = periods(w.step);
  const pay = (p, rr, ef, left) => [
    { t: 'Essentials', v: w.essMonth, c: 'blushMid' },
    { t: 'RRSP', v: rr, c: 'lavMid' },
    { t: 'Goal savings', v: ef, c: 'skyMid' },
    ...(num(M.pensionYou) ? [{ t: 'Your pension', v: num(M.pensionYou), c: 'butterMid' }] : []),
    { t: 'Left for fun', v: left, c: 'mintMid' },
  ];
  const A = pay(w.payA, num(M.rrspA), num(M.efA), w.leftA), B = pay(w.payB, num(M.rrspB), num(M.efB), w.leftB);
  const sinkMax = Math.max(1, ...w.sinking.map((f) => f.amount));
  return (
    <>
      <p className="eb mp-crumb" style={{ color: 'var(--mintInk)' }}>💰 Money in & out</p>
      <p className="ph">money in and out</p>
      <p className="phs">Your pay covers each month. Your extras fill your sinking funds through the year.</p>
      <div className="mp-sum2">
        <div style={{ background: 'var(--mint)', '--k': 'var(--mintInk)' }}><small>You save</small><b>{fmt(w.youSave)}</b><Wave n={0} /></div>
        <div style={{ background: 'var(--lav)', '--k': 'var(--lavInk)' }}><small>Employer adds</small><b>{fmt(w.colgateYear)}</b><Wave n={1} /></div>
        <div style={{ background: 'var(--butter)', '--k': 'var(--butterInk)' }}><small>Sinking funds</small><b>{fmt(w.fundsYear)}</b><Wave n={2} /></div>
      </div>

      <p className="eb mp-ebout" style={{ color: 'var(--mintInk)' }}>💼 Where your pay goes</p>
      <PayRing labels={[aL, bL]} A={A} B={B} payA={w.payA} payB={w.payB} />{/* ln_18 */}
      <button className="mp-whatif" onClick={() => go({ page: 'whatif' })}><span className="e">✨</span><span className="t"><b>What if?</b><small>Try a different goal savings amount, or a one-off</small></span><span className="go">›</span></button>{/* plan_21 */}
      <Change step="money" label="Change pay, savings or essentials" />

      <p className="eb mp-ebout" style={{ color: 'var(--butterInk)' }}>🎉 Your extras through the year</p>
      <Card className="mp-roomy">
        <div className="mp-bigrow" style={{ '--k': 'var(--butterInk)' }}><span className="mp-big" style={{ fontSize: 28 }}>{fmt(w.extrasIn)}</span><span>coming in a year</span></div>
        <ExtrasChart w={w} />
        <Calc rows={w.extras.map((x) => [x.e + ' ' + x.t + (x.when && x.when.length ? ' · ' + whenText(x.when) : ''), fmt(num(x.amount))])} />
      </Card>
      <Card eb="🫙 Filling your sinking funds" ink="butterInk" className="mp-roomy">
        {w.sinking.map((f, k) => (
          <div key={f.id} className="mp-sink" style={{ '--c': 'var(--' + ['blushMid', 'lavMid', 'skyMid', 'mintMid', 'butterMid'][k % 5] + ')' }}>
            <div><span>{f.e} {f.t}</span><b>{fmt(f.amount)}</b></div>
            <span className="mp-sinkbar"><i style={{ width: (f.amount / sinkMax * 100) + '%' }} /></span>
          </div>
        ))}
        <Calc rows={[Object.assign(['Sinking funds', fmt(w.fundsYear)], { tot: true }), [w.extrasLeft >= 0 ? 'Left over, free' : 'Short', fmt(Math.abs(w.extrasLeft))]]} />
      </Card>
      <Change step="extras" label="Change your extras" />

      <p className="eb mp-ebout" style={{ color: 'var(--skyInk)' }}>📅 Your money year</p>
      <Card className="mp-roomy"><MoneyYear plan={plan} w={w} /></Card>
    </>
  );
}

// plan_21: What if. Try a different goal savings amount, a one-off, or both,
// and see when every goal in your lineup would fill. Nothing is saved.
const monthsApart = (a, b) => (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth();
function WhatIf({ plan, W, w, back }) {
  const M = plan.money;
  const [aL, bL] = periods(w.step);
  const two = aL !== bL;
  const now = new Date();
  const [a, setA] = useState(num(M.efA));
  const [b, setB] = useState(num(M.efB));
  const [one, setOne] = useState(0);
  const [om, setOm] = useState(now.getMonth() === 11 ? 1 : now.getMonth() + 2);
  const [oy, setOy] = useState(now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear());
  const [sm, setSm] = useState(now.getMonth() === 11 ? 1 : now.getMonth() + 2); // plan_22: when the new amount starts
  const [sy, setSy] = useState(now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear());
  const w2 = useMemo(() => work({ ...plan, money: { ...M, efChange: { a, b: two ? b : a, start: Math.round(num(sy)) + '-' + String(sm).padStart(2, '0') }, oneOffs: num(one) ? [{ amount: num(one), start: Math.round(num(oy)) + '-' + String(om).padStart(2, '0') }] : [] } }, W), [plan, W, a, b, one, om, oy, sm, sy]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = (w.queue || []).filter((q) => !q.needs).map((q) => ({ q, n: (w2.queue || []).find((x) => x.id === q.id) || q }));
  const when = (q) => (q.keeps ? q.startAt : q.fullAt);
  const word = (q) => (q.keeps ? 'gets goal savings from ' : 'full ');
  return (
    <>
      <p className="ph">what if?</p>
      <p className="phs">Just to try things out. Nothing in your plan changes.</p>
      <Card eb="🎯 Your goal savings" ink="lavInk">
        <div className="mp-field"><span>Each month<small>Now {two ? fmt(num(M.efA)) + ' ' + aL + ' · ' + fmt(num(M.efB)) + ' ' + bL : fmt(num(M.efA))}</small></span>
          {two
            ? <span className="mp-pair"><Amt value={a} onChange={(v) => setA(num(v))} suffix={aL} small /><Amt value={b} onChange={(v) => setB(num(v))} suffix={bL} small /></span>
            : <Amt value={a} onChange={(v) => setA(num(v))} small />}
        </div>
        <div className="mp-field"><span>Starting<small>Until then, your plan's amounts carry on</small></span><span className="mp-wi-when"><MonthPick from={1} value={sm} onChange={setSm} /><Amt plain prefix="" value={sy} onChange={(v) => setSy(Math.round(num(v)))} small /></span></div>
        <div className={'mp-field' + (num(one) > 0 ? '' : ' last')}><span>A one-off<small>Optional</small></span><Amt value={one} onChange={(v) => setOne(num(v))} small /></div>
        {num(one) > 0 && <div className="mp-field last"><span>One-off added in</span><span className="mp-wi-when"><MonthPick from={1} value={om} onChange={setOm} /><Amt plain prefix="" value={oy} onChange={(v) => setOy(Math.round(num(v)))} small /></span></div>}
        <p className="mp-wi-note">Your goals fill one at a time, in your lineup order.</p>
      </Card>
      <Card eb="Your lineup with this" ink="lavInk">
        {rows.map(({ q, n }, i) => {
          const was = when(q);
          const is = when(n);
          const d = was && is ? monthsApart(is, was) : 0;
          const full = q.st === 'done';
          return (
            <div key={q.id} className="mp-wi-row">
              <span className="n">{i + 1}</span>
              <span className="e">{q.e}</span>
              <span className="tx">
                <b>{q.t}</b>
                {full ? <span className="now">Full already 🎉</span> : (
                  <>
                    {d !== 0 && <span className="was">{word(q)}{was ? monthYear(was) : 'not within 30 years'}</span>}
                    <span className="now">{word(n)}{is ? monthYear(is) : 'not within 30 years'}</span>
                  </>
                )}
              </span>
              {!full && d !== 0 && <span className={'mp-wi-diff ' + (d > 0 ? 'up' : 'down')}>{Math.abs(d)} mo {d > 0 ? 'sooner' : 'later'}</span>}
            </div>
          );
        })}
      </Card>
      <button className="mp-btn" onClick={back}>Done</button>
      <p className="mp-hint">Want to keep it? Change your amounts on the Each month step.</p>
    </>
  );
}

// plan_21: a goal in your lineup just filled up.
function Party({ b, w, onDone }) {
  const now = new Date();
  const early = b.by ? monthsApart(now, b.by) : 0;
  const nextB = (w.queue || []).find((x) => x.id !== b.id && !x.needs && x.st !== 'done');
  const stats = [['Saved', fmt(b.target)]];
  if (b.id === 'efund' && w.efMonthsNow) stats.push(['Covers', (Math.round(w.efMonthsNow * 10) / 10) + ' months']);
  if (b.by) stats.push(['Wanted by', monthYear(b.by)]);
  return (
    <div className="mp-party">
      <div className="mp-party-in">
        <div className="tile">{b.e}</div>
        <h2>Your {b.t.toLowerCase()} is full! 🎉</h2>
        <p className="lead">You saved every dollar of it{early > 1 ? ', and ' + early + ' months sooner than you wanted' : early === 1 ? ', and a month sooner than you wanted' : ''}!{b.id === 'efund' ? " That's your safety net, done!" : ''}</p>
        <div className="stats">{stats.map(([k, v]) => <div key={k}><small>{k}</small><b>{v}</b></div>)}</div>
        {nextB && (
          <div className="next">
            <span className="e">{nextB.e}</span>
            <span><small>Next in your lineup, starting now</small><b>{nextB.t}{nextB.keeps ? '' : nextB.fullAt ? ' · full about ' + monthYear(nextB.fullAt) : ''}</b></span>
          </div>
        )}
        <button className="mp-btn" onClick={onDone}>Keep going!</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- pages for each part */
function Area({ area, plan, set, W, w, go }) {
  const [sheet, setSheet] = useState(null);
  const sheets = sheet ? (sheet.kind === 'goal'
    ? <GoalEditor g={sheet.g} funds={W.funds} onClose={() => setSheet(null)}
        onSave={(x) => { const c = { ...x }; delete c.isNew; set({ goals: plan.goals.map((g) => (g.id === x.id ? c : g)) }); setSheet(null); }}
        onDelete={() => { if (window.confirm('Remove ' + sheet.g.t + '?')) { set({ goals: plan.goals.filter((g) => g.id !== sheet.g.id), order: plan.order.filter((k) => k !== sheet.g.id) }); setSheet(null); } }} />
    : <SpecialEditor kind={sheet.kind} plan={plan} set={set} W={W} w={w} onClose={() => setSheet(null)} />) : null;
  const Change = ({ step, label }) => <button className="mp-change" onClick={() => go({ step: stepIndex(step), solo: true })}>{label || 'Change this'}</button>;
  const head = (e, t, ink) => <><p className="eb mp-crumb" style={{ color: 'var(--' + ink + ')' }}>{e} {t}</p></>;
  if (area === 'networth') return <NetWorthArea plan={plan} w={w} Change={Change} />;
  if (area === 'money') return <MoneyArea plan={plan} w={w} Change={Change} go={go} />;
  if (area === 'cushion') {
    return (
      <>
        {head('🛟', 'Cushion', 'skyInk')}<p className="ph">your cushion</p><p className="phs">{plan.cushion.purpose}</p>
        <Card tint="sky">
          <div className="mp-bigrow"><span className="mp-big">{fmt(w.efBal)}</span><span>of {fmt(w.efTarget)}</span></div>
          <Bar pct={pct(w.efBal, w.efTarget)} ink="skyInk" track="var(--glass)" />
          <div className="mp-split"><span>{w.efMonthsNow.toFixed(1)} months covered</span><span>{w.efFull === 'Full!' ? 'Full! 🎉' : w.efFull ? 'Full about ' + w.efFull : ''}</span></div>
        </Card>
        <Card eb="🧮 How it's worked out" ink="peachInk">
          <Calc rows={[['Essentials a month', fmt(w.essMonth)], ['Life buffer', fmt(w.buffer)], Object.assign(['An emergency month', fmt(w.emergencyMonth)], { tot: true }), ['Covered now', w.efMonthsNow.toFixed(1) + ' months'], ['Covered when full', w.efMonthsFull.toFixed(1) + ' months'], ['Still to go', fmt(w.efLeft)], ['From your pay a year', fmt(w.efYear)]]} />
        </Card>
        <Change step="cushion" label="Change the goal" />
      </>
    );
  }
  if (area === 'goals') return <GoalsList plan={plan} set={set} W={W} w={w} go={go} />;
  if (area === 'safety' || area === 'protect') return <SafetyArea plan={plan} set={set} w={w} go={go} />;
  if (area === 'steps') return <StepsArea plan={plan} set={set} w={w} />;
  if (area === 'keep') return <KeepArea plan={plan} go={go} />;
  if (area === 'travel') {
    return (
      <>
        {head('🇳🇬', 'Seeing family', 'lavInk')}<p className="ph">seeing family</p>
        <div className="mp-stack"><TravelYear plan={plan} set={set} w={w} /></div>
        <Card eb="🗓️ How it changes" ink="lavInk" className="mp-gap">
          <Row t="Now" s={plan.travel.now.map((x) => x.t).join(' + ')} right={fmt(w.travelNowYear)} />
          <Row t={'From ' + w.laterFrom} s={plan.travel.later.map((x) => (num(x.trips) > 1 ? x.trips + ' × ' : '') + x.t).join(' + ')} right={fmt(w.travelLaterYear)} />
          <Row t="With kids" s="Extra tickets" right={num(plan.travel.kidTicket) ? fmt(num(plan.travel.kidTicket)) + ' a ticket' : 'Add then'} />
          <p className="mp-hint left">In today's dollars. With prices going up, {w.laterFrom} is about {fmt(w.travelNominal(w.laterFrom))}.</p>
        </Card>
        <Card eb="💚 Where else it shows up" ink="mintInk">
          <Row e="🫙" bg="blush" t="Your extras" s={fmt(w.travelNowYear) + ' of your bonuses go to your travel fund each year'} />
          <Row e="🌻" bg="mint" t="Retirement" s={fmt(w.travelLaterYear) + ' a year is part of your retirement number'} />
          <Row e="💜" bg="lav" t="Over your lifetime" s={short(w.travelLifetime) + " in today's dollars"} />
        </Card>
        <Change step="travel" label="Change trips or prices" />
      </>
    );
  }
  if (area === 'invest') {
    return (
      <>
        {head('📈', 'Investing', 'lavInk')}<p className="ph">your investing</p>
        <Card eb="🌳 Balances" ink="lavInk">
          {W.invs.filter((a) => (plan.nw.inv[a.id] || {}).count !== false).map((a) => <Row key={a.id} t={a.name} right={fmt(a.balance)}><Src k="w" /></Row>)}
          {plan.nw.manual.filter((m) => m.kind === 'invest').map((m) => <Row key={m.id} t={m.t} right={fmt(num(m.amount))}><Src k="y" /></Row>)}
          <Calc rows={[Object.assign(['All together', fmt(w.invested)], { tot: true }), ['Going in a year', fmt(w.contribYear)]]} />
        </Card>
        <Card eb="🥧 Mix" ink="skyInk"><b className="mp-lead">{plan.invest.mix}</b><p className="mp-p">{plan.invest.why}</p></Card>
        <Card eb="🗂️ Accounts" ink="butterInk">
          {accOf(plan).map((a) => <Row key={a.k} e={a.e} bg="field" t={a.t} right={<Tag c={plan.invest.accounts[a.k] === 'Using' ? 'mint' : /look|do/i.test(plan.invest.accounts[a.k]) ? 'butter' : 'sky'}>{plan.invest.accounts[a.k]}</Tag>} />)}
        </Card>
        <Card className="mp-roomy">
          <Row e="🌻" bg="mint" t="Retirement" s={short(w.retNeed) + ' by ' + w.retAge + ' · ' + (w.retOnTrack ? 'on track' : 'behind')} right="›" onClick={() => go({ goal: 'retire' })} />
          <Row e="🗓️" bg="sky" t="Tax" s={'Refunds go to ' + plan.tax.refund.toLowerCase()} right="›" onClick={() => go({ area: 'tax' })} />
        </Card>
        <Change step="invest" label="Change investing" />
      </>
    );
  }
  if (area === 'retire') {
    return (
      <>
        {head('🌻', 'Retirement', 'mintInk')}<p className="ph">retirement</p>
        <Card tint={w.retOnTrack ? 'mint' : 'butter'} eb={w.retOnTrack ? '💚 Looking good' : '🌼 Worth a look'} ink={w.retOnTrack ? 'mintInk' : 'butterInk'}>
          <Calc rows={[['Needed by ' + w.retAge, short(w.retNeed)], Object.assign(['Heading for by ' + w.retAge, 'about ' + short(w.retHeading)], { b: true })]} />
          <Src k="c" text="today's dollars" />
        </Card>
        <Card eb={'🧾 Where ' + short(w.retNeedCalc) + ' comes from'} ink="peachInk">
          <Calc rows={[['Everyday life a year', fmt(num(plan.retire.everyday))], ['Seeing family a year', fmt(w.travelLaterYear)], ['Government pensions', '−' + fmt(num(plan.retire.gov))], Object.assign(['From savings a year', fmt(w.fromSavings)], { tot: true }), Object.assign(['Lasting ' + w.yearsIn + ' years', short(w.retNeedCalc)], { b: true })]} />
        </Card>
        <Card eb="🔭 Heading for" ink="skyInk">
          <Calc rows={[['Invested today', fmt(w.invested)], ['RRSP from pay a year', fmt(w.rrspYear)], ['Pension a year', fmt(w.pensionYear)], ['Growth after prices', (w.real * 100).toFixed(1) + '% a year'], ...(w.moveHit ? [['Less the move, after the 25% held back', 'included']] : [])]} />
        </Card>
        <Change step="retire" label="Change retirement" />
      </>
    );
  }
  if (area === 'protect') {
    return (
      <>
        {head('🩹', 'Insurance & paperwork', 'peachInk')}<p className="ph">insurance and paperwork</p>
        <Card eb="🩹 Insurance" ink="skyInk">
          {insOf(plan).map((i) => <Row key={i.k} e={i.e} bg="field" t={i.t} right={<Tag c={plan.insurance[i.k] === 'Not sure' ? 'butter' : /don't/i.test(plan.insurance[i.k]) ? 'sky' : 'mint'}>{plan.insurance[i.k]}</Tag>} />)}
        </Card>
        <Card eb={'📝 Paperwork · ' + paperOf(plan).filter((p) => plan.paper[p.k]).length + ' of ' + paperOf(plan).length} ink="peachInk">
          {paperOf(plan).map((p) => <Check key={p.k} on={!!plan.paper[p.k]} t={p.t} onClick={() => set({ paper: { ...plan.paper, [p.k]: !plan.paper[p.k] } })} />)}
        </Card>
        <Change step="insurance" label="Change insurance" />
      </>
    );
  }
  if (area === 'move') {
    return (
      <>
        {head('✈️', 'Moving abroad', 'blushInk')}<p className="ph">moving abroad</p>
        <Card tint="blush">
          <div className="mp-bigrow"><span className="mp-big">{fmt(w.futureHave)}</span><span>of {fmt(w.moveTotal)}</span></div>
          <Bar pct={pct(w.futureHave, w.moveTotal)} ink="blushInk" track="var(--glass)" />
          <div className="mp-split"><span>{w.futureFund ? w.futureFund.emoji + ' ' + w.futureFund.name : 'Future fund'}</span><span>{w.futureFull ? 'Full about ' + w.futureFull : ''}</span></div>
        </Card>
        <Card eb="🔗 Saved in" ink="mintInk">
          <Chips options={['Typed in', ...W.funds.filter((f) => f !== w.efFund).map((f) => f.emoji + ' ' + f.name)]} value={w.futureFund ? w.futureFund.emoji + ' ' + w.futureFund.name : 'Typed in'}
            onChange={(v) => { const f = W.funds.find((ff) => ff.emoji + ' ' + ff.name === v); set({ move: { ...plan.move, fundId: f ? f.id : 'none' } }); }} />
        </Card>
        <Card eb="🌱 Your future fund" ink="mintInk"><MoveMaths plan={plan} w={w} /></Card>
        <Card eb="🏙️ Living in Europe, a month" ink="skyInk">
          {((plan.move.europe || {}).shared || []).map((c) => <Row key={c.id} e={c.e} bg="field" t={c.t} s="Shared, your half" right={fmt(num(c.amount) / 2)} />)}
          {((plan.move.europe || {}).personal || []).map((c) => <Row key={c.id} e={c.e} bg="field" t={c.t} right={fmt(num(c.amount))} />)}
          <Calc rows={[Object.assign(['Your month', fmt(w.euMonth)], { tot: true }), ['Most likely', plan.move.likely]]} />
        </Card>
        <Card eb="🌍 A year before you go" ink="butterInk">
          {plan.leavingList.map((x) => <Check key={x.id} on={x.done} t={x.t} onClick={() => set({ leavingList: plan.leavingList.map((y) => (y.id === x.id ? { ...y, done: !y.done } : y)) })} />)}
        </Card>
        <Change step="move" label="Change the move" />
      </>
    );
  }
  if (area === 'tax') {
    return (
      <>
        {head('🗓️', 'Tax', 'skyInk')}<p className="ph">tax</p>
        <Card>
          <Row t="RRSP deadline" right="Early March" />
          <Row t="File your taxes" right="Apr 30" />
          <Row t="Worthly HQ year end" right="Dec 31" />
        </Card>
        <Card>
          <Row t="Refunds go to" right={plan.tax.refund} />
          <Row t="Paying yourself from Worthly HQ" right={plan.tax.payself} />
        </Card>
        <Change step="tax" label="Change tax" />
      </>
    );
  }
  if (area === 'numbers') {
    const A = plan.assume;
    return (
      <>
        {head('🧮', 'How your numbers work', 'butterInk')}<p className="ph">how your numbers work</p>
        <p className="phs">Every planner uses a few guesses about the future. These are yours, and you change them in Update your plan.</p>
        <Card eb="🔮 Your guesses" ink="butterInk">{/* up_01 */}
          <Row t="Prices go up" right={A.prices + '% a year'} />
          <Row t="Investments grow" right={A.growth + '% a year'} />
          <Row t="Growth once retired" right={A.retGrowth + '% a year'} />
          <Row t="Plan until" right={A.until + ' years old'} />
          <Src k="y" />
        </Card>
        <Change step="guesses" label="Change your guesses" />
        <NumberMap plan={plan} w={w} go={go} />
        <Card eb="💡 Today's dollars" ink="skyInk">
          <p className="mp-p first">Big future amounts are shown in today's dollars, so $9,800 means what $9,800 buys now. Your plan grows them with prices when it works out what to set aside each month.</p>
        </Card>
        <Card eb="🔎 The tags" ink="lavInk">
          <div className="mp-tagrow"><Src k="w" /><small>Pulled from Worthly, and updates itself</small></div>
          <div className="mp-tagrow"><Src k="y" /><small>Stays the same until you change it</small></div>
          <div className="mp-tagrow"><Src k="c" /><small>Changes whenever the numbers it uses change</small></div>
        </Card>
      </>
    );
  }
  return null;
}

/* ---------------------------------------------------------------- tax_01: your year in your plan */
// A snapshot of every goal, saved at the start of each year and again the
// first time your plan opens in the next one.
function yearSnap(plan, w) {
  const goals = allGoals(plan, w).map((g) => {
    const a = goalAmt(g, w, plan);
    const q = (w.queue || []).find((b) => b.id === g.id);
    return { id: g.id, e: g.e, t: g.t, bg: g.bg, have: Math.round(num(a.have)), of: a.of && isFinite(a.of) ? Math.round(a.of) : null, fullAt: q && q.fullAt ? q.fullAt.toISOString() : null };
  });
  return { ...snapshot(plan, w), goals, paperDone: paperOf(plan).filter((p) => plan.paper[p.k]).length };
}

function YearPage({ y0, plan, set, w }) {
  const [y, setY] = useState(y0);
  const yrs = plan.years || {};
  const rec = yrs[y] || {};
  const live = !rec.end;
  const end = rec.end || yearSnap(plan, w);
  const start = rec.start;
  useEffect(() => {
    if (rec.end && !(plan.yearSeen || {})[y]) set({ yearSeen: { ...(plan.yearSeen || {}), [y]: true } });
  }, [y, !!rec.end]); // eslint-disable-line react-hooks/exhaustive-deps
  const allYears = Object.keys(yrs).map(Number).sort((a, b) => b - a);
  const head = (
    <>
      <p className="ph">your {y} plan</p>
      {allYears.length > 1 && <div className="mp-yrchips"><Chips options={allYears.map(String)} value={String(y)} onChange={(v) => setY(Number(v))} /></div>}
    </>
  );
  if (!start) return <>{head}<Card><p className="mp-p first">Your first snapshot saves itself the next time your plan opens.</p></Card></>;
  const sg = Object.fromEntries((start.goals || []).map((g) => [g.id, g]));
  const goals = (end.goals || []).filter((g) => g.id !== 'resp');
  const isFull = (g) => g.of != null && g.of > 0 && g.have >= g.of;
  const added = goals.filter((g) => g.id !== 'retire' && sg[g.id]).reduce((s, g) => s + Math.max(0, g.have - sg[g.id].have), 0);
  const filled = goals.filter((g) => g.id !== 'retire' && isFull(g) && !(sg[g.id] && isFull(sg[g.id])));
  const nwCh = end.netWorth - start.netWorth;
  const invCh = end.invested - start.invested;
  const paperCh = (end.paperDone || 0) - (start.paperDone || 0);
  const upDown = (v) => (v >= 0 ? '↗ up ' : '↘ down ') + fmt(Math.abs(v));
  const diff = (g) => {
    const s = sg[g.id];
    if (isFull(g)) return ['mint', 'Full! 🎉'];
    if (!s || !s.fullAt || !g.fullAt) return null;
    const m = monthsApart(new Date(g.fullAt), new Date(s.fullAt));
    if (m > 0) return ['mint', m + ' month' + (m > 1 ? 's' : '') + ' sooner'];
    if (m < 0) return ['peach', -m + ' month' + (m < -1 ? 's' : '') + ' later'];
    return ['lav', 'Same date'];
  };
  return (
    <>
      {head}
      <p className="phs">{live ? 'So far, since ' + dateLabel(start.date) + '. It saves itself the first time you open your plan in ' + (y + 1) + '.' : 'Saved ' + dateLabel(end.date) + ', compared with ' + dateLabel(start.date) + '.'}</p>
      <Card>
        <div className="mp-yhead">
          <div><b className="mp-lead">{live ? 'Your year so far ✨' : 'What a year! 🥳'}</b><p className="mp-p">You added {fmt(added)} to your goals{filled.length ? ' and filled ' + (filled.length === 1 ? 'one of them' : filled.length + ' of them') : ''}!</p></div>
          <span className="mp-yemo">📸</span>
        </div>
        <div className="ln19-keys">
          <div className="ln19-key" style={{ background: 'var(--mint)' }}><i>💚</i><small>Net worth</small><b>{fmt(end.netWorth)}</b><em style={{ color: 'var(--mintInk)' }}>{upDown(nwCh)}</em></div>
          <div className="ln19-key" style={{ background: 'var(--lav)' }}><i>📈</i><small>Invested</small><b>{short(end.invested)}</b><em style={{ color: 'var(--lavInk)' }}>{upDown(invCh)}</em></div>
          <div className="ln19-key" style={{ background: 'var(--sky)' }}><i>🎯</i><small>Added to goals</small><b>{fmt(added)}</b><em style={{ color: 'var(--skyInk)' }}>{filled.length ? filled.length + ' filled' : 'Keep going!'}</em></div>
          <div className="ln19-key" style={{ background: 'var(--blush)' }}><i>📝</i><small>Paperwork</small><b>{(end.paperDone || 0) + ' of ' + paperOf(plan).length}</b><em style={{ color: 'var(--blushInk)' }}>{paperCh > 0 ? paperCh + ' done this year' : 'done'}</em></div>
        </div>
      </Card>
      <div className="ln19-card">
        <div className="ln19-ch"><h3>Goal by goal</h3></div>
        {goals.map((g) => {
          const s = sg[g.id];
          const d = diff(g);
          const p = g.of ? Math.min(100, Math.round(g.have / g.of * 100)) : 0;
          const moved = s && s.fullAt && g.fullAt && s.fullAt.slice(0, 7) !== g.fullAt.slice(0, 7);
          return (
            <div key={g.id} className="ln19-goal">
              <span className="ln19-gpic" style={{ background: 'linear-gradient(135deg, var(--' + g.bg + 'Mid), var(--' + g.bg + '))' }}><span className="e">{g.e}</span></span>
              <span className="tx">
                <span className="ln19-gt"><b>{g.t}</b>{d && <span className="mp-pill" style={{ background: 'var(--' + d[0] + ')', color: 'var(--' + d[0] + 'Ink)' }}><i style={{ background: 'var(--' + d[0] + 'Ink)' }} />{d[1]}</span>}</span>
                <span className="am"><span>{s ? short(s.have) + ' → ' : ''}<strong>{short(g.have)}</strong>{g.of ? ' / ' + short(g.of) : ''}</span>{g.of ? <strong>{p}%</strong> : null}</span>
                {g.of ? <span className="bar"><i style={{ width: Math.max(2, p) + '%', background: 'var(--' + g.bg + 'Mid)' }} /></span> : null}
                <span className="tg">{isFull(g) ? 'Full!' : g.fullAt ? 'Full about ' + monthYear(new Date(g.fullAt)) + (moved ? ', was ' + monthYear(new Date(s.fullAt)) : '') : ''}</span>
              </span>
            </div>
          );
        })}
      </div>
      <Card eb={'💭 ' + (live ? 'What you want from ' + y : 'Your note for ' + y)} ink="lavInk" className="mp-gap">
        <Txt area value={rec.note || ''} onChange={(v) => set({ years: { ...yrs, [y]: { ...rec, note: v } } })} placeholder="Fill the emergency fund, make a will…" />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- check-up, history, fresh */
function Checkup({ plan, set, w, go, back, changedFirst }) {
  const [picked, setPicked] = useState([]);
  const [ok, setOk] = useState({});
  const steps = [...new Set(CHANGES.filter((c) => picked.includes(c.t)).flatMap((c) => c.steps))];
  const typed = [
    { k: 'pay', t: 'Pay, two paycheques', v: fmt(w.payA) + ' and ' + fmt(w.payB), step: 'money' },
    { k: 'save', t: 'RRSP and emergency fund from pay', v: fmt(w.rrspYear) + ' and ' + fmt(w.efYear) + ' a year', step: 'money' },
    { k: 'ess', t: 'Essentials and life buffer', v: fmt(w.essMonth) + ' + ' + fmt(w.buffer) + ' a month', step: 'money' },
    { k: 'pension', t: 'Work pension', v: fmt(w.pensionYear) + ' a year', step: 'money' },
    { k: 'extras', t: 'Bonuses and extras', v: fmt(w.extrasIn) + ' a year', step: 'extras' },
    { k: 'sinking', t: 'Sinking funds', v: fmt(w.fundsYear) + ' a year', step: 'extras' },
    { k: 'manual', t: "Balances Worthly doesn't have", v: plan.nw.manual.length ? plan.nw.manual.map((m) => m.t).join(', ') : null, step: 'networth' },
    { k: 'move', t: 'Move date and costs', v: plan.move.ready + ' · ' + fmt(w.moveTotal), step: 'move' },
    { k: 'travel', t: 'Trip prices', v: fmt(w.travelNowYear) + ' now · ' + fmt(w.travelLaterYear) + ' later', step: 'travel' },
    { k: 'retire', t: 'Retirement spending', v: fmt(num(plan.retire.everyday)) + ' a year', step: 'retire' },
  ].filter((x) => x.v);
  const save = () => {
    const snap = { ...snapshot(plan, w), changes: picked };
    set({ checkups: [...plan.checkups, snap] });
    back();
  };
  const last = plan.checkups.length ? plan.checkups[plan.checkups.length - 1] : plan.startSnap;
  return (
    <>
      <p className="ph">{changedFirst ? "what's changed?" : 'your check-up'}</p>
      <p className="phs">Pick everything that's new and your plan only asks about those parts!</p>
      <Card>
        <Chips multi options={CHANGES.map((c) => c.t)} value={picked} onChange={setPicked} />
      </Card>
      {steps.length > 0 && (
        <Card eb="✨ Worth looking at" ink="skyInk">
          {steps.map((k) => { const s = STEPS[stepIndex(k)]; return <Row key={k} e={CHAPTERS[s.ch - 1].e} bg={CHAPTERS[s.ch - 1].bg} t={s.t} right="›" onClick={() => go({ step: stepIndex(k), solo: true })} />; })}
        </Card>
      )}
      <Card eb="✏️ Numbers you typed in" ink="peachInk">
        <p className="mp-p first">These don't update on their own, so tick each one that's still right, or tap ✎ to change it.</p>
        {typed.map((x) => (
          <div key={x.k} className="mp-tline">
            <Check on={!!ok[x.k]} t={x.t} s={x.v} onClick={() => setOk({ ...ok, [x.k]: !ok[x.k] })} />
            <button className="mp-pen" onClick={() => go({ step: stepIndex(x.step), solo: true })}>✎</button>
          </div>
        ))}
      </Card>
      {last && (
        <Card tint="mint" eb={'💚 Since ' + dateLabel(last.date)} ink="mintInk">
          <Calc rows={[['Net worth', fmt(last.netWorth) + ' → ' + fmt(w.netWorth)], ['Invested', fmt(last.invested) + ' → ' + fmt(w.invested)], ['Cushion', fmt(last.cushion) + ' → ' + fmt(w.efBal)], Object.assign(['Net worth change', (w.netWorth >= last.netWorth ? '+' : '−') + fmt(Math.abs(w.netWorth - last.netWorth))], { tot: true })]} />
        </Card>
      )}
      <button className="mp-btn" onClick={save}>Save my check-up</button>
    </>
  );
}

function History({ plan, go }) {
  return (
    <>
      <p className="ph">your history</p>
      <p className="phs">A snapshot from when you made your plan and every check-up since!</p>
      <Card eb="📆 This plan" ink="lavInk">
        {plan.startSnap && <Row t={dateLabel(plan.startSnap.date)} s="Plan made" right={fmt(plan.startSnap.netWorth)} />}
        {plan.checkups.map((c) => <Row key={c.date} t={dateLabel(c.date)} s={'Check-up' + (c.changes && c.changes.length ? ' · ' + c.changes.join(', ') : '')} right={fmt(c.netWorth)} />)}
      </Card>
      {Object.keys(plan.years || {}).length > 0 && ( /* tax_01 */
        <Card eb="📸 Your years" ink="mintInk">
          {Object.keys(plan.years).sort().reverse().map((k) => <Row key={k} t={k} s={plan.years[k].end ? 'Saved ' + dateLabel(plan.years[k].end.date) : 'So far'} right="›" onClick={() => go({ page: 'year', y: Number(k) })} />)}
        </Card>
      )}
      {plan.past.length > 0 && (
        <Card eb="🗂️ Past plans" ink="butterInk">
          {plan.past.map((p, i) => <Row key={i} e="📒" bg="butter" t={'Made ' + dateLabel(p.createdAt)} s={'Until ' + dateLabel(p.endedAt) + (p.values && p.values.success ? ' · ' + p.values.success : '')} right="›" onClick={() => go({ page: 'past', i })} />)}
        </Card>
      )}
    </>
  );
}

function PastPlan({ p }) {
  const s = p.endSnap || {};
  return (
    <>
      <p className="ph">past plan</p>
      <p className="phs">Made {dateLabel(p.createdAt)} · until {dateLabel(p.endedAt)}</p>
      {p.values && p.values.success && <Card tint="sky" eb="🌱 In one sentence" ink="skyInk"><b className="mp-lead">{p.values.success}</b></Card>}
      <Card eb="📍 When it ended" ink="mintInk">
        <Calc rows={[['Net worth', fmt(s.netWorth || 0)], ['Invested', fmt(s.invested || 0)], ['Cushion', fmt(s.cushion || 0)], ['Retirement needed', short(s.retNeed || 0)], ['Set aside a month', fmt(s.setAside || 0)]]} />
      </Card>
      <Card eb="🎯 Goals then" ink="blushInk">
        {(p.goals || []).map((g) => <Row key={g.id} e={g.e} bg="field" t={g.t} s={g.amount ? fmt(num(g.amount)) + (g.year ? ' by ' + byLabel(g.month, g.year) : '') : ''} />)}
        <Row e="✈️" bg="field" t="Moving" s={'Ready by ' + ((p.move && p.move.ready) || '')} />
        <Row e="🌻" bg="field" t="Retirement" s={'Around ' + ((p.retire && p.retire.age) || '')} />
      </Card>
    </>
  );
}

function Fresh({ plan, w, onStart, back }) {
  const [keep, setKeep] = useState('Keep my answers, just let me redo them');
  return (
    <>
      <p className="ph">start fresh?</p>
      <p className="phs">Your current plan saves to Past plans, so you can always look back on it!</p>
      <Card>
        <Q>What should the new plan start with?</Q>
        <Chips options={['Keep my answers, just let me redo them', 'A totally blank plan']} value={keep} onChange={setKeep} />
        <button className="mp-btn" onClick={() => onStart(keep.startsWith('Keep'))}>Start my new plan</button>
        <button className="mp-link mid" onClick={back}>Keep my current plan</button>
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- the whole thing */
// mp_01: the old part pages now open the goal pages
const GOAL_AREAS = { cushion: 'efund', travel: 'travel', move: 'move', retire: 'retire' };

export default function MoneyPlan({ saved, raw, data, why, topValues = [], onSave, onBack, refresh, startPage = null /* plan_27 */ }) {
  const [plan, setPlan] = useState(() => readPlan(saved, raw));
  const [stack, setStack] = useState(() => {
    const p = readPlan(saved, raw);
    if (p && p.finished && startPage === 'checkup') return [{ page: 'home' }, { page: 'checkup' }]; // plan_27
    return [p && p.finished ? { page: 'home' } : { page: 'intro' }];
  });
  useAutosave(plan, (v) => { if (v) onSave(v); });
  // patch 228: fetch your latest Worthly accounts each time the plan opens
  const [fresh, setFresh] = useState(null);
  useEffect(() => { let on = true; (refresh ? refresh() : Promise.resolve(null)).then((d) => { if (on && d) setFresh(d); }).catch(() => {}); return () => { on = false; }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const D = fresh || data;
  const W = useMemo(() => worthly(D, plan || newPlan(raw)), [D, plan && plan.money && plan.money.months]); // eslint-disable-line react-hooks/exhaustive-deps
  const w = useMemo(() => work(plan || newPlan(raw), W), [plan, W]); // eslint-disable-line react-hooks/exhaustive-deps
  // Worthly trial: each open page is its own layer, so a new page starts at the top on its own.

  const set = (patch) => setPlan((p) => ({ ...p, ...patch, updatedAt: new Date().toISOString() }));
  const go = (pg) => setStack((s) => [...s, pg]);
  const back = () => (stack.length > 1 ? setStack((s) => s.slice(0, -1)) : onBack());
  const replace = (pg) => setStack((s) => [...s.slice(0, -1), pg]);

  const start = () => {
    const p = plan || newPlan(raw);
    setPlan(p);
    replace({ step: p.stepAt || 0 });
  };
  const finish = () => {
    const now = new Date().toISOString();
    const done = plan.finished;
    set({ finished: true, finishedAt: plan.finishedAt || now, startSnap: plan.startSnap || snapshot(plan, w), stepAt: 0 });
    if (done) back(); else setStack([{ page: 'home' }]);
  };
  const startFresh = (keep) => {
    const old = { ...plan, past: undefined, endedAt: new Date().toISOString(), endSnap: snapshot(plan, w) };
    const base = keep ? { ...plan } : newPlan(raw);
    const now = new Date().toISOString();
    setPlan({ ...base, createdAt: now, updatedAt: now, finished: false, finishedAt: null, startSnap: null, checkups: [], stepAt: 0, done: {}, past: [...(plan.past || []), old] });
    setStack([{ step: 0 }]);
  };

  // ln_19: the money plan pages get their own soft lavender background
  useEffect(() => { document.documentElement.classList.add('ln19-lav'); return () => document.documentElement.classList.remove('ln19-lav'); }, []);

  // Worthly trial: swipe back. Put your thumb on the left edge and drag right;
  // the page follows your finger with the page before it underneath. Past
  // 28% of the screen it slides away, otherwise it springs back. On your
  // plan's main page, swiping goes back to Home.
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const backRef = useRef(onBack);
  backRef.current = onBack;
  useEffect(() => {
    let on = false; let sx = 0; let sy = 0; let dx = 0; let el = null;
    const reset = () => { if (el) { el.style.transition = ''; el.style.transform = ''; } on = false; el = null; };
    const start = (e) => {
      const t = e.touches[0];
      if (!t || t.clientX > 30) return; // the left edge only
      if (document.querySelector('.md .sheet-wrap')) return; // a pop-up is open
      if (document.querySelector('.md .mp-party')) return; // plan_21: the celebration is showing
      const pages = document.querySelectorAll('.mp-page');
      el = pages[pages.length - 1];
      if (!el) return;
      on = true; sx = t.clientX; sy = t.clientY; dx = 0;
      el.style.transition = 'none';
    };
    const move = (e) => {
      if (!on) return;
      const t = e.touches[0];
      dx = Math.max(0, t.clientX - sx);
      if (Math.abs(t.clientY - sy) > 40 && dx < 20) { reset(); return; } // it was a scroll
      el.style.transform = 'translateX(' + dx + 'px)';
      e.preventDefault();
    };
    const end = () => {
      if (!on) return;
      on = false;
      const page = el;
      el = null;
      page.style.transition = 'transform .2s ease';
      if (dx > window.innerWidth * 0.28) {
        page.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (stackRef.current.length > 1) setStack((st) => st.slice(0, -1));
          else if (backRef.current) backRef.current();
        }, 180);
      } else page.style.transform = '';
    };
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', reset);
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', reset);
    };
  }, []);

  // Worthly trial: when you tap into a box, leave room for the keyboard and
  // scroll what you're typing into view above it.
  useEffect(() => {
    let off = null;
    const root = () => document.querySelector('.md');
    const onIn = (e) => {
      const t = e.target;
      if (!t || !t.closest || !t.closest('.md') || !/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.tagName === 'SELECT') return;
      clearTimeout(off);
      const r = root();
      if (r) r.classList.add('mp-kb');
      setTimeout(() => { try { t.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* older WebKit */ } }, 320);
    };
    const onOut = () => {
      clearTimeout(off);
      off = setTimeout(() => {
        const a = document.activeElement;
        if (a && a.closest && a.closest('.md') && /^(INPUT|TEXTAREA)$/.test(a.tagName)) return;
        const r = root();
        if (r) r.classList.remove('mp-kb');
      }, 150);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      clearTimeout(off);
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      const r = root();
      if (r) r.classList.remove('mp-kb');
    };
  }, []);

  // plan_21: a goal that's just filled up gets a celebration, once. The first
  // time this runs, goals that are already full are marked as seen.
  const fullIds = plan && plan.finished ? (w.queue || []).filter((q) => q.st === 'done' && !q.keeps && !q.needs).map((q) => q.id) : [];
  const seen = (plan && plan.celebrated) || null;
  useEffect(() => {
    if (plan && plan.finished && !plan.celebrated) set({ celebrated: Object.fromEntries(fullIds.map((id) => [id, 'before'])) });
  }, [plan && plan.finished, !!(plan && plan.celebrated)]); // eslint-disable-line react-hooks/exhaustive-deps
  // plan_40: a goal has to stay full for 5 minutes before it's celebrated, so a typo doesn't count.
  // If a goal stops being full within a week of its celebration, that celebration is undone.
  const FULL_WAIT = 5 * 60 * 1000;
  const loaded = !!(W && (((W.funds || []).length) || ((W.invs || []).length)));
  const fullKey = fullIds.join(',');
  useEffect(() => {
    if (!plan || !plan.finished || !plan.celebrated || !loaded) return;
    const since = { ...(plan.fullSince || {}) };
    const cel = { ...plan.celebrated };
    let changed = false;
    fullIds.forEach((id) => { if (!since[id] && !cel[id]) { since[id] = new Date().toISOString(); changed = true; } });
    Object.keys(since).forEach((id) => { if (!fullIds.includes(id)) { delete since[id]; changed = true; } });
    Object.keys(cel).forEach((id) => {
      if (cel[id] !== 'before' && !fullIds.includes(id) && Date.now() - new Date(cel[id]).getTime() < 7 * 864e5) { delete cel[id]; changed = true; }
    });
    if (changed) set({ fullSince: since, celebrated: cel });
  }, [fullKey, loaded, !!(plan && plan.celebrated)]); // eslint-disable-line react-hooks/exhaustive-deps
  const fullFor = (id) => { const d = plan && plan.fullSince && plan.fullSince[id]; return d ? Date.now() - new Date(d).getTime() : -1; };
  const waiting = seen ? fullIds.some((id) => !seen[id] && fullFor(id) < FULL_WAIT) : false;
  const [, setFullTick] = useState(0);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => setFullTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [waiting]);
  const party = seen ? (w.queue || []).find((q) => fullIds.includes(q.id) && !seen[q.id] && fullFor(q.id) >= FULL_WAIT) : null;

  // tax_01: save a snapshot at the start of each year, and close last year's
  // the first time your plan opens in a new one.
  useEffect(() => {
    if (!plan || !plan.finished || !loaded) return;
    const Y = new Date().getFullYear();
    const yrs = { ...(plan.years || {}) };
    const snap = yearSnap(plan, w);
    let changed = false;
    Object.keys(yrs).forEach((k) => { if (Number(k) < Y && yrs[k] && !yrs[k].end) { yrs[k] = { ...yrs[k], end: snap }; changed = true; } });
    if (!yrs[Y] || !yrs[Y].start) { yrs[Y] = { ...(yrs[Y] || {}), start: snap }; changed = true; }
    if (changed) set({ years: yrs });
  }, [loaded, !!(plan && plan.finished)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every open page stays drawn, each in its own layer.
  const renderPage = (cur) => {
  let body = null;
  let top = null;
  if (cur.page === 'intro') {
    const resume = plan && !plan.finished && plan.stepAt > 0;
    body = (
      <>
        <p className="ph">your money plan</p>
        <p className="phs">Everything a financial planner would walk you through, at your own pace! It saves as you go, so you can stop and come back anytime.</p>
        <div className="ta-stack">
          <Card eb="📒 Six parts" ink="lavInk">
            {CHAPTERS.map((c) => <Row key={c.n} e={c.e} bg={c.bg} t={c.name} s={c.sub} right={<span className="mp-faint">{STEPS.filter((s) => s.ch === c.n).length} steps</span>} />)}
            <Row e="✨" bg="lav" t="Your plan" s="All of it on one page, and you can tap into every part" />
          </Card>
          <Card eb="🔎 Where every number comes from" ink="skyInk">
            <p className="mp-p first">Every number in your plan wears a little tag, so you always know where it came from! You can change any of them.</p>
            <div className="mp-tagrow"><Src k="w" /><small>Pulled from your Worthly accounts and budget</small></div>
            <div className="mp-tagrow"><Src k="y" /><small>Something you typed in yourself</small></div>
            <div className="mp-tagrow"><Src k="c" /><small>Calculated from your other numbers, with the maths shown</small></div>
          </Card>
          <Card tint="butter"><p className="mp-p first">Everyone's plan covers these same areas! What changes from person to person is which goals you have and what comes first, so a few steps ask whether they apply to you.</p></Card>
          <button className="mp-btn" onClick={start}>{resume ? 'Carry on from step ' + (plan.stepAt + 1) : "Let's start!"}</button>
        </div>
      </>
    );
  } else if (cur.step != null && plan) {
    const i = cur.step;
    const S = STEPS[i];
    const ch = CHAPTERS[S.ch - 1];
    const Comp = STEP_COMPONENTS[S.k];
    const solo = !!cur.solo;
    const next = () => {
      if (solo) return back();
      if (i >= STEPS.length - 1) return finish();
      if (!plan.finished) set({ stepAt: i + 1 });
      replace({ ...cur, step: i + 1 });
    };
    const prev = () => (solo ? back() : i === 0 ? (cur.review ? back() : replace({ page: 'intro' })) : replace({ ...cur, step: i - 1 }));
    top = (
      <div className="mp-top">
        <div className="mp-topin"><span className="mp-chap" style={{ color: 'var(--' + ch.ink + ')' }}>{ch.e} {ch.name}</span><span className="mp-stepn">{i + 1} of {STEPS.length}</span></div>
        <Bar pct={(i + 1) / STEPS.length * 100} ink={ch.ink} />
      </div>
    );
    body = (
      <>
        <p className="ph mp-h">{S.k === 'move' && plan.move ? 'Moving abroad ✈️' : S.k === 'travel' ? 'Seeing family 🇳🇬' : S.t}</p>
        <p className="phs">{STEP_SUBS[S.k]}</p>
        <div className="mp-stack"><Comp plan={plan} set={set} W={W} w={w} why={why} topValues={topValues} /></div>
        <div className="mp-nav">
          {!solo && i > 0 && <button className="mp-btn soft" onClick={prev}>Back</button>}
          <button className="mp-btn" onClick={next}>{solo ? 'Done' : i >= STEPS.length - 1 ? (plan.finished ? 'Back to my plan' : 'See my plan!') : 'Next'}</button>
        </div>
      </>
    );
  } else if (cur.page === 'home' && plan) {
    body = <div className="mp-stack"><PlanHome plan={plan} W={W} w={w} go={go} set={set} /></div>;
  } else if ((cur.goal || GOAL_AREAS[cur.area]) && plan) {
    body = <div className="mp-stack"><GoalPage key={cur.goal || cur.area} id={cur.goal || GOAL_AREAS[cur.area]} plan={plan} set={set} W={W} w={w} go={go} /></div>;
  } else if (cur.area && plan) {
    body = <div className="mp-stack"><Area area={cur.area} plan={plan} set={set} W={W} w={w} go={go} /></div>;
  } else if (cur.page === 'whatif' && plan) {
    body = <div className="mp-stack"><WhatIf plan={plan} W={W} w={w} back={back} /></div>;
  } else if (cur.page === 'year' && plan) {
    body = <div className="mp-stack"><YearPage y0={cur.y || new Date().getFullYear()} plan={plan} set={set} w={w} /></div>;
  } else if (cur.page === 'checkup' && plan) {
    body = <div className="mp-stack"><Checkup plan={plan} set={set} w={w} go={go} back={back} changedFirst={!!cur.changed} /></div>;
  } else if (cur.page === 'update' && plan) {
    body = <div className="mp-stack"><UpdateArea plan={plan} w={w} go={go} /></div>;
  } else if (cur.page === 'history' && plan) {
    body = <div className="mp-stack"><History plan={plan} go={go} /></div>;
  } else if (cur.page === 'past' && plan && plan.past[cur.i]) {
    body = <div className="mp-stack"><PastPlan p={plan.past[cur.i]} /></div>;
  } else if (cur.page === 'fresh' && plan) {
    body = <div className="mp-stack"><Fresh plan={plan} w={w} onStart={startFresh} back={back} /></div>;
  }
  return <div className="mp">{top}{body}</div>;
  };
  const pageKey = (p) => [p.page, p.step, p.area, p.goal, p.i].map((v) => (v == null ? '' : v)).join('|');
  return (
    <div className="mp-layers">
      {stack.map((p, i) => (
        <div key={i + ':' + pageKey(p)} className="mp-page" style={{ zIndex: 2000 + i }}>
          {renderPage(p)}
        </div>
      ))}
      {party && <Party b={party} w={w} onDone={() => set({ celebrated: { ...seen, [party.id]: new Date().toISOString() } })} />}
    </div>
  );
}

// The grey line on the Money tab tile.
export function planSub(saved) {
  if (!saved || !saved.v) return 'Your long-term plan, all in one place!';
  if (!saved.finished) return 'Carry on from step ' + ((saved.stepAt || 0) + 1);
  if (checkupDue(saved)) return 'Time for your check-up!';
  return 'Next check-up ' + nextCheckup(saved);
}

// Worthly trial (mp_01): the status the Home card shows - the same "on track"
// and "working on" the top of your plan works out. Null until the plan is set up.
export function planStatus(saved, raw, data) {
  try {
    const plan = readPlan(saved, raw);
    if (!plan || !plan.finished) return null;
    const W = worthly(data, plan);
    const w = work(plan, W);
    const goals = allGoals(plan, w);
    const must = goals.filter((g) => g.tier === 'must');
    const good = (s) => s === 'on' || s === 'done' || s === 'covered';
    const goodN = must.filter((g) => good(g.status)).length;
    const lineup = [...(w.queue || []).map((b) => b.id).filter((id) => id !== 'retire'), 'retire'];
    const steps = lineup.map((id) => goals.find((g) => g.id === id)).filter(Boolean)
      .map((g) => ({ k: g.id, e: g.e, t: (g.renamed ? g.t : MILE[g.id]) || g.t, done: g.status === 'done' || goalPct(g) >= 100 }));
    const cur = steps.find((s) => !s.done) || null;
    return {
      onTrack: goodN >= must.length - 1,
      line: cur ? (cur.k === 'retire' ? 'Retiring well' : 'Working on your ' + cur.t.toLowerCase()) : 'Every step is done! 🎉',
      emoji: cur ? cur.e : '🎉',
    };
  } catch (e) {
    return null;
  }
}

// plan_22: how much your net worth grows in a number of months, at your
// plan's pace (today's dollars). Null until the plan is set up.
export function planGrowth(saved, raw, data) {
  try {
    const plan = readPlan(saved, raw);
    if (!plan || !plan.finished) return null;
    const w = work(plan, worthly(data, plan));
    const base = w.nwAt(0);
    return (months) => w.nwAt(months / 12) - base;
  } catch (e) {
    return null;
  }
}
